import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import SafetyShell from "../safety/SafetyShell";
import ManualCompareDialog from "./ManualCompareDialog";
import LoadBoard from "./LoadBoard";
import { SplitBar, QualityCard, WeightLbsKg, KG_TO_LB } from "./planQualityUI";
import { TopViewPanel, ElevationPanel, TelemetryRail, CatalogTray, UnassignedTray } from "./visualWorkspace";
import { applySandboxMove } from "./sandboxElevation";
import { PlacementAuditor } from "./placementAuditor";
import { isCustomerPro } from "../lib/roles";
import { TRUCK_SPEC_DEFAULTS, TRUCK_SPEC_FIELDS } from "../lib/truckSpecs";
import {
  Container, Upload, FileSpreadsheet, FileText, Play, Settings2, Plus, Minus, Trash2, Save,
  ArrowLeftRight, PackageCheck, AlertTriangle, CheckCircle2, TrendingDown, Boxes, Scale3d, Layers, ArrowUpDown, RefreshCw,
  GripVertical, FolderOpen, PackagePlus, History, Shuffle, GitCompareArrows, ChevronLeft, SlidersHorizontal, X, Lock, Loader2, XCircle, HelpCircle, Truck,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authH = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

const STATUS_META = {
  fits: { label: "Fits", color: "bg-emerald-100 text-emerald-700 border-emerald-300", icon: CheckCircle2 },
  under_loaded: { label: "Under-loaded", color: "bg-amber-100 text-amber-700 border-amber-300", icon: TrendingDown },
  over_loaded: { label: "Over capacity", color: "bg-rose-100 text-rose-700 border-rose-300", icon: AlertTriangle },
};

// GLOBAL, DETERMINISTIC per-SKU colour. A product code ALWAYS maps to the same colour across
// every container, every re-optimization and every edit — adding/removing a pallet never
// re-colours any other product. FNV-1a hash of the (upper-cased) code indexes a CURATED
// high-contrast palette. The backend PDF/Excel use the identical algorithm + palette, so
// on-screen colours match the exports byte-for-byte.
const SKU_PALETTE = [
  "#2563eb", "#e11d48", "#16a34a", "#ea580c", "#7c3aed", "#0891b2",
  "#db2777", "#a16207", "#4338ca", "#0d9488", "#b91c1c", "#65a30d",
  "#9333ea", "#0284c7", "#c2410c", "#15803d", "#be185d", "#1e40af",
  "#047857", "#a21caf", "#854d0e", "#0e7490", "#4d7c0f", "#6d28d9",
  "#dc2626", "#d97706", "#059669", "#7e22ce", "#0369a1", "#ca8a04",
  "#be123c", "#3730a3", "#166534", "#831843",
];
function _skuHash(code) {
  const s = String(code).toUpperCase();
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193) >>> 0; }
  return h >>> 0;
}
// Collision-resolved, deterministic, edit-stable per-SKU colour map. Codes are assigned in a
// STABLE sorted order via linear probing from their hash bucket, so two DIFFERENT SKUs in one
// load never share a colour (until the 34-colour palette is exhausted), yet the same code keeps
// its colour across containers, re-optimize and pallet add/remove (the code SET is unchanged).
// Identical algorithm on the backend (assign_palette) so the PDF/Excel match the screen.
function colorForCode(code) {
  if (!code) return "#64748b";
  return SKU_PALETTE[_skuHash(code) % SKU_PALETTE.length];
}

function buildColorMap(placement) {
  const codes = [];
  (placement || []).forEach(p => {
    (p.layer_products && p.layer_products.length ? p.layer_products : [p.product_code]).forEach(code => { if (code) codes.push(code); });
  });
  const uniq = Array.from(new Set(codes)).sort();
  const n = SKU_PALETTE.length;
  const used = new Set();
  const map = {};
  for (const code of uniq) {
    const start = _skuHash(code) % n;
    let picked = -1;
    for (let i = 0; i < n; i++) { const idx = (start + i) % n; if (!used.has(idx)) { picked = idx; break; } }
    if (picked < 0) picked = start;   // > palette size: unavoidable collision, fall back to hash bucket
    used.add(picked);
    map[code] = SKU_PALETTE[picked];
  }
  return map;
}

export default function ContainerPlanner({ user, onLogout }) {
  const isCPro = isCustomerPro(user);
  const [types, setTypes] = useState([]);
  const [typeId, setTypeId] = useState("");
  const [source, setSource] = useState(isCPro ? "draft" : "po"); // po | draft — CUSTOMER_PRO is locked to draft uploads
  const [pos, setPos] = useState([]);
  const [poSearch, setPoSearch] = useState("");
  const [poId, setPoId] = useState("");
  const [draft, setDraft] = useState(null); // {lines, po_number, ship_to_location}
  const [allowRotation, setAllowRotation] = useState(true);
  const [ignoreLayerCap, setIgnoreLayerCap] = useState(true);
  const [combineBases, setCombineBases] = useState(false);
  const [packingMode, setPackingMode] = useState(() => localStorage.getItem("cp_packing_mode") || "density"); // density | stability
  const [ledgeGuard, setLedgeGuard] = useState(() => localStorage.getItem("cp_ledge_guard") === "1"); // Density-mode 85% Rule (safety filter, opt-in)
  const [strictCtu, setStrictCtu] = useState(() => localStorage.getItem("cp_strict_ctu") === "1"); // Strict IMO CTU Balance (Beta)
  const [usHighway, setUsHighway] = useState(() => localStorage.getItem("cp_us_highway") === "1"); // US Highway Compliance (BETA) — Law-of-Levers axle physics
  const [usDecisionSuite, setUsDecisionSuite] = useState(() => localStorage.getItem("cp_us_decision_suite") === "1"); // Decision Suite — CoG-X + two-scenario axle ranges
  const [sandboxMode, setSandboxMode] = useState(() => localStorage.getItem("cp_sandbox_mode") === "1");   // Compliance Sandbox: drag pallets in the elevations, NO REVERT, live physics
  const [topClearance, setTopClearance] = useState(() => {
    const v = localStorage.getItem("cp_top_clearance");
    return v != null && v !== "" ? (parseFloat(v) || 0) : 0;
  });
  const [optimize, setOptimize] = useState(false);
  const [centerGap, setCenterGap] = useState(() => {
    const v = localStorage.getItem("cp_center_gap");
    return v != null && v !== "" ? (parseFloat(v) || 0) : 4;
  });
  const [maxOverhang, setMaxOverhang] = useState(() => {
    const v = localStorage.getItem("cp_max_overhang");
    return v != null && v !== "" ? (parseFloat(v) || 0) : 0;
  });
  const [crossClearance, setCrossClearance] = useState(() => {
    const v = localStorage.getItem("cp_cross_clearance");
    return v != null && v !== "" ? (parseFloat(v) || 0) : 0;
  });
  const [maxStackWeight, setMaxStackWeight] = useState(() => {
    const v = localStorage.getItem("cp_max_stack_weight");
    return v != null && v !== "" ? (parseFloat(v) || 1600) : 1600;
  });
  const [palletGap, setPalletGap] = useState(() => {
    const v = localStorage.getItem("cp_pallet_gap");
    return v != null && v !== "" ? (parseFloat(v) || 0) : 2;
  });
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPresets, setShowPresets] = useState(false);
  const [showCompare, setShowCompare] = useState(false);
  const [showBoard, setShowBoard] = useState(false);
  const [boardSeed, setBoardSeed] = useState(null);   // containers seeded into the Load Board from the current auto-plan
  const [syncing, setSyncing] = useState(false);
  const draftRef = useRef(null);
  // fill-remaining-space state
  const [fillQty, setFillQty] = useState({});      // product_code -> pallets
  const [topupExtra, setTopupExtra] = useState([]); // one-tap top-up extra lines [{product_code, qty(pieces)}]
  const [rankBy, setRankBy] = useState("space");
  const [suggest, setSuggest] = useState(null);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [basePlan, setBasePlan] = useState(null);   // snapshot before fill items were added
  const pppRef = useRef({});                        // product_code -> pieces/pallet
  // manual remove-pallets state (to solve over-capacity by trimming the load)
  const [removeQty, setRemoveQty] = useState({});   // product_code -> pallets to remove
  const removePppRef = useRef({});                  // product_code -> pieces/pallet (from plan rows)
  const skipRecomputeRef = useRef(false);           // skip the one recompute fired by seeding a fresh auto-plan (preserves swing/hints)
  const dirtyRef = useRef(new Set());               // container indices edited since last recompute (LOCKED model)
  const lastGoodRef = useRef([]);                   // last validated cEdit snapshot (for capacity-reject revert)
  const pendingAddRef = useRef({});                 // {containerIdx: productCode} — last code added to a container (for the Placement Auditor "Why it failed?")
  const auditReqRef = useRef(0);                     // monotonically increasing token so a slow audit response can never overwrite a newer one
  const [audit, setAudit] = useState(null);         // Placement Decision Auditor result (read-only rule tree); null = closed
  const [lastAudit, setLastAudit] = useState(null);  // last BLOCKED audit — kept so the Telemetry Auditor button can reopen it after the toast is gone
  const [pool, setPool] = useState({});             // Unassigned Pallet Pool: {code:{po,filler,description,ppp}}
  const [reoptConfirm, setReoptConfirm] = useState(null);  // pending Re-Optimize result awaiting extra-container confirm
  const lastGoodPoolRef = useRef({});               // last validated pool snapshot (reverts a rejected pool→container move)
  // manual arrange mode (operator-defined lane stacks)
  const [mode, setMode] = useState("auto");         // auto | manual
  const [arrangement, setArrangement] = useState({ A: [], B: [] });
  // multi-container split (large orders across N identical containers)
  const [splitMulti, setSplitMulti] = useState(false);
  const [multi, setMulti] = useState(null);          // {containers:[plan...], summary, container_count}
  const [activeContainer, setActiveContainer] = useState(0);
  // multi-container EDIT (adjust the ERP-suggested split)
  const [editSplit, setEditSplit] = useState(false);
  const [cEdit, setCEdit] = useState([]);            // [{type_id, alloc:{code:pallets}, arrangement:{A,B}|null}]
  const [splitProducts, setSplitProducts] = useState([]); // [{code, description, ppp, required}]
  // multi-container per-container gap fill (Q4)
  const [showFill, setShowFill] = useState(false);
  const [mfSuggest, setMfSuggest] = useState(null);
  const [mfLoading, setMfLoading] = useState(false);
  const [arrIdx, setArrIdx] = useState(null);        // container index currently being arranged
  const [savedSplits, setSavedSplits] = useState([]);
  const [restorePrompt, setRestorePrompt] = useState(null);  // most-recent saved split offered for the picked PO
  // ---- zero-scroll visual workspace ----
  const navigate = useNavigate();
  const [catalog, setCatalog] = useState([]);          // full product catalog for the bottom tray
  const [replanning, setReplanning] = useState([]);    // container indices currently being re-planned (others show a lock)
  const [showConfig, setShowConfig] = useState(false);  // workspace config drawer (physics options)
  const [showFiller, setShowFiller] = useState(false);  // whole-PO filler manifest popup
  const [showSetupOpts, setShowSetupOpts] = useState(false); // setup-screen advanced options accordion
  // Admin-configurable Truck & Chassis physics (Steer/Drive/Trailer tares + geometry + Fifth Wheel Lead)
  const [truckSpecs, setTruckSpecs] = useState(TRUCK_SPEC_DEFAULTS);
  const [showTruckSetup, setShowTruckSetup] = useState(false);
  const loadTruckSpecs = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/container-planner/truck-specs`, authH());
      if (r.data?.truck_specs) setTruckSpecs(r.data.truck_specs);
    } catch { /* keep defaults on 403 or offline */ }
  }, []);
  useEffect(() => { loadTruckSpecs(); }, [loadTruckSpecs]);
  useEffect(() => { axios.get(`${API}/catalog/products`, authH()).then(r => setCatalog(r.data.products || [])).catch(() => {}); }, []);

  const loadTypes = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/container-types`, authH());
      const list = r.data.container_types || [];
      setTypes(list);
      if (!typeId && list.length) setTypeId((list.find(t => t.is_default) || list[0]).id);
    } catch (err) {
      if (err.response?.status === 403) toast.error("Permission denied: container.plan required");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadPos = useCallback(async () => {
    if (isCPro) return;   // CUSTOMER_PRO cannot browse ERP POs — /api/po/list would 403
    try {
      const r = await axios.get(`${API}/po/list`, authH());
      const list = Array.isArray(r.data) ? r.data : (r.data.pos || r.data.items || []);
      setPos(list);
    } catch { /* ignore */ }
  }, [isCPro]);

  useEffect(() => { loadTypes(); loadPos(); }, [loadTypes, loadPos]);

  // remember the top-clearance setting across sessions
  useEffect(() => { localStorage.setItem("cp_top_clearance", String(topClearance)); }, [topClearance]);
  useEffect(() => { localStorage.setItem("cp_center_gap", String(centerGap)); }, [centerGap]);
  useEffect(() => { localStorage.setItem("cp_max_overhang", String(maxOverhang)); }, [maxOverhang]);
  useEffect(() => { localStorage.setItem("cp_cross_clearance", String(crossClearance)); }, [crossClearance]);
  useEffect(() => { localStorage.setItem("cp_max_stack_weight", String(maxStackWeight)); }, [maxStackWeight]);
  useEffect(() => { localStorage.setItem("cp_pallet_gap", String(palletGap)); }, [palletGap]);
  useEffect(() => { localStorage.setItem("cp_packing_mode", packingMode); }, [packingMode]);
  useEffect(() => { localStorage.setItem("cp_ledge_guard", ledgeGuard ? "1" : "0"); }, [ledgeGuard]);
  useEffect(() => { localStorage.setItem("cp_strict_ctu", strictCtu ? "1" : "0"); }, [strictCtu]);
  useEffect(() => { localStorage.setItem("cp_us_highway", usHighway ? "1" : "0"); }, [usHighway]);
  useEffect(() => { localStorage.setItem("cp_us_decision_suite", usDecisionSuite ? "1" : "0"); }, [usDecisionSuite]);
  useEffect(() => { localStorage.setItem("cp_sandbox_mode", sandboxMode ? "1" : "0"); }, [sandboxMode]);

  const syncFromProd = async () => {
    if (!window.confirm("Replace ALL preview POs and the product catalog with the latest data from production (erp.manikindia.com)?\n\nThis wipes the current preview data and cannot be undone.")) return;
    setSyncing(true);
    try {
      const r = await axios.post(`${API}/admin/sync-from-production`, {}, authH());
      toast.success(`Synced from production: ${r.data.pos} POs · ${r.data.products} products · ${r.data.components} BOM lines (${r.data.duration_sec}s)`);
      setPlan(null); setPoId(""); setFillQty({}); setRemoveQty({}); setTopupExtra([]); setSuggest(null); setBasePlan(null); setMulti(null);
      loadPos();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sync failed — check backend logs");
    } finally { setSyncing(false); }
  };

  const uploadDraft = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/container-planner/parse-draft`, fd, { headers: { ...authH().headers, "Content-Type": "multipart/form-data" } });
      setDraft(r.data);
      setPlan(null); setFillQty({}); setRemoveQty({}); setTopupExtra([]); setSuggest(null); setBasePlan(null); setMulti(null);
      toast.success(`Parsed ${r.data.lines?.length || 0} line(s)${r.data.po_number ? ` from PO ${r.data.po_number}` : ""}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not parse the Excel");
    } finally { if (draftRef.current) draftRef.current.value = ""; }
  };

  const extraLines = () => Object.entries(fillQty)
    .filter(([, p]) => p > 0)
    .map(([code, p]) => ({ product_code: code, qty: p * (pppRef.current[code] || 0) }))
    .filter(e => e.qty > 0);

  // pieces to remove per product, derived from remove-pallet steppers
  const qtyDeltas = () => Object.entries(removeQty)
    .filter(([, p]) => p > 0)
    .reduce((acc, [code, p]) => {
      const pcs = p * (removePppRef.current[code] || 0);
      if (pcs > 0) acc[code] = pcs;
      return acc;
    }, {});

  const setRemove = (code, pallets, ppp) => {
    if (ppp) removePppRef.current[code] = ppp;
    setRemoveQty(prev => { const n = { ...prev }; if (pallets > 0) n[code] = pallets; else delete n[code]; return n; });
  };
  const clearRemove = () => setRemoveQty({});

  const planBody = (opt = optimize) => {
    const base = { container_type_id: typeId, allow_rotation: allowRotation, optimize: opt, ignore_layer_cap: ignoreLayerCap, combine_bases: combineBases, packing_mode: packingMode, ledge_guard: ledgeGuard, strict_ctu: strictCtu, us_highway_mode: usHighway, us_decision_suite_mode: usDecisionSuite, top_clearance_in: Number(topClearance) || 0, pallet_margin_in: Number(palletGap) || 0, max_stack_weight_kg: Number(maxStackWeight) || 1600, center_gap_in: Number(centerGap) || 0, max_overhang_in: Number(maxOverhang) || 0, cross_clearance_in: Number(crossClearance) || 0 };
    if (mode === "manual") {
      const mb = { container_type_id: typeId, allow_rotation: allowRotation, packing_mode: packingMode, ledge_guard: ledgeGuard, strict_ctu: strictCtu, us_highway_mode: usHighway, us_decision_suite_mode: usDecisionSuite, top_clearance_in: Number(topClearance) || 0, pallet_margin_in: Number(palletGap) || 0, max_stack_weight_kg: Number(maxStackWeight) || 1600, center_gap_in: Number(centerGap) || 0, max_overhang_in: Number(maxOverhang) || 0, cross_clearance_in: Number(crossClearance) || 0, arrangement };
      if (source === "po") mb.po_id = poId;
      else { mb.lines = draft?.lines || []; mb.ship_to_location = draft?.ship_to_location; mb.title = draft?.po_number ? `Manual arrange · Draft ${draft.po_number}` : undefined; }
      return mb;
    }
    const extra = [...extraLines(), ...topupExtra];
    if (extra.length) base.extra_lines = extra;
    const deltas = qtyDeltas();
    if (Object.keys(deltas).length) base.qty_deltas = deltas;
    if (source === "po") return { ...base, po_id: poId };
    return { ...base, lines: draft?.lines || [], ship_to_location: draft?.ship_to_location, title: draft?.po_number ? `Stuffing Plan · Draft ${draft.po_number}` : "Stuffing Plan · Draft order" };
  };

  // ---- manual arrangement mutators ----
  const laneKey = (l) => (l === "B" ? "B" : "A");
  const addStacksToLane = (lane, code, n = 1) => {
    const L = laneKey(lane);
    setArrangement(prev => ({ ...prev, [L]: [...prev[L], ...Array.from({ length: Math.max(1, n) }, () => ({ layers: [code] }))] }));
  };
  const addLayerToStack = (lane, idx, code) => {
    const L = laneKey(lane);
    setArrangement(prev => ({ ...prev, [L]: prev[L].map((c, i) => i === idx ? { layers: [...c.layers, code] } : c) }));
  };
  const removeLayer = (lane, idx, li) => {
    const L = laneKey(lane);
    setArrangement(prev => {
      const col = prev[L][idx];
      const layers = col.layers.filter((_, k) => k !== li);
      const next = layers.length ? prev[L].map((c, i) => i === idx ? { layers } : c) : prev[L].filter((_, i) => i !== idx);
      return { ...prev, [L]: next };
    });
  };
  const removeStack = (lane, idx) => {
    const L = laneKey(lane);
    setArrangement(prev => ({ ...prev, [L]: prev[L].filter((_, i) => i !== idx) }));
  };
  const clearArrangement = () => setArrangement({ A: [], B: [] });

  // ---- saved manual arrangements (team-wide, per PO + container) ----
  const [saved, setSaved] = useState([]);
  const loadSavedList = useCallback(async () => {
    if (!typeId) { setSaved([]); return; }
    try {
      const params = { container_type_id: typeId };
      if (source === "po" && poId) params.po_id = poId;
      const r = await axios.get(`${API}/container-planner/arrangements`, { ...authH(), params });
      setSaved(r.data.arrangements || []);
    } catch { setSaved([]); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeId, poId, source]);
  useEffect(() => { if (mode === "manual") loadSavedList(); }, [mode, loadSavedList]);

  const saveArrangement = async (name = "") => {
    if (!(arrangement.A.length || arrangement.B.length)) { toast.error("Nothing to save — add some stacks first"); return; }
    const body = { container_type_id: typeId, arrangement, top_clearance_in: Number(topClearance) || 0, pallet_margin_in: Number(palletGap) || 0, allow_rotation: allowRotation };
    if (source === "po") body.po_id = poId;
    if (name) body.name = name;
    try {
      await axios.post(`${API}/container-planner/arrangements`, body, authH());
      toast.success(name ? `Saved “${name}”` : "Arrangement saved");
      loadSavedList();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const deleteSaved = async (id) => {
    try { await axios.delete(`${API}/container-planner/arrangements/${id}`, authH()); toast.success("Deleted"); loadSavedList(); }
    catch { toast.error("Delete failed"); }
  };
  const applySaved = (doc) => {
    const a = doc.arrangement || {};
    setArrangement({ A: a.A || [], B: a.B || [] });
    setMode("manual");
    if (typeof doc.top_clearance_in === "number") setTopClearance(doc.top_clearance_in);
    toast.success(`Loaded ${doc.name ? `“${doc.name}”` : "saved layout"} — editable`);
  };

  // ---- auto-improve + compare ----
  const [improve, setImprove] = useState(null);
  const [improving, setImproving] = useState(false);
  const [compareOpen, setCompareOpen] = useState(false);
  useEffect(() => { setImprove(null); }, [arrangement]);

  const _manualBody = () => {
    const b = { container_type_id: typeId, allow_rotation: allowRotation, top_clearance_in: Number(topClearance) || 0, pallet_margin_in: Number(palletGap) || 0, arrangement };
    if (source === "po") b.po_id = poId;
    return b;
  };
  const planArrangement = async (arr) => {
    const body = { container_type_id: typeId, allow_rotation: allowRotation, top_clearance_in: Number(topClearance) || 0, pallet_margin_in: Number(palletGap) || 0, arrangement: arr };
    if (source === "po") body.po_id = poId;
    const r = await axios.post(`${API}/container-planner/plan`, body, authH());
    return r.data;
  };
  const autoImprove = async () => {
    if (!(arrangement.A.length || arrangement.B.length)) { toast.error("Add some stacks first"); return; }
    setImproving(true);
    try {
      const r = await axios.post(`${API}/container-planner/auto-improve`, _manualBody(), authH());
      setImprove(r.data);
      if (!r.data.improved_better) toast("Your layout is already close to optimal — no better reshuffle found");
    } catch (e) { toast.error(e.response?.data?.detail || "Auto-improve failed"); }
    finally { setImproving(false); }
  };
  const applyImprove = () => {
    if (improve?.improved?.arrangement) { setArrangement(improve.improved.arrangement); toast.success("Applied the suggested layout"); }
  };
  const getSuggestedArrangement = async () => {
    const r = await axios.post(`${API}/container-planner/auto-improve`, _manualBody(), authH());
    return r.data.improved.arrangement;
  };

  const seedFromAuto = async () => {
    if (!typeId) { toast.error("Pick a container type"); return; }
    if (source === "po" && !poId) { toast.error("Pick a PO to seed from"); return; }
    if (source === "draft" && !(draft?.lines?.length)) { toast.error("Upload a draft PO first"); return; }
    setLoading(true);
    try {
      let src = (plan && !plan.manual) ? plan : null;
      if (!src) {
        const body = { container_type_id: typeId, allow_rotation: allowRotation, optimize, ignore_layer_cap: ignoreLayerCap, combine_bases: combineBases, top_clearance_in: Number(topClearance) || 0, pallet_margin_in: Number(palletGap) || 0, max_stack_weight_kg: Number(maxStackWeight) || 1600 };
        if (source === "po") body.po_id = poId; else { body.lines = draft?.lines || []; body.ship_to_location = draft?.ship_to_location; }
        const r = await axios.post(`${API}/container-planner/plan`, body, authH());
        src = r.data;
      }
      const arr = { A: [], B: [] };
      (src.placement || []).slice().sort((a, b) => a.start_in - b.start_in).forEach(p => {
        const L = p.lane === "B" ? "B" : "A";
        const layers = (p.layer_products && p.layer_products.length) ? [...p.layer_products] : [p.product_code];
        arr[L].push({ layers });
      });
      setArrangement(arr);
      setMode("manual");
      toast.success(`Seeded ${arr.A.length + arr.B.length} stacks from the auto-plan — now editable`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not seed from auto-plan");
    } finally { setLoading(false); }
  };

  // Convert a plan's placement into a Load Board arrangement ({A:[{layers,rotated}], B:[...]}).
  const planToArrangement = (p) => {
    const arr = { A: [], B: [] };
    (p?.placement || []).slice().sort((a, b) => a.start_in - b.start_in).forEach((pl) => {
      const L = pl.lane === "B" ? "B" : "A";
      const layers = (pl.layer_products && pl.layer_products.length) ? [...pl.layer_products] : [pl.product_code];
      arr[L].push({ layers, rotated: !!pl.rotated });
    });
    return arr;
  };
  const openBoardFromPlan = () => {
    if (!plan) { toast.error("Run a plan first"); return; }
    setBoardSeed([{ container_type_id: plan.container?.id || typeId, label: plan.title || "Container 1", arrangement: planToArrangement(plan) }]);
    setShowBoard(true);
  };
  const openBoardFromMulti = () => {
    if (!multi?.containers?.length) { toast.error("Run a multi-container split first"); return; }
    setBoardSeed(multi.containers.map((c, i) => ({
      container_type_id: c.container?.id || typeId, label: `Container ${i + 1}`, arrangement: planToArrangement(c),
    })));
    setShowBoard(true);
  };


  const densityRerunRef = useRef(false);
  const runPlan = async (opt = optimize) => {
    const doOpt = opt === true;
    if (!typeId) { toast.error("Pick a container type"); return; }
    if (source === "po" && !poId) { toast.error("Pick a PO"); return; }
    if (source === "draft" && !(draft?.lines?.length)) { toast.error("Upload a draft PO Excel first"); return; }
    setLoading(true);
    setOptimize(doOpt);
    let optToastId;
    if (doOpt) {
      const nEst = multi?.containers?.length || null;
      optToastId = toast.loading(nEst ? `Optimising ${nEst} container${nEst !== 1 ? "s" : ""}…` : "Optimising your plan…", { description: "Balancing weight & fit — this can take a few seconds for large orders." });
    }
    try {
      // Unified visual workspace: always fan the order out to N containers (N can be 1)
      // and seed the per-container editable allocation model so drag-drop / ✕ edits work.
      const r = await axios.post(`${API}/container-planner/plan-multi`, planBody(doOpt), authH());
      const conts = r.data.containers || [];
      setMulti(r.data);
      skipRecomputeRef.current = true;   // keep this auto result (with swing/hints); don't immediately recompute
      seedEditFromMulti(r.data);
      setEditSplit(true);
      setArrIdx(null);
      const idx = activeContainer < conts.length ? activeContainer : 0;
      setActiveContainer(idx);
      setPlan(conts[idx] || null);
      setBasePlan(null);
      // Stability fallthrough: this Stability plan needs MORE containers than Density would.
      if (packingMode === "stability" && r.data.stability_needs_more_space) {
        const dens = r.data.density_container_count;
        toast.warning("Stability mode needs more space. Switch to Density mode to fit in current containers.", {
          id: "stability-fallthrough",
          duration: 12000,
          description: dens ? `Density mode would fit this in ${dens} container${dens === 1 ? "" : "s"}.` : undefined,
          action: {
            label: "Switch to Density",
            onClick: () => { densityRerunRef.current = true; setPackingMode("density"); },
          },
        });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Planning failed");
    } finally {
      setLoading(false);
      if (optToastId) toast.dismiss(optToastId);
    }
  };

  // "Switch to Density" toast action flips the mode then re-runs the plan (state is async,
  // so a ref-guarded effect re-runs once the new mode is committed).
  useEffect(() => {
    if (densityRerunRef.current) { densityRerunRef.current = false; runPlan(optimize); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packingMode]);

  const loadSuggestions = async (rb = rankBy) => {
    setSuggestLoading(true);
    try {
      const r = await axios.post(`${API}/container-planner/fill-suggestions`, { ...planBody(optimize), rank_by: rb }, authH());
      (r.data.items || []).forEach(it => { pppRef.current[it.product_code] = it.pieces_per_pallet; });
      (r.data.combos || []).forEach(c => c.items.forEach(it => { if (it.pieces && it.pallets) pppRef.current[it.product_code] = Math.round(it.pieces / it.pallets); }));
      setSuggest(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load fill suggestions");
    } finally { setSuggestLoading(false); }
  };

  const setFill = (code, pallets, ppp) => {
    if (ppp) pppRef.current[code] = ppp;
    setFillQty(prev => { const n = { ...prev }; if (pallets > 0) n[code] = pallets; else delete n[code]; return n; });
  };
  const applyCombo = (combo) => {
    const n = {};
    combo.items.forEach(it => { n[it.product_code] = it.pallets; if (it.pieces && it.pallets) pppRef.current[it.product_code] = Math.round(it.pieces / it.pallets); });
    setFillQty(n);
  };
  const clearFill = () => setFillQty({});

  // One-tap TOP-UP: fill the swing (last) container to a full load by adding FILLER pallets of
  // the PO's own products. Isolated to the swing container ONLY — its allocation is bumped and
  // just that container is re-planned (locked). Other containers are never touched, so a second
  // top-up can never jumble the rest of the PO. Filler pallets are tagged and persist.
  const topUpSwing = () => {
    const tu = multi?.swing?.topup || [];
    if (!tu.length) { toast.info("The last container is already full"); return; }
    const lastIdx = (cEdit.length || 1) - 1;
    if (lastIdx < 0) return;
    // Placement Auditor: remember the biggest topped-up code for a "Why it failed?" explanation
    { const biggest = [...tu].filter(t => (t.pallets || 0) > 0).sort((a, b) => (b.pallets || 0) - (a.pallets || 0))[0]; if (biggest) pendingAddRef.current[lastIdx] = biggest.product_code; }
    // register any topped-up products in the manifest so pppOf() resolves their pieces/pallet
    setSplitProducts(prev => {
      const known = new Set(prev.map(p => p.code));
      const add = tu.filter(t => !known.has(t.product_code) && (t.pallets || 0) > 0)
        .map(t => ({ code: t.product_code, description: t.description || "", ppp: t.pieces && t.pallets ? Math.round(t.pieces / t.pallets) : 0, required: 0 }));
      return add.length ? [...prev, ...add] : prev;
    });
    markDirty(lastIdx);
    setCEdit(prev => prev.map((c, i) => {
      if (i !== lastIdx) return c;
      const alloc = { ...c.alloc }, filler = { ...(c.filler || {}) };
      tu.forEach(t => {
        const n = t.pallets || 0;
        if (n > 0) { alloc[t.product_code] = (alloc[t.product_code] || 0) + n; filler[t.product_code] = (filler[t.product_code] || 0) + n; }
      });
      return { ...c, alloc, filler, arrangement: null };
    }));
    setActiveContainer(lastIdx);
    toast.success("Topping up the last container…");
    // the LOCKED recompute effect re-plans only this container; its capacity gate (aligned with
    // Re-Optimize) reverts the add if it can't fit on weight, height OR floor length after nesting.
  };

  // One-tap DROP & RESHIP: remove the swing (last) container from the order and re-optimize the
  // remaining containers from the current allocation (filler tags preserved).
  const dropSwing = () => {
    if (!multi || cEdit.length < 2) return;
    const kept = cEdit.slice(0, -1);
    const dropped = cEdit[cEdit.length - 1];
    const droppedPallets = Object.values(dropped.alloc || {}).reduce((s, v) => s + (v || 0), 0);
    reoptimizeFrom(kept, `Dropped ${droppedPallets} pallets — re-planned the remaining containers`);
  };

  // debounced re-plan whenever the added-fill quantities change
  useEffect(() => {
    if (!plan) return;
    const t = setTimeout(() => { runPlan(optimize); }, 450);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fillQty]);

  // debounced re-plan whenever the manual arrangement changes
  useEffect(() => {
    if (mode !== "manual") return;
    const t = setTimeout(() => { runPlan(false); }, 450);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arrangement, mode]);

  // debounced re-plan whenever remove-pallet quantities change
  useEffect(() => {
    if (!plan) return;
    const t = setTimeout(() => { runPlan(optimize); }, 450);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [removeQty]);

  // keep the displayed plan (and therefore the fill suggestions) in sync when loading
  // rules change — otherwise a stale plan can disagree with a fresh fill computation
  useEffect(() => {
    if (!plan || editSplit) return;
    setSuggest(null);
    const t = setTimeout(() => { runPlan(optimize); }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowRotation, ignoreLayerCap, topClearance, palletGap]);


  const exportFile = async (kind) => {
    try {
      // Print EXACTLY what's on screen — send the rendered container plans (with their real
      // placement coordinates) so the backend never re-plans. This makes the PDF/Excel a 1:1
      // blueprint of the visual workspace (same lanes, same rows, same positions).
      const plans = (multi?.containers?.length) ? multi.containers : (plan ? [plan] : []);
      if (!plans.length) { toast.error("Run a plan first"); return; }
      // ONE resolved colour map across every container -> the backend prints THESE exact colours.
      const allPl = [];
      plans.forEach(p => (p.placement || []).forEach(x => allPl.push(x)));
      const body = {
        rendered_plans: plans,
        title: multi?.title || plan?.title || "Stuffing Plan",
        split: plans.length > 1,
        color_map: buildColorMap(allPl),
      };
      const r = await axios.post(`${API}/container-planner/export.${kind}`, body, { ...authH(), responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `stuffing-plan.${kind}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  // ---- multi-container split editing ----
  const pppOf = (code) => (splitProducts.find(p => p.code === code)?.ppp) || 0;

  // specOf: build the per-container lines. Each code is split into its PO portion and its
  // FILLER portion (Top-Up / catalog adds) so the backend tags every filler pallet `added`.
  const specOf = (c) => {
    const lines = [];
    Object.entries(c.alloc).forEach(([code, plt]) => {
      if (!(plt > 0)) return;
      const ppp = pppOf(code) || 0;
      const f = Math.min(c.filler?.[code] || 0, plt);
      const po = plt - f;
      if (po > 0) lines.push({ product_code: code, qty: po * ppp });
      if (f > 0) lines.push({ product_code: code, qty: f * ppp, added: true });
    });
    return { container_type_id: c.type_id, lines, arrangement: c.arrangement || undefined };
  };
  const payloadFrom = (cols) => cols.map(c => specOf(c));
  const buildContainersPayload = () => payloadFrom(cEdit);

  // Live whole-PO FILLER manifest: filler pallet counts come from the ACTIVE allocation (cEdit)
  // so it updates instantly as pallets are added/removed/dragged; per-pallet weight is taken from
  // the latest plan's product rows (stable). Aggregated across every container.
  const fillerSummary = useMemo(() => {
    const meta = {};
    (multi?.containers || []).forEach(c => (c.product_rows || []).forEach(r => {
      if (!r.matched || meta[r.product_code]) return;
      meta[r.product_code] = { description: r.description || "", unit: r.pallet_count ? (r.total_weight_kg || 0) / r.pallet_count : 0 };
    }));
    const agg = {};
    cEdit.forEach(c => Object.entries(c.filler || {}).forEach(([code, f]) => {
      const n = Math.min(f || 0, c.alloc?.[code] || 0);
      if (n <= 0) return;
      if (!agg[code]) agg[code] = { code, description: meta[code]?.description || splitProducts.find(p => p.code === code)?.description || "", pallets: 0, weight: 0 };
      agg[code].pallets += n;
      agg[code].weight += n * (meta[code]?.unit || 0);
    }));
    const rows = Object.values(agg).sort((a, b) => b.pallets - a.pallets);
    return { rows, totalPallets: rows.reduce((s, r) => s + r.pallets, 0), totalWeight: rows.reduce((s, r) => s + r.weight, 0) };
  }, [cEdit, multi, splitProducts]);

  // Live utilisation averages (update instantly on drag/drop via `multi.containers`):
  //  • Core  = mean payload % of the FULL bins (every container except the last/swing one)
  //  • Global = mean payload % across ALL containers, including the partial swing container
  const utilStats = useMemo(() => {
    const conts = multi?.containers || [];
    if (!conts.length) return null;
    const mean = (arr) => arr.length ? Math.round(arr.reduce((s, c) => s + (c.utilisation_weight_pct || 0), 0) / arr.length) : 0;
    const hasSwing = conts.length > 1;                 // swing = the last container in a multi-split
    const full = hasSwing ? conts.slice(0, -1) : conts;
    const global = mean(conts);
    return { core: full.length ? mean(full) : global, global, hasSwing };
  }, [multi]);

  // One-tap: strip EVERY filler (non-PO) pallet from ALL containers, keep each container's PO
  // pallets in place, and re-plan the affected containers (locked — no PO jumbling across containers).
  const clearAllFiller = () => {
    const total = fillerSummary.totalPallets;
    if (!total) { toast.info("No filler pallets to clear"); return; }
    const hasFiller = (c) => Object.values(c.filler || {}).some(f => (f || 0) > 0);
    const affected = cEdit.map((c, i) => hasFiller(c) ? i : -1).filter(i => i >= 0);
    setCEdit(prev => prev.map(c => {
      if (!hasFiller(c)) return c;
      const alloc = { ...c.alloc };
      Object.entries(c.filler || {}).forEach(([code, f]) => {
        const n = Math.min(f || 0, alloc[code] || 0);
        alloc[code] = (alloc[code] || 0) - n;
        if (alloc[code] <= 0) delete alloc[code];
      });
      return { ...c, alloc, filler: {}, arrangement: null };
    }));
    affected.forEach(i => markDirty(i));
    setShowFiller(false);
    toast.success(`Cleared ${total} filler pallet${total === 1 ? "" : "s"} — re-planning…`);
  };

  const recomputeSplit = async (cols = cEdit, ov = {}) => {
    if (!cols.length) return;
    const body = {
      container_type_id: ov.typeId ?? typeId,
      allow_rotation: ov.allowRotation ?? allowRotation,
      ignore_layer_cap: ov.ignoreLayerCap ?? ignoreLayerCap,
      combine_bases: ov.combineBases ?? combineBases,
      packing_mode: ov.packingMode ?? packingMode,
      ledge_guard: ov.ledgeGuard ?? ledgeGuard,
      strict_ctu: ov.strictCtu ?? strictCtu,
      us_highway_mode: ov.usHighway ?? usHighway,
      us_decision_suite_mode: ov.usDecisionSuite ?? usDecisionSuite,
      top_clearance_in: Number(ov.topClearance ?? topClearance) || 0,
      pallet_margin_in: Number(ov.palletGap ?? palletGap) || 0,
      max_stack_weight_kg: Number(ov.maxStackWeight ?? maxStackWeight) || 1600,
      center_gap_in: Number(ov.centerGap ?? centerGap) || 0,
      max_overhang_in: Number(ov.maxOverhang ?? maxOverhang) || 0,
      cross_clearance_in: Number(ov.crossClearance ?? crossClearance) || 0,
      containers: payloadFrom(cols),
    };
    if (source === "po") body.po_id = poId; else { body.lines = draft?.lines || []; body.ship_to_location = draft?.ship_to_location; }
    try {
      const r = await axios.post(`${API}/container-planner/plan-multi`, body, authH());
      setMulti(r.data);
      lastGoodRef.current = cols.map(c => ({ ...c, alloc: { ...c.alloc }, filler: { ...(c.filler || {}) } }));
      lastGoodPoolRef.current = pool;
      dirtyRef.current = new Set();
      const idx = Math.min(activeContainer, r.data.container_count - 1);
      setActiveContainer(idx);
      setPlan(r.data.containers[idx] || null);
    } catch (err) { toast.error(err.response?.data?.detail || "Recompute failed"); }
  };

  // physics options shared by the single-container re-plan
  const _physOpts = () => ({
    allow_rotation: allowRotation, ignore_layer_cap: ignoreLayerCap, combine_bases: combineBases,
    packing_mode: packingMode, strict_ctu: strictCtu, us_highway_mode: usHighway, us_decision_suite_mode: usDecisionSuite,
    ledge_guard: ledgeGuard,
    top_clearance_in: Number(topClearance) || 0, pallet_margin_in: Number(palletGap) || 0,
    max_stack_weight_kg: Number(maxStackWeight) || 1600, center_gap_in: Number(centerGap) || 0,
    max_overhang_in: Number(maxOverhang) || 0, cross_clearance_in: Number(crossClearance) || 0,
  });
  const planOne = async (spec) => {
    const r = await axios.post(`${API}/container-planner/plan-one`, { ..._physOpts(), container_type_id: spec.container_type_id, lines: spec.lines, arrangement: spec.arrangement }, authH());
    return r.data;
  };

  // ---- Placement Decision Auditor (READ-ONLY): ask the backend for the ordered PASS/FAIL rule
  // tree explaining a pallet addition. Never changes packing/stacking — pure reporting. ----
  const linesFromAlloc = (alloc = {}) => Object.entries(alloc)
    .filter(([, plt]) => plt > 0)
    .map(([code, plt]) => ({ product_code: code, qty: plt * (pppOf(code) || 1) }));
  const runAudit = async (payload, { onlyIfBlocked = false, showLoading = false } = {}) => {
    if (isCPro) return;   // Placement Auditor is hidden for CUSTOMER_PRO
    const token = ++auditReqRef.current;
    // instantly clear any stale report and show a loading state for THIS candidate
    if (showLoading) setAudit({ loading: true, candidate: { product_code: payload?.candidate?.product_code } });
    try {
      const r = await axios.post(`${API}/container-planner/audit-placement`,
        { ..._physOpts(), container_type_id: typeId, ...payload }, authH());
      if (token !== auditReqRef.current) return r.data;   // a newer audit superseded this one — ignore stale response
      const data = r.data;
      if (data?.verdict === "blocked") setLastAudit(data);   // keep for the persistent Telemetry Auditor button
      // proactive (manual stacking) auto-opens only on a genuine physical/stacking block (steps 1-5)
      if (onlyIfBlocked && !(data.blocked_step && data.blocked_step <= 5)) {
        if (showLoading) setAudit(null);
        return data;
      }
      setAudit(data);
      return data;
    } catch (e) {
      if (token === auditReqRef.current && showLoading) setAudit(null);
      return null;
    }
  };
  // Fix-It actions from the auditor (only actionable one today: bump the max stack weight cap)
  const applyFixIt = (action) => {
    if (!action) return;
    if (action.type === "set_max_stack_weight" && action.value) {
      setMaxStackWeight(action.value);
      setAudit(null);
      toast.success(`Max stack weight set to ${action.value} kg — re-planning…`);
    }
  };
  // explicit stack-on-top audit fired from the Manual Arrange lane builder
  const auditStack = (lane, baseLayers, code) => {
    if (arrIdx == null) return;
    const ct = cEdit[arrIdx]?.container_type_id || typeId;
    runAudit({ container_type_id: ct, lines: linesFromAlloc(cEdit[arrIdx]?.alloc), target: { lane, base_layers: baseLayers }, candidate: { product_code: code } }, { onlyIfBlocked: true });
  };
  // new floor-stack audit (Manual Arrange "add a stack") — best-fit, auto-opens only if blocked
  const auditNewStack = (lane, code) => {
    if (arrIdx == null) return;
    const ct = cEdit[arrIdx]?.container_type_id || typeId;
    runAudit({ container_type_id: ct, lines: linesFromAlloc(cEdit[arrIdx]?.alloc), candidate: { product_code: code } }, { onlyIfBlocked: true });
  };
  // "?" on an Elevation box: READ-ONLY targeted query — can ONE MORE of that exact product sit on
  // top of that exact stack? Always opens the report (pass or fail). Does not touch the packing.
  const linesFromPlan = (pl) => (pl?.product_rows || [])
    .filter(r => r.matched && ((r.qty || 0) > 0 || (r.pallet_count || 0) > 0))
    .map(r => ({ product_code: r.product_code, qty: (r.qty != null ? r.qty : (r.pallet_count || 0) * (r.pieces_per_pallet || 0)) }));
  const auditBox = (lane, baseCodes, code) => {
    const ci = activeContainer;
    const pl = multi?.containers?.[ci] || activePlan;
    const ct = cEdit[ci]?.type_id || pl?.container?.id || typeId;
    runAudit({ container_type_id: ct, lines: linesFromPlan(pl), target: { lane, base_layers: baseCodes }, candidate: { product_code: code } }, { showLoading: true });
  };
  const relabel = (plan, idx, n, base) => ({ ...plan, title: `${base?.title || "Stuffing Plan"} · Container ${idx + 1} of ${n}`, ship_to: base?.ship_to });
  const summaryOf = (conts) => ({
    total_pallets: conts.reduce((s, c) => s + (c.total_pallets || 0), 0),
    total_weight_kg: Math.round(conts.reduce((s, c) => s + (c.total_weight_kg || 0), 0) * 10) / 10,
    truncated: conts.some(c => c.status === "over_loaded"),
  });

  // LOCKED recompute: re-plan ONLY the edited containers; every OTHER container is frozen.
  // A container is never allowed to exceed 100% — such an edit is rejected & reverted, and the
  // other containers are left untouched. Cross-container reshuffle only happens on Global Re-Optimize.
  const recomputeLocked = async () => {
    const dirty = [...dirtyRef.current];
    dirtyRef.current = new Set();
    if (!dirty.length) return;
    setAudit(prev => (prev && !prev.loading) ? null : prev);   // an edit changed the context — drop any stale auditor report (re-opens fresh below if this edit is rejected)
    const specs = buildContainersPayload();
    const updates = {};
    const rejected = [];
    const _tot = (c) => Object.values(c?.alloc || {}).reduce((s, v) => s + (v || 0), 0);
    setReplanning(dirty);   // mark which container(s) are recomputing -> others show a "locked" padlock
    try {
      for (const i of dirty) {
        if (i < 0 || i >= specs.length) continue;
        try {
          const p = await planOne(specs[i]);
          const cap = p.capacity || {};
          // REMOVAL IS ALWAYS ALLOWED: if this edit did NOT increase the container's pallet
          // count (a removal, a clear, or a neutral swap), accept it unconditionally. Taking
          // load OUT can never make a container "more over capacity", so the guard must never
          // block it — that was the removal paradox (couldn't empty an already-full container).
          const isRemoval = _tot(cEdit[i]) <= _tot(lastGoodRef.current[i]);
          // ALIGNED with Re-Optimize (same axle/stability physics): reject any ADDITION that
          // leaves the container physically over-capacity AFTER the engine's best nesting —
          // over weight, over height, OR over floor length that stacking can't absorb.
          // Compliance Sandbox: NO REVERT. Every drop is accepted regardless of physical
          // legality; the operator sees the reds on the telemetry rail and adjusts by hand.
          const over = p.status === "over_loaded" || cap.over_weight || cap.over_height || (p.utilisation_weight_pct || 0) > 100;
          if (!sandboxMode && over && !isRemoval) rejected.push(i); else updates[i] = p;
        } catch (err) { toast.error(err.response?.data?.detail || "Recompute failed"); return; }
      }
    } finally { setReplanning([]); }
    if (rejected.length) {
      // revert the offending container(s) to the last validated allocation; leave others intact
      setCEdit(prev => prev.map((c, idx) => rejected.includes(idx) ? (lastGoodRef.current[idx] || c) : c));
      // a rejected pool→container move must also put the pallet back in the Unassigned pool
      setPool(lastGoodPoolRef.current);
      let opened = false;
      rejected.forEach(i => {
        const code = pendingAddRef.current[i];
        const ct = lastGoodRef.current[i]?.container_type_id || specs[i]?.container_type_id || typeId;
        const auditPayload = { container_type_id: ct, lines: linesFromAlloc(lastGoodRef.current[i]?.alloc), candidate: { product_code: code } };
        toast.error(`Container ${i + 1} won't fit — that change exceeds the weight, height or floor-length limit (even after stacking). Reverted.`, {
          id: `cap-reject-${i}`,
          action: (code && !isCPro) ? { label: "Why it failed?", onClick: () => runAudit(auditPayload) } : undefined,
        });
        // AUTO-OPEN the Placement Auditor for the first diagnosable rejection (no click needed).
        // showLoading clears any stale report instantly and shows a spinner for THIS SKU.
        if (code && !opened) { opened = true; runAudit(auditPayload, { showLoading: true }); }
      });
    } else {
      lastGoodPoolRef.current = pool;   // this edit was accepted — the pool is now the good baseline
    }
    dirty.forEach(i => { if (!rejected.includes(i)) delete pendingAddRef.current[i]; });   // drop stale candidates on accepted containers
    setMulti(prev => {
      if (!prev) return prev;
      const n = prev.containers.length;
      const conts = prev.containers.map((c, idx) => updates[idx] ? relabel(updates[idx], idx, n, prev) : c);
      return { ...prev, containers: conts, summary: { ...(prev.summary || {}), ...summaryOf(conts) } };
    });
    // snapshot the now-good allocation (accepted edits kept, rejected reverted)
    lastGoodRef.current = cEdit.map((c, idx) => rejected.includes(idx) ? (lastGoodRef.current[idx] || { ...c, alloc: { ...c.alloc }, filler: { ...(c.filler || {}) } }) : { ...c, alloc: { ...c.alloc }, filler: { ...(c.filler || {}) } });
  };

  // Build unified re-optimize input straight from the ACTIVE allocation (cEdit) — the single
  // source of truth. Sends EVERY current pallet (PO + Top-Ups + catalog adds) as explicit
  // lines (PO) + extra_lines (filler), NEVER reverting to the original PO. The greedy splitter
  // preserves the filler tag through the re-split, so FILLER pallets stay flagged.
  // Apply a re-optimize result to the workspace — shared by the direct path and the
  // "extra container confirmed" path.
  const applyReoptResult = (result, okMsg) => {
    setMulti(result);
    skipRecomputeRef.current = true;
    seedEditFromMulti(result);
    setEditSplit(true); setArrIdx(null); setActiveContainer(0);
    setPlan(result.containers[0] || null);
    toast.success(okMsg || "Re-optimised — pallets redistributed for the best overall fit");
    if (result.balance_warning) toast.warning(result.balance_warning, { duration: 9000, id: "reopt-balance-warn" });
  };

  const reoptimizeFrom = async (cols, okMsg) => {
    const totals = {};   // code -> { po: pallets, filler: pallets, ppp }
    cols.forEach(c => Object.entries(c.alloc || {}).forEach(([code, plt]) => {
      if (!(plt > 0)) return;
      const f = Math.min(c.filler?.[code] || 0, plt);
      if (!totals[code]) totals[code] = { po: 0, filler: 0, ppp: pppOf(code) || 0 };
      totals[code].po += (plt - f);
      totals[code].filler += f;
    }));
    const lines = Object.entries(totals).filter(([, t]) => t.po > 0)
      .map(([code, t]) => ({ product_code: code, qty: t.po * t.ppp }));
    const extra_lines = Object.entries(totals).filter(([, t]) => t.filler > 0)
      .map(([code, t]) => ({ product_code: code, qty: t.filler * t.ppp }));
    if (!lines.length && !extra_lines.length) { toast.info("Nothing to optimise"); return; }
    setLoading(true);
    const tid = toast.loading("Re-optimising — prioritising axle & L/R balance…");
    try {
      const body = { ..._physOpts(), container_type_id: typeId, optimize: true, balance_priority: true, lines, target_containers: cols.length };
      if (extra_lines.length) body.extra_lines = extra_lines;
      if (source === "draft") body.ship_to_location = draft?.ship_to_location;
      const r = await axios.post(`${API}/container-planner/plan-multi`, body, authH());
      toast.dismiss(tid);
      const newCount = r.data.container_count || (r.data.containers || []).length;
      // Re-Optimize guard: never silently add a container. If the best fit needs MORE
      // containers than the target, hold the result and ask the operator before proceeding.
      if (newCount > cols.length) {
        setReoptConfirm({ result: r.data, okMsg, target: cols.length, newCount });
        return;
      }
      applyReoptResult(r.data, okMsg);
    } catch (err) { toast.error(err.response?.data?.detail || "Re-optimise failed"); }
    finally { setLoading(false); toast.dismiss(tid); }
  };

  // Global Re-Optimize: the ONLY action allowed to reshuffle pallets across containers.
  const globalReoptimize = () => { if (cEdit.length) reoptimizeFrom(cEdit); };

  // one-tap: shift pallets out of over-length/over-weight containers into ones with room
  const rebalanceSplit = async () => {
    if (!multi) return;
    const containers = editSplit ? buildContainersPayload() : multi.containers.map(c => ({
      container_type_id: c.container?.id || typeId,
      lines: (c.product_rows || []).filter(r => r.matched && (r.qty || 0) > 0).map(r => ({ product_code: r.product_code, qty: r.qty })),
    }));
    const opts = { container_type_id: typeId, allow_rotation: allowRotation, ignore_layer_cap: ignoreLayerCap, combine_bases: combineBases, top_clearance_in: Number(topClearance) || 0, pallet_margin_in: Number(palletGap) || 0, max_stack_weight_kg: Number(maxStackWeight) || 1600 };
    try {
      const rb = await axios.post(`${API}/container-planner/rebalance`, { ...opts, containers }, authH());
      if (!rb.data.changed) { toast.info(rb.data.message || "Nothing to rebalance"); return; }
      const body = { ...opts, containers: rb.data.containers.map(c => ({ container_type_id: c.container_type_id || typeId, lines: c.lines })) };
      if (source === "po") body.po_id = poId; else { body.lines = draft?.lines || []; body.ship_to_location = draft?.ship_to_location; }
      const r = await axios.post(`${API}/container-planner/plan-multi`, body, authH());
      setMulti(r.data); skipRecomputeRef.current = true; seedEditFromMulti(r.data); setEditSplit(true); setArrIdx(null); setActiveContainer(0); setPlan(r.data.containers[0] || null);
      const moved = (rb.data.moves || []).reduce((s, m) => s + m.pallets, 0);
      toast.success(`Rebalanced — moved ${moved} pallet${moved === 1 ? "" : "s"}${rb.data.added_container ? " · added a container" : ""}`);
      if ((rb.data.unplaced || []).length) toast.warning("Some pallets still don't fit anywhere — consider a larger container type.");
    } catch (err) { toast.error(err.response?.data?.detail || "Rebalance failed"); }
  };

  // seed the editable per-container allocation model (cEdit + splitProducts) from a multi result.
  // Filler pallets are re-derived from each row's `filler_pallet_count`, so the FILLER tag
  // survives Re-Optimize and every locked recompute (single source of truth = the allocation).
  const seedEditFromMulti = (result) => {
    const meta = {};
    const cols = (result.containers || []).map(p => {
      const alloc = {}, filler = {};
      (p.product_rows || []).forEach(r => {
        if (!r.matched) return;
        alloc[r.product_code] = (alloc[r.product_code] || 0) + (r.pallet_count || 0);
        const f = r.filler_pallet_count || 0;
        if (f > 0) filler[r.product_code] = (filler[r.product_code] || 0) + f;
        if (!meta[r.product_code]) meta[r.product_code] = { code: r.product_code, description: r.description || "", ppp: r.pieces_per_pallet || 0, required: 0 };
        if (!meta[r.product_code].ppp && r.pieces_per_pallet) meta[r.product_code].ppp = r.pieces_per_pallet;
      });
      return { type_id: typeId, alloc, filler, arrangement: null };
    });
    Object.values(meta).forEach(m => { m.required = cols.reduce((s, c) => s + (c.alloc[m.code] || 0), 0); });
    setSplitProducts(Object.values(meta));
    setCEdit(cols);
    lastGoodRef.current = cols.map(c => ({ ...c, alloc: { ...c.alloc }, filler: { ...c.filler } }));
    lastGoodPoolRef.current = pool;
    dirtyRef.current = new Set();
  };

  const enterEditSplit = () => {
    if (!multi) return;
    seedEditFromMulti(multi);
    setEditSplit(true);
    setArrIdx(null);
  };

  const exitEditSplit = () => { setEditSplit(false); setArrIdx(null); };

  const markDirty = (...idxs) => idxs.forEach(i => { if (i != null && i >= 0) dirtyRef.current.add(i); });
  const emptyPlan = (tid) => {
    const t = types.find(x => x.id === tid) || types[0] || {};
    return {
      container: { id: t.id, name: t.name, internal_length_in: t.internal_length_in, internal_width_in: t.internal_width_in, internal_height_in: t.internal_height_in, max_payload_kg: t.max_payload_kg },
      placement: [], product_rows: [], status: "under_loaded", total_pallets: 0, total_weight_kg: 0,
      utilisation_length_pct: 0, utilisation_weight_pct: 0, weight_distribution: {}, quality: null, advisory: [],
    };
  };

  // clamp filler pallets so they never exceed the total pallets of that code in the container
  const _clampFiller = (filler, code, alloc) => {
    const n = { ...(filler || {}) };
    const cap = alloc[code] || 0;
    if ((n[code] || 0) > cap) { if (cap > 0) n[code] = cap; else delete n[code]; }
    return n;
  };
  const setAlloc = (ci, code, val) => {
    markDirty(ci);
    setCEdit(prev => prev.map((c, i) => {
      if (i !== ci) return c;
      const alloc = { ...c.alloc, [code]: val };
      return { ...c, alloc, filler: _clampFiller(c.filler, code, alloc), arrangement: null };
    }));
  };
  const setCType = (ci, tid) => { markDirty(ci); setCEdit(prev => prev.map((c, i) => i === ci ? { ...c, type_id: tid, arrangement: null } : c)); };
  const addContainer = () => {
    setCEdit(prev => [...prev, { type_id: typeId, alloc: {}, filler: {}, arrangement: null }]);
    lastGoodRef.current = [...lastGoodRef.current, { type_id: typeId, alloc: {}, filler: {}, arrangement: null }];
    setMulti(prev => prev ? { ...prev, containers: [...prev.containers, emptyPlan(typeId)], container_count: prev.containers.length + 1 } : prev);
  };
  const removeContainer = (ci) => {
    const c = cEdit[ci];
    const empty = !c.arrangement && Object.values(c.alloc).every(v => !v);
    if (!empty) { toast.error("Empty this container first (set its pallets to 0)"); return; }
    setCEdit(prev => prev.filter((_, i) => i !== ci));
    lastGoodRef.current = lastGoodRef.current.filter((_, i) => i !== ci);
    setMulti(prev => prev ? { ...prev, containers: prev.containers.filter((_, i) => i !== ci), container_count: Math.max(0, prev.containers.length - 1) } : prev);
    // keep live pointers aimed at the SAME container after the array reindexes
    if (arrIdx === ci) setArrIdx(null);
    else if (arrIdx != null && arrIdx > ci) setArrIdx(a => a - 1);
    setActiveContainer(a => Math.max(0, Math.min(a > ci ? a - 1 : a, cEdit.length - 2)));
  };

  const resetSplit = async () => {
    setArrIdx(null);
    setFillQty(prev => Object.keys(prev).length ? {} : prev);
    setTopupExtra(prev => prev.length ? [] : prev);
    setRemoveQty(prev => Object.keys(prev).length ? {} : prev);
    setPool({}); lastGoodPoolRef.current = {};   // reset clears any deferred (Unassigned) pallets
    try {
      const base = planBody(false);
      delete base.extra_lines; delete base.qty_deltas;      // reset = original PO only
      const r = await axios.post(`${API}/container-planner/plan-multi`, base, authH());
      setMulti(r.data);
      skipRecomputeRef.current = true;   // preserve the fresh ERP result (swing/hints)
      seedEditFromMulti(r.data);         // filler re-derives to empty (no added rows)
      setEditSplit(true);
      setActiveContainer(0);
      setPlan(r.data.containers[0] || null);
      toast.success("Reset to ERP suggestion");
    } catch (err) { toast.error(err.response?.data?.detail || "Reset failed"); }
  };

  const updateArr = (ci, fn) => { markDirty(ci); setCEdit(prev => prev.map((c, i) => i === ci ? { ...c, arrangement: fn(c.arrangement || { A: [], B: [] }) } : c)); };
  const openArrange = (ci) => {
    markDirty(ci);
    setCEdit(prev => prev.map((c, i) => {
      if (i !== ci || c.arrangement) return c;
      const src = multi?.containers?.[ci];
      const arr = { A: [], B: [] };
      (src?.placement || []).slice().sort((a, b) => a.start_in - b.start_in).forEach(p => {
        const L = p.lane === "B" ? "B" : "A";
        const layers = (p.layer_products && p.layer_products.length) ? [...p.layer_products] : [p.product_code];
        arr[L].push(p.rotated ? { layers, rotated: true } : { layers });
      });
      return { ...c, arrangement: arr };
    }));
    setActiveContainer(ci);
    setArrIdx(ci);
  };
  // drag a product's pallets from one container column into another (moves its filler too)
  const moveAlloc = (fromCi, toCi, code) => setCEdit(prev => {
    if (fromCi === toCi) return prev;
    pendingAddRef.current[toCi] = code;   // Placement Auditor: this code was just added to toCi
    const amt = prev[fromCi]?.alloc?.[code] || 0;
    if (!amt || prev[fromCi]?.arrangement || prev[toCi]?.arrangement) return prev;
    const fillerAmt = Math.min(prev[fromCi]?.filler?.[code] || 0, amt);
    markDirty(fromCi, toCi);
    return prev.map((c, i) => {
      if (i === fromCi) {
        const filler = { ...(c.filler || {}) }; delete filler[code];
        return { ...c, alloc: { ...c.alloc, [code]: 0 }, filler, arrangement: null };
      }
      if (i === toCi) {
        const filler = { ...(c.filler || {}) };
        if (fillerAmt > 0) filler[code] = (filler[code] || 0) + fillerAmt;
        return { ...c, alloc: { ...c.alloc, [code]: (c.alloc[code] || 0) + amt }, filler, arrangement: null };
      }
      return c;
    });
  });
  const arrMut = {
    addStacksToLane: (lane, code, n = 1) => { updateArr(arrIdx, a => ({ ...a, [lane]: [...(a[lane] || []), ...Array.from({ length: Math.max(1, n) }, () => ({ layers: [code] }))] })); auditNewStack(lane, code); },
    addLayerToStack: (lane, idx, code) => updateArr(arrIdx, a => ({ ...a, [lane]: a[lane].map((c, i) => i === idx ? { layers: [...c.layers, code] } : c) })),
    removeLayer: (lane, idx, li) => updateArr(arrIdx, a => ({ ...a, [lane]: a[lane].map((c, i) => i === idx ? { layers: c.layers.filter((_, j) => j !== li) } : c).filter(c => c.layers.length) })),
    removeStack: (lane, idx) => updateArr(arrIdx, a => ({ ...a, [lane]: a[lane].filter((_, i) => i !== idx) })),
    clearArrangement: () => { markDirty(arrIdx); setCEdit(prev => prev.map((c, i) => i === arrIdx ? { ...c, arrangement: { A: [], B: [] } } : c)); },
  };

  // ---- Q4: per-container add. `asFiller` marks the pallet as non-PO (Top-Up / catalog). ----
  const addPalletToContainer = (ci, code, ppp, desc = "", asFiller = true) => {
    if (!editSplit) enterEditSplit();
    markDirty(ci);
    pendingAddRef.current[ci] = code;   // Placement Auditor: candidate just added to this container (Catalog Tray / Top-View drop)
    setSplitProducts(prev => prev.some(p => p.code === code)
      ? prev
      : [...prev, { code, description: desc, ppp: ppp || 0, required: 0 }]);
    setCEdit(prev => prev.map((c, i) => {
      if (i !== ci) return c;
      const alloc = { ...c.alloc, [code]: (c.alloc[code] || 0) + 1 };
      const filler = asFiller ? { ...(c.filler || {}), [code]: (c.filler?.[code] || 0) + 1 } : (c.filler || {});
      // Lock the Sequence: once an arrangement exists (from Sandbox drag OR the
      // Arrange dialog), it is HELD across every subsequent operation until an
      // explicit Re-Optimize / Run Plan. We APPEND the new pallet to the lighter
      // lane so the manual sequence is preserved. If no arrangement exists yet,
      // fall back to auto-pack (arrangement=null, unchanged legacy behaviour).
      if (c.arrangement) {
        const cur = c.arrangement;
        const lenA = (cur.A || []).length, lenB = (cur.B || []).length;
        const targetLane = lenA <= lenB ? "A" : "B";
        const nextArr = { A: [...(cur.A || [])], B: [...(cur.B || [])] };
        nextArr[targetLane].push({ layers: [code] });
        return { ...c, alloc, filler, arrangement: nextArr };
      }
      return { ...c, alloc, filler, arrangement: null };
    }));
  };
  const loadContainerFill = async (ci) => {
    const cont = multi?.containers?.[ci];
    if (!cont) return;
    const lines = (cont.product_rows || [])
      .filter(r => r.matched && (r.qty || 0) > 0)
      .map(r => ({ product_code: r.product_code, qty: r.qty }));
    setMfLoading(true); setMfSuggest(null);
    try {
      const ctid = cEdit[ci]?.type_id || cont.container?.id || typeId;
      const body = { container_type_id: ctid, lines, allow_rotation: allowRotation,
        ignore_layer_cap: ignoreLayerCap, combine_bases: combineBases, top_clearance_in: Number(topClearance) || 0, pallet_margin_in: Number(palletGap) || 0,
        max_stack_weight_kg: Number(maxStackWeight) || 1600, rank_by: "space" };
      const r = await axios.post(`${API}/container-planner/fill-suggestions`, body, authH());
      setMfSuggest({ ci, ...r.data });
    } catch (err) { toast.error(err.response?.data?.detail || "Could not fetch fill suggestions"); }
    finally { setMfLoading(false); }
  };
  const underAllocated = () => splitProducts
    .map(p => ({ ...p, allocated: cEdit.reduce((s, c) => s + (c.alloc[p.code] || 0), 0) }))
    .filter(p => p.required && p.allocated < p.required);

  // options change affects ALL containers -> mark every one dirty (still no cross-container reshuffle)
  useEffect(() => {
    if (!editSplit || !cEdit.length) return;
    dirtyRef.current = new Set(cEdit.map((_, i) => i));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowRotation, ignoreLayerCap, combineBases, topClearance, palletGap, maxStackWeight]);

  // LOCKED live recompute (debounced): re-plans only the containers marked dirty by an edit
  useEffect(() => {
    if (!editSplit || !cEdit.length) return;
    if (skipRecomputeRef.current) { skipRecomputeRef.current = false; return; }
    const t = setTimeout(() => { recomputeLocked(); }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cEdit, editSplit, allowRotation, ignoreLayerCap, combineBases, topClearance, palletGap, maxStackWeight]);

  // refresh per-container catalog fill suggestions when the active container changes
  useEffect(() => {
    if (showFill && multi) loadContainerFill(activeContainer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeContainer]);

  // ---- saved splits (named edited plans stored against a PO) ----
  const loadSavedSplits = async (pid) => {
    if (!pid) { setSavedSplits([]); return []; }
    try { const r = await axios.get(`${API}/container-planner/splits?po_id=${pid}`, authH()); const s = r.data.splits || []; setSavedSplits(s); return s; }
    catch { setSavedSplits([]); return []; }
  };
  useEffect(() => { if (editSplit && source === "po" && poId) loadSavedSplits(poId); }, [editSplit, source, poId]);

  // Auto-detect a saved split when a PO is picked and offer a one-tap restore prompt.
  useEffect(() => {
    let cancelled = false;
    setRestorePrompt(null);
    if (source !== "po" || !poId) { setSavedSplits([]); return; }
    (async () => {
      const splits = await loadSavedSplits(poId);
      if (cancelled || !splits.length) return;
      const recent = [...splits].sort((a, b) =>
        new Date(b.updated_at || b.created_at || 0) - new Date(a.updated_at || a.created_at || 0))[0];
      setRestorePrompt(recent);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poId, source]);

  const applyRestore = (s) => {
    setSplitMulti(true);
    setMode("auto");
    setBasePlan(null);
    applySavedSplit(s);          // sets splitProducts + cEdit + editSplit -> debounced recompute builds `multi`
    setRestorePrompt(null);
  };

  const saveSplit = async (name) => {
    if (!name || !name.trim()) return;
    if (source !== "po" || !poId) { toast.error("Pick a PO to save a split against it"); return; }
    try {
      const deferredCount = Object.values(pool).reduce((s, e) => s + (e.po || 0) + (e.filler || 0), 0);
      const body = {
        po_id: poId, name: name.trim(), container_type_id: typeId,
        containers: cEdit, products: splitProducts, summary: multi?.summary,
        deferred: pool,   // pallets still in the Unassigned pool = NOT SHIPPED / DEFERRED
        allow_rotation: allowRotation, ignore_layer_cap: ignoreLayerCap, combine_bases: combineBases,
        packing_mode: packingMode, strict_ctu: strictCtu, us_highway_mode: usHighway, us_decision_suite_mode: usDecisionSuite,
        top_clearance_in: Number(topClearance) || 0, pallet_margin_in: Number(palletGap) || 0,
        max_stack_weight_kg: Number(maxStackWeight) || 1600,
        center_gap_in: Number(centerGap) || 0, max_overhang_in: Number(maxOverhang) || 0, cross_clearance_in: Number(crossClearance) || 0,
      };
      await axios.post(`${API}/container-planner/splits`, body, authH());
      toast.success(`Split "${name.trim()}" saved`);
      if (deferredCount > 0) {
        const codes = Object.entries(pool).map(([c, e]) => `${c} ×${(e.po || 0) + (e.filler || 0)}`).join(", ");
        toast.warning(`⚠ ${deferredCount} pallet(s) flagged DEFERRED / NOT SHIPPED (still in Unassigned): ${codes}`, { duration: 9000, id: "save-deferred-warn" });
      }
      loadSavedSplits(poId);
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
  };
  const applySavedSplit = (s) => {
    const cols = (s.containers || []).map(c => ({ type_id: c.type_id || s.container_type_id || typeId, alloc: c.alloc || {}, filler: c.filler || {}, arrangement: c.arrangement || null }));
    setSplitProducts(s.products || []);
    setPool(s.deferred || {}); lastGoodPoolRef.current = s.deferred || {};   // restore deferred pool
    setCEdit(cols);
    // Force EVERY toggle/slider back to the saved snapshot so the reloaded plan matches exactly.
    if (s.container_type_id) setTypeId(s.container_type_id);
    if (typeof s.allow_rotation === "boolean") setAllowRotation(s.allow_rotation);
    if (typeof s.ignore_layer_cap === "boolean") setIgnoreLayerCap(s.ignore_layer_cap);
    if (typeof s.combine_bases === "boolean") setCombineBases(s.combine_bases);
    if (s.packing_mode) setPackingMode(s.packing_mode);
    if (typeof s.ledge_guard === "boolean") setLedgeGuard(s.ledge_guard);
    if (typeof s.strict_ctu === "boolean") setStrictCtu(s.strict_ctu);
    if (typeof s.us_highway_mode === "boolean") setUsHighway(s.us_highway_mode);
    if (s.top_clearance_in != null) setTopClearance(s.top_clearance_in);
    if (s.max_stack_weight_kg) setMaxStackWeight(s.max_stack_weight_kg);
    if (s.pallet_margin_in != null) setPalletGap(s.pallet_margin_in);
    if (s.center_gap_in != null) setCenterGap(s.center_gap_in);
    if (s.max_overhang_in != null) setMaxOverhang(s.max_overhang_in);
    if (s.cross_clearance_in != null) setCrossClearance(s.cross_clearance_in);
    setEditSplit(true);
    setArrIdx(null);
    setActiveContainer(0);
    skipRecomputeRef.current = true;   // do the full rebuild ourselves; skip the locked effect once
    // Fix the async race: React state setters above are async, so pass the saved settings
    // EXPLICITLY into the recompute instead of reading stale state.
    recomputeSplit(cols, {
      typeId: s.container_type_id || typeId,
      allowRotation: typeof s.allow_rotation === "boolean" ? s.allow_rotation : allowRotation,
      ignoreLayerCap: typeof s.ignore_layer_cap === "boolean" ? s.ignore_layer_cap : ignoreLayerCap,
      combineBases: typeof s.combine_bases === "boolean" ? s.combine_bases : combineBases,
      packingMode: s.packing_mode || packingMode,
      ledgeGuard: typeof s.ledge_guard === "boolean" ? s.ledge_guard : ledgeGuard,
      strictCtu: typeof s.strict_ctu === "boolean" ? s.strict_ctu : strictCtu,
      usHighway: typeof s.us_highway_mode === "boolean" ? s.us_highway_mode : usHighway,
      topClearance: s.top_clearance_in != null ? s.top_clearance_in : topClearance,
      palletGap: s.pallet_margin_in != null ? s.pallet_margin_in : palletGap,
      maxStackWeight: s.max_stack_weight_kg || maxStackWeight,
      centerGap: s.center_gap_in != null ? s.center_gap_in : centerGap,
      maxOverhang: s.max_overhang_in != null ? s.max_overhang_in : maxOverhang,
      crossClearance: s.cross_clearance_in != null ? s.cross_clearance_in : crossClearance,
    });
    toast.success(`Loaded "${s.name}"`);
  };
  const deleteSavedSplit = async (id) => {
    try { await axios.delete(`${API}/container-planner/splits/${id}`, authH()); loadSavedSplits(poId); toast.success("Deleted"); }
    catch { toast.error("Delete failed"); }
  };

  // ---- Compliance Sandbox: mutate the arrangement in-place when a pallet is dropped
  //      in an elevation panel. build_manual_plan reflows every column from the container
  //      nose, so the visible plan compacts automatically. NO REVERT is enforced upstream
  //      in recomputeLocked (sandboxMode short-circuits the over-capacity guard). ----
  const _seedArrangement = (pl) => {
    const arr = { A: [], B: [] };
    (pl?.placement || []).slice().sort((a, b) => a.start_in - b.start_in).forEach(p => {
      // AB (full-width) columns MUST survive the round-trip through the sandbox
      // arrangement schema — otherwise build_manual_plan drops the AB-ness on
      // reseed and _uds_spread_placement's AB-barrier logic never engages,
      // reintroducing the cross-lane visual gaps the operator sees on PO 20681.
      //
      // Seed AB pallets into lane A (arbitrary — build_manual_plan re-derives
      // the lane from `full_width`) with BOTH the original orientation hint
      // (`rotated`) AND an explicit `full_width: true` flag. The backend
      // honours `full_width` after _orient, restoring lane="AB" on the placed
      // pallet regardless of which orientation _orient would have picked.
      const isAB = p.lane === "AB" || !!p.full_width;
      const L = isAB ? "A" : (p.lane === "B" ? "B" : "A");
      const layers = (p.layer_products && p.layer_products.length) ? [...p.layer_products] : [p.product_code];
      const col = { layers };
      if (p.rotated) col.rotated = true;
      if (isAB) col.full_width = true;
      arr[L].push(col);
    });
    return arr;
  };
  const _ensureArrangement = (ci) => {
    // If the active container has no explicit arrangement yet, seed it from its
    // current placement — every subsequent drag mutates THIS arrangement (never
    // the auto-packer's) so the operator's manual sequence is preserved.
    setCEdit(prev => prev.map((c, i) => {
      if (i !== ci || c.arrangement) return c;
      const pl = multi?.containers?.[ci];
      return { ...c, arrangement: _seedArrangement(pl) };
    }));
  };
  const handleSandboxMove = (source, targetLane, drop_along_in /* , drop_h_in */) => {
    const ci = activeContainer;
    const cur = cEdit[ci];
    if (!cur) return;
    const srcPlacement = multi?.containers?.[ci]?.placement || [];
    // Seed the arrangement lazily on the first drag so nothing else has to change up-front.
    const arrangement = cur.arrangement || _seedArrangement(multi?.containers?.[ci]);
    const nextArr = applySandboxMove(arrangement, srcPlacement, source, targetLane, drop_along_in);
    if (!nextArr || nextArr === arrangement) return;
    markDirty(ci);
    setCEdit(prev => prev.map((c, i) => i === ci ? { ...c, arrangement: nextArr } : c));
  };

  // Toggle 90° rotation of the ENTIRE column at (lane, arr_index). Applies to the
  // manual arrangement so it flows through build_manual_plan → the footprint width
  // becomes the along dimension and axle balance recomputes immediately. Rotating a
  // floor pallet rotates the whole stack (the arrangement column IS the whole stack).
  const handleRotateStack = (lane, arr_index) => {
    const ci = activeContainer;
    const cur = cEdit[ci];
    if (!cur) return;
    const arrangement = cur.arrangement || _seedArrangement(multi?.containers?.[ci]);
    const L = lane === "B" ? "B" : "A";
    if (!Array.isArray(arrangement[L]) || arr_index < 0 || arr_index >= arrangement[L].length) return;
    const nextArr = {
      A: [...(arrangement.A || [])],
      B: [...(arrangement.B || [])],
    };
    const col = nextArr[L][arr_index] || { layers: [] };
    nextArr[L][arr_index] = { ...col, rotated: !col.rotated };
    markDirty(ci);
    setCEdit(prev => prev.map((c, i) => i === ci ? { ...c, arrangement: nextArr } : c));
  };

  // When the Compliance Sandbox is switched ON, seed the arrangement for every
  // container from its current placement so drag/drop starts with the exact
  // layout the operator sees on screen. Also flip US Highway ON by default —
  // the sandbox is designed around the Law-of-Levers Scale Ticket read-out.
  //
  // Two side-effects are CRITICAL here (bug found by testing_agent iter_206):
  //   (a) markDirty(i) for every seeded container — otherwise the debounced
  //       recomputeLocked bails ("if (!dirty.length) return") and the backend
  //       never re-plans via build_manual_plan → placement has no arr_index →
  //       the top-view ↻ rotate pill never renders and Stack Rotation is dead.
  //   (b) Inject arr_index into the current multi.placement so the ↻ pill is
  //       visible IMMEDIATELY, even before the 300 ms server round-trip.
  useEffect(() => {
    if (!sandboxMode || !editSplit || !cEdit.length) return;
    // IMPORTANT: React 18 defers setState updater callbacks to the render phase,
    // so we MUST compute `seeded` synchronously BEFORE calling setCEdit — otherwise
    // markDirty/setMulti below see an empty array and the ↻ pill never renders.
    const seeded = [];
    cEdit.forEach((c, i) => {
      if (c.arrangement) return;
      const pl = multi?.containers?.[i];
      if (!pl?.placement?.length) return;
      seeded.push(i);
    });
    if (!seeded.length) return;
    setCEdit(prev => prev.map((c, i) => {
      if (!seeded.includes(i)) return c;
      const pl = multi?.containers?.[i];
      return { ...c, arrangement: _seedArrangement(pl) };
    }));
    {
      seeded.forEach(i => markDirty(i));   // (a) unblock recomputeLocked
      // (b) mirror per-lane arr_index into multi.containers[*].placement so
      // topdownSvg emits the ↻ pill for every stack right away.
      setMulti(prev => {
        if (!prev?.containers?.length) return prev;
        const cs = prev.containers.map((ct, i) => {
          if (!seeded.includes(i) || !ct?.placement?.length) return ct;
          const sorted = [...ct.placement].sort((a, b) => (a.start_in || 0) - (b.start_in || 0));
          const idxOf = new Map();
          const laneCount = { A: 0, B: 0 };
          sorted.forEach(p => {
            const L = p.lane === "B" ? "B" : "A";
            idxOf.set(p, laneCount[L]++);
          });
          return { ...ct, placement: ct.placement.map(p => (p.arr_index != null ? p : { ...p, arr_index: idxOf.get(p) ?? 0 })) };
        });
        return { ...prev, containers: cs };
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sandboxMode, editSplit]);

  const filteredPos = pos.filter(p => !poSearch || (p.po_number || "").toLowerCase().includes(poSearch.toLowerCase()) || (p.customer_name || "").toLowerCase().includes(poSearch.toLowerCase()));

  // ---- derived state for the visual workspace ----
  const activePlan = multi?.containers?.[activeContainer] || null;
  // ONE global colour map over EVERY container's codes so a SKU keeps its colour on every tab,
  // and no two different SKUs collide within the load. The same resolved map is sent on export
  // so the PDF/Excel match the screen exactly.
  const cmap = useMemo(() => {
    const conts = multi?.containers?.length ? multi.containers : (activePlan ? [activePlan] : []);
    const all = [];
    conts.forEach(c => (c.placement || []).forEach(p => all.push(p)));
    return buildColorMap(all);
  }, [multi, activePlan]);
  const activeTypeId = cEdit[activeContainer]?.type_id || typeId;
  const poNumber = source === "po" ? (pos.find(p => p.id === poId)?.po_number || "") : (draft?.po_number || "");
  const dropAdd = (code, ppp, desc) => addPalletToContainer(activeContainer, code, ppp, desc);

  // ✕ on an Elevation box: MOVE that exact pallet to the Unassigned pool (never delete). The box's
  // border tells us its identity — dashed = Filler, solid = PO — so the removal is precise.
  const removeOne = (code, isFiller) => {
    const col = cEdit[activeContainer];
    const cur = col?.alloc?.[code] || 0;
    if (cur <= 0) { toast.info(`No ${code} pallets in this container`); return; }
    const fillerCur = col?.filler?.[code] || 0;
    const poCur = cur - fillerCur;
    // honour the clicked box; fall back to whatever kind actually remains
    let kind = isFiller ? "filler" : "po";
    if (kind === "filler" && fillerCur <= 0) kind = "po";
    if (kind === "po" && poCur <= 0) kind = "filler";
    markDirty(activeContainer);
    setCEdit(prev => prev.map((c, i) => {
      if (i !== activeContainer) return c;
      const alloc = { ...c.alloc, [code]: (c.alloc[code] || 0) - 1 };
      if (alloc[code] <= 0) delete alloc[code];
      const filler = { ...(c.filler || {}) };
      if (kind === "filler") { filler[code] = (filler[code] || 0) - 1; if (filler[code] <= 0) delete filler[code]; }
      // Lock the Sequence: once a manual arrangement exists, ✕ must NOT reset it.
      // Peel the first matching layer out of the arrangement (top-of-last-stack
      // first, so a stacked pallet unpicks top-first) and let build_manual_plan
      // reflow the remaining pallets toward the nose.
      if (c.arrangement) {
        const cur = c.arrangement;
        const next = { A: [...(cur.A || [])], B: [...(cur.B || [])] };
        outer: for (const L of ["A", "B"]) {
          for (let si = next[L].length - 1; si >= 0; si--) {
            const layers = [...(next[L][si].layers || [])];
            for (let li = layers.length - 1; li >= 0; li--) {
              if (layers[li] === code) {
                layers.splice(li, 1);
                if (layers.length === 0) next[L].splice(si, 1);
                else next[L][si] = { ...next[L][si], layers };
                break outer;
              }
            }
          }
        }
        return { ...c, alloc, filler, arrangement: next };
      }
      return { ...c, alloc, filler, arrangement: null };
    }));
    const desc = splitProducts.find(p => p.code === code)?.description || "";
    const ppp = pppOf(code) || 0;
    setPool(prev => {
      const n = { ...prev };
      const e = n[code] ? { ...n[code] } : { po: 0, filler: 0, description: desc, ppp };
      e[kind] = (e[kind] || 0) + 1;
      e.description = e.description || desc; e.ppp = e.ppp || ppp;
      n[code] = e;
      return n;
    });
    toast.message(`Moved 1 ${code} (${kind === "filler" ? "Filler" : "PO"}) to Unassigned`, { duration: 1600 });
  };

  // Drag a pool chip onto the Top View (or tap +): drop one deferred pallet back into the ACTIVE
  // container. The locked recompute validates capacity; if it won't fit it reverts and the pallet
  // returns to the pool (see recomputeLocked reject branch).
  const moveFromPool = (code, kind) => {
    const e = pool[code];
    if (!e || (e[kind] || 0) <= 0) { toast.info("No such pallet in the pool"); return; }
    if (!editSplit) enterEditSplit();
    const ppp = e.ppp || pppOf(code) || 0;
    const desc = e.description || "";
    setPool(prev => {
      const n = { ...prev };
      const x = { ...n[code] };
      x[kind] = (x[kind] || 0) - 1;
      if ((x.po || 0) <= 0 && (x.filler || 0) <= 0) delete n[code]; else n[code] = x;
      return n;
    });
    setSplitProducts(prev => prev.some(p => p.code === code) ? prev : [...prev, { code, description: desc, ppp, required: 0 }]);
    markDirty(activeContainer);
    pendingAddRef.current[activeContainer] = code;   // Placement Auditor: candidate returned from the Unassigned Pool
    setCEdit(prev => prev.map((c, i) => {
      if (i !== activeContainer) return c;
      const alloc = { ...c.alloc, [code]: (c.alloc[code] || 0) + 1 };
      const filler = kind === "filler" ? { ...(c.filler || {}), [code]: (c.filler?.[code] || 0) + 1 } : (c.filler || {});
      if (c.arrangement) {
        // Lock the Sequence: append the returning pallet to the lighter lane so the layout stays operator-defined.
        const cur = c.arrangement;
        const lenA = (cur.A || []).length, lenB = (cur.B || []).length;
        const targetLane = lenA <= lenB ? "A" : "B";
        const nextArr = { A: [...(cur.A || [])], B: [...(cur.B || [])] };
        nextArr[targetLane].push({ layers: [code] });
        return { ...c, alloc, filler, arrangement: nextArr };
      }
      return { ...c, alloc, filler, arrangement: null };
    }));
    toast.message(`Returning 1 ${code} to Container ${activeContainer + 1}…`, { duration: 1400 });
  };

  // ✕ on a pool chip: permanently drop that product+kind from the Unassigned tray (not shipped).
  const discardFromPool = (code, kind) => {
    setPool(prev => {
      const e = prev[code];
      if (!e) return prev;
      const next = { ...prev };
      const x = { ...e, [kind]: 0 };
      if ((x.po || 0) <= 0 && (x.filler || 0) <= 0) delete next[code]; else next[code] = x;
      lastGoodPoolRef.current = next;
      return next;
    });
    toast.success(`Removed ${code} (${kind === "filler" ? "Filler" : "PO"}) from Unassigned`);
  };

  // Clear All: empty the Unassigned tray completely.
  const clearPool = () => {
    if (!Object.keys(pool).length) return;
    setPool({}); lastGoodPoolRef.current = {};
    toast.success("Cleared all unassigned pallets");
  };

  const newPlan = () => { setMulti(null); setPlan(null); setEditSplit(false); setArrIdx(null); setCEdit([]); setPool({}); lastGoodPoolRef.current = {}; setTopupExtra([]); setBasePlan(null); dirtyRef.current = new Set(); };

  const configFields = (
    <div className="space-y-2 text-sm">
      <div className="space-y-1" data-testid="packing-strategy-selector">
        <div className="text-[11px] uppercase tracking-wide text-slate-400">Packing strategy</div>
        <div className="inline-flex w-full rounded-md border border-slate-700 bg-slate-800 p-0.5">
          <button type="button" onClick={() => setPackingMode("density")}
            className={`flex-1 h-8 rounded text-xs font-medium transition-colors ${packingMode === "density" ? "bg-sky-600 text-white" : "text-slate-300 hover:text-white"}`}
            data-testid="packing-mode-density-btn">Density Optimized</button>
          <button type="button" onClick={() => setPackingMode("stability")}
            className={`flex-1 h-8 rounded text-xs font-medium transition-colors ${packingMode === "stability" ? "bg-sky-600 text-white" : "text-slate-300 hover:text-white"}`}
            data-testid="packing-mode-stability-btn">Stability Optimized</button>
        </div>
        <div className="text-[11px] text-slate-500">{packingMode === "stability" ? "Wider footprints & stepped heights for a safer, more stable load." : "Maximises fit — fewest containers."}</div>
      </div>
      {packingMode === "density" && (
        <label className="flex items-start gap-2 text-slate-300 rounded-md border border-amber-800/60 bg-amber-950/30 px-2 py-1.5" data-testid="ledge-guard-toggle">
          <input type="checkbox" checked={ledgeGuard} onChange={e => setLedgeGuard(e.target.checked)} className="h-4 w-4 rounded mt-0.5" data-testid="ledge-guard-checkbox" />
          <span>Ledge Guard (85% Rule) <span className="text-[9px] font-bold uppercase tracking-wider px-1 py-0.5 rounded bg-amber-500/20 text-amber-300 align-middle">Safety</span>
            <span className="block text-[11px] text-slate-500">Blocks a top pallet from perching on a base &gt;15% longer. Light-touch safety filter that keeps Density&#39;s fewest-containers goal — separate from Stability mode&#39;s full block-packing.</span></span>
        </label>
      )}
      <label className="flex items-start gap-2 text-slate-300 rounded-md border border-cyan-800/60 bg-cyan-950/30 px-2 py-1.5" data-testid="strict-ctu-toggle">
        <input type="checkbox" checked={strictCtu} onChange={e => setStrictCtu(e.target.checked)} className="h-4 w-4 rounded mt-0.5" data-testid="strict-ctu-checkbox" />
        <span>Strict IMO CTU Balance <span className="text-[9px] font-bold uppercase tracking-wider px-1 py-0.5 rounded bg-cyan-500/20 text-cyan-300 align-middle">Beta</span>
          <span className="block text-[11px] text-slate-500">Wall-flushed lateral CoG (CoG-Y), 5% off-centre alert &amp; centre-airbag count.</span></span>
      </label>
      <label className="flex items-start gap-2 text-slate-300 rounded-md border border-orange-800/60 bg-orange-950/30 px-2 py-1.5" data-testid="us-highway-toggle">
        <input type="checkbox" checked={usHighway} onChange={e => setUsHighway(e.target.checked)} className="h-4 w-4 rounded mt-0.5" data-testid="us-highway-checkbox" />
        <span>Enable US Highway Mode <span className="text-[9px] font-bold uppercase tracking-wider px-1 py-0.5 rounded bg-orange-500/20 text-orange-300 align-middle">Beta</span>
          <span className="block text-[11px] text-slate-500">Law-of-Levers axle physics (Steer / Drive / Trailer / GVW) using 5-axle chassis geometry. Replaces the 60/40 rule with the 12k / 34k / 34k / 80k lb FHWA limits.</span></span>
      </label>
      <label className="flex items-start gap-2 text-slate-300 rounded-md border border-emerald-800/60 bg-emerald-950/30 px-2 py-1.5" data-testid="us-decision-suite-toggle">
        <input type="checkbox" checked={usDecisionSuite} onChange={e => setUsDecisionSuite(e.target.checked)} className="h-4 w-4 rounded mt-0.5" data-testid="us-decision-suite-checkbox" />
        <span>US Highway Decision Suite <span className="text-[9px] font-bold uppercase tracking-wider px-1 py-0.5 rounded bg-emerald-500/20 text-emerald-300 align-middle">Demo</span>
          <span className="block text-[11px] text-slate-500">Nose-packs the load, computes longitudinal CoG-X (target 45%) and illustrative axle ranges across TWO chassis scenarios (426&quot;/42&quot; and 480&quot;/30&quot;). Draws a coloured CoG-X line in the elevations.</span></span>
      </label>
      <label className="flex items-center gap-2 text-slate-300"><input type="checkbox" checked={allowRotation} onChange={e => setAllowRotation(e.target.checked)} className="h-4 w-4 rounded" data-testid="allow-rotation-checkbox" />Allow 90° pallet rotation</label>
      <label className="flex items-start gap-2 text-slate-300"><input type="checkbox" checked={ignoreLayerCap} onChange={e => setIgnoreLayerCap(e.target.checked)} className="h-4 w-4 rounded mt-0.5" data-testid="ignore-layer-cap-checkbox" /><span>Ignore max stack layers<span className="block text-[11px] text-slate-500">Stack to container height</span></span></label>
      <label className="flex items-start gap-2 text-slate-300"><input type="checkbox" checked={combineBases} onChange={e => setCombineBases(e.target.checked)} className="h-4 w-4 rounded mt-0.5" data-testid="combine-bases-checkbox" /><span>Combine same-footprint bases</span></label>
      <div className="grid grid-cols-2 gap-2 pt-1">
        <label className="text-[11px] text-slate-400">Top clearance (in)<input type="number" min="0" step="1" value={topClearance} onChange={e => setTopClearance(Math.max(0, parseFloat(e.target.value || "0")))} className="w-full h-8 text-sm border border-slate-700 rounded px-2 bg-slate-800 text-slate-100 mt-0.5" data-testid="top-clearance-input" /></label>
        <label className="text-[11px] text-slate-400">Max stack weight (kg)<input type="number" min="0" step="50" value={maxStackWeight} onChange={e => setMaxStackWeight(Math.max(0, parseFloat(e.target.value || "0")))} className="w-full h-8 text-sm border border-slate-700 rounded px-2 bg-slate-800 text-slate-100 mt-0.5" data-testid="max-stack-weight-input" /></label>
        <label className="text-[11px] text-slate-400">Centre gap (in)<input type="number" min="0" max="24" step="0.5" value={centerGap} onChange={e => setCenterGap(Math.max(0, parseFloat(e.target.value || "0")))} className="w-full h-8 text-sm border border-slate-700 rounded px-2 bg-slate-800 text-slate-100 mt-0.5" data-testid="center-gap-input" /></label>
        <label className="text-[11px] text-slate-400">Max overhang (in)<input type="number" min="0" max="24" step="0.5" value={maxOverhang} onChange={e => setMaxOverhang(Math.max(0, parseFloat(e.target.value || "0")))} className="w-full h-8 text-sm border border-slate-700 rounded px-2 bg-slate-800 text-slate-100 mt-0.5" data-testid="max-overhang-input" /></label>
        <label className="text-[11px] text-slate-400">Rotation clearance (in)<input type="number" min="0" max="12" step="0.5" value={crossClearance} onChange={e => setCrossClearance(Math.max(0, parseFloat(e.target.value || "0")))} className="w-full h-8 text-sm border border-slate-700 rounded px-2 bg-slate-800 text-slate-100 mt-0.5" data-testid="cross-clearance-input" /></label>
        <label className="text-[11px] text-slate-400">Pallet gap (in)<input type="number" min="0" max="12" step="0.5" value={palletGap} onChange={e => setPalletGap(Math.max(0, parseFloat(e.target.value || "0")))} className="w-full h-8 text-sm border border-slate-700 rounded px-2 bg-slate-800 text-slate-100 mt-0.5" data-testid="pallet-gap-input" /></label>
      </div>
    </div>
  );

  return (
    <>
    <Toaster position="top-center" richColors style={{ zIndex: 2147483647 }} />
    <div className="fixed inset-0 z-40 flex flex-col bg-slate-950 text-slate-100" data-testid="container-planner-workspace">

      {/* ERP header */}
      <div className="h-[48px] shrink-0 bg-slate-900 border-b border-slate-800 px-4 flex items-center justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <button onClick={() => navigate("/safety")} className="text-slate-400 hover:text-slate-100 flex items-center gap-1 text-sm" data-testid="workspace-back-btn">
            <ChevronLeft className="w-4 h-4" /> Safety
          </button>
          <span className="text-slate-700">·</span>
          <Container className="w-4 h-4 text-blue-400" />
          <span className="text-sm font-bold tracking-tight truncate">Container Stuffing Planner</span>
          {poNumber && <span className="text-xs font-mono text-slate-400 truncate">· {poNumber}</span>}
        </div>
        <div className="flex items-center gap-2">
          {user?.role === "ADMIN" && (
            <Button variant="outline" size="sm" className="h-7 bg-transparent border-amber-500/40 text-amber-300 hover:bg-amber-500/10" onClick={syncFromProd} disabled={syncing} data-testid="sync-prod-btn">
              <RefreshCw className={`w-3.5 h-3.5 mr-1 ${syncing ? "animate-spin" : ""}`} />{syncing ? "Syncing…" : "Sync"}
            </Button>
          )}
          <Button variant="outline" size="sm" className="h-7 bg-transparent border-slate-700 text-slate-200 hover:bg-slate-800" onClick={() => { setBoardSeed(null); setShowBoard(true); }} data-testid="open-load-board-btn"><Boxes className="w-3.5 h-3.5 mr-1" />Load Board</Button>
          {!isCPro && (
            <>
              <Button variant="outline" size="sm" className="h-7 bg-transparent border-slate-700 text-slate-200 hover:bg-slate-800" onClick={() => setShowPresets(true)} data-testid="manage-presets-btn"><Settings2 className="w-3.5 h-3.5 mr-1" />Presets</Button>
              <Button variant="outline" size="sm" className="h-7 bg-transparent border-orange-500/40 text-orange-300 hover:bg-orange-500/10" onClick={() => setShowTruckSetup(true)} data-testid="truck-setup-btn" title="Edit tractor/chassis tares, axle span and fifth-wheel lead — affects the US Scale Ticket physics"><Truck className="w-3.5 h-3.5 mr-1" />Truck Setup</Button>
            </>
          )}
        </div>
      </div>

      {!multi ? (
        /* ===================== SETUP ===================== */
        <div className="flex-1 min-h-0 overflow-auto flex items-start justify-center p-6">
          <div className="w-full max-w-2xl bg-slate-900 border border-slate-800 rounded-xl p-5 space-y-4" data-testid="planner-setup">
            <div>
              <h2 className="text-base font-bold">Plan a new load</h2>
              <p className="text-xs text-slate-400">Pick a PO (or upload a draft), choose a container, then open the visual workspace.</p>
            </div>
            <div className="flex gap-1 bg-slate-800 p-1 rounded-lg w-fit">
              {!isCPro && (
                <button onClick={() => { setSource("po"); newPlan(); }} className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${source === "po" ? "bg-slate-950 text-blue-400 shadow" : "text-slate-400"}`} data-testid="source-po-tab">From a PO</button>
              )}
              <button onClick={() => { setSource("draft"); newPlan(); }} className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${source === "draft" ? "bg-slate-950 text-blue-400 shadow" : "text-slate-400"}`} data-testid="source-draft-tab">Upload draft PO</button>
            </div>
            {source === "po" ? (
              <div>
                <Input className="h-8 text-sm mb-2 bg-slate-800 border-slate-700 text-slate-100 placeholder:text-slate-500" placeholder="Search PO number or customer…" value={poSearch} onChange={e => setPoSearch(e.target.value)} data-testid="po-search-input" />
                <div className="h-48 overflow-auto border border-slate-800 rounded">
                  {filteredPos.map(p => (
                    <button key={p.id} onClick={() => { setPoId(p.id); newPlan(); }} className={`w-full text-left px-3 py-2 border-b border-slate-800 hover:bg-slate-800 text-sm ${poId === p.id ? "bg-blue-500/10 border-l-2 border-l-blue-500" : ""}`} data-testid={`po-option-${p.po_number}`}>
                      <div className="flex items-center justify-between"><span className="font-medium text-slate-100">{p.po_number}</span><span className="text-[11px] text-slate-500">{p.product_count} lines</span></div>
                      <div className="text-xs text-slate-500 truncate">{p.customer_name || p.vendor_name} · {p.ship_to_location || "—"}</div>
                    </button>
                  ))}
                  {filteredPos.length === 0 && <div className="p-4 text-center text-sm text-slate-500">No POs</div>}
                </div>
                {restorePrompt && (
                  <div className="mt-2 flex flex-wrap items-center gap-2 rounded-lg border border-indigo-500/40 bg-indigo-500/10 px-3 py-2" data-testid="restore-split-prompt">
                    <History className="w-4 h-4 text-indigo-400 shrink-0" />
                    <span className="text-[13px] text-indigo-200 flex-1 min-w-[140px]">Saved split <b>"{restorePrompt.name}"</b><span className="text-indigo-400"> · {(restorePrompt.containers || []).length} containers</span></span>
                    <Button size="sm" className="h-7 bg-indigo-600 hover:bg-indigo-500" onClick={() => applyRestore(restorePrompt)} data-testid="restore-split-btn"><FolderOpen className="w-3.5 h-3.5 mr-1" />Restore</Button>
                    <button type="button" className="text-indigo-400 hover:text-indigo-200 text-sm px-1" onClick={() => setRestorePrompt(null)} data-testid="restore-split-dismiss">✕</button>
                  </div>
                )}
              </div>
            ) : (
              <div>
                <p className="text-sm text-slate-400 mb-2">Upload a draft/hypothetical PO Excel — nothing is saved, it's only run through the planner.</p>
                <input ref={draftRef} type="file" accept=".xls,.xlsx,.xlsm" onChange={uploadDraft} className="text-sm text-slate-300" data-testid="draft-upload-input" />
                {draft && <div className="mt-3 text-sm"><Badge className="bg-blue-500/15 text-blue-300 border-blue-500/30">{draft.lines?.length || 0} lines parsed</Badge>{draft.ship_to_location && <span className="ml-2 text-slate-400">Ship to: {draft.ship_to_location}</span>}</div>}
              </div>
            )}
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500 font-medium font-mono">Container type</label>
              <select className="w-full h-9 text-sm border border-slate-700 rounded px-2 mt-0.5 bg-slate-800 text-slate-100" value={typeId} onChange={e => { setTypeId(e.target.value); newPlan(); }} data-testid="container-type-select">
                {types.map(t => <option key={t.id} value={t.id}>{t.name} — {t.max_payload_kg.toFixed(0)}kg{t.is_default ? " (default)" : ""}</option>)}
              </select>
            </div>
            <div className="border border-slate-800 rounded-lg">
              <button onClick={() => setShowSetupOpts(s => !s)} className="w-full flex items-center justify-between px-3 py-2 text-sm text-slate-300" data-testid="setup-options-toggle">
                <span className="flex items-center gap-1.5"><SlidersHorizontal className="w-3.5 h-3.5" />Planning options</span>
                <ChevronLeft className={`w-4 h-4 transition-transform ${showSetupOpts ? "-rotate-90" : "rotate-0"}`} />
              </button>
              {showSetupOpts && <div className="px-3 pb-3">{configFields}</div>}
            </div>
            <Button className="w-full bg-blue-600 hover:bg-blue-500 h-10" onClick={() => runPlan()} disabled={loading} data-testid="run-plan-btn">
              <Play className="w-4 h-4 mr-1" />{loading ? "Planning…" : "Open visual workspace"}
            </Button>
          </div>
        </div>
      ) : (
        /* ===================== WORKSPACE ===================== */
        <>
          <div className="h-[48px] shrink-0 bg-slate-900/90 border-b border-slate-800 px-4 flex items-center justify-between gap-3" data-testid="top-toolbar">
            <div className="flex items-center gap-2 min-w-0">
              <Button size="sm" variant="outline" className="h-7 bg-transparent border-slate-700 text-slate-200 hover:bg-slate-800" onClick={newPlan} data-testid="new-plan-btn"><ChevronLeft className="w-3.5 h-3.5 mr-1" />New / change PO</Button>
              <span className="text-xs font-mono text-slate-400">{multi.container_count} × {multi.container_type?.name}</span>
              {utilStats && (
                <>
                  <span className="text-slate-700">·</span>
                  <span className="text-[11px] text-slate-400 whitespace-nowrap" data-testid="avg-utilisation-summary" title="Core = full bins only (excludes the swing) · Total = every container incl. swing">
                    Avg. Utilisation:{" "}
                    <span className="font-semibold text-emerald-300 tabular-nums" data-testid="avg-util-core">{utilStats.core}%</span>
                    <span className="text-slate-500"> (Full Bins)</span>
                    <span className="text-slate-600"> | </span>
                    <span className="font-semibold text-blue-300 tabular-nums" data-testid="avg-util-global">{utilStats.global}%</span>
                    <span className="text-slate-500"> (Total)</span>
                  </span>
                </>
              )}
            </div>
            <div className="flex items-center gap-1.5">
              <Button size="sm" variant="outline" className="h-7 bg-transparent border-slate-700 text-slate-200 hover:bg-slate-800" onClick={addContainer} data-testid="add-container-btn"><Plus className="w-3.5 h-3.5 mr-1" />Container</Button>
              {multi.containers.some(c => c.status === "over_loaded") && (
                <Button size="sm" className="h-7 bg-rose-600 hover:bg-rose-500 text-white" onClick={rebalanceSplit} data-testid="rebalance-split-btn"><Shuffle className="w-3.5 h-3.5 mr-1" />Rebalance</Button>
              )}
              <SplitSaveLoad canSave={source === "po" && !!poId} savedSplits={savedSplits} onSaveSplit={saveSplit} onLoadSplit={applySavedSplit} onDeleteSplit={deleteSavedSplit} />
              <Button size="sm" variant={sandboxMode ? "default" : "outline"}
                className={`h-7 ${sandboxMode ? "bg-emerald-600 hover:bg-emerald-500 text-white border-emerald-500" : "bg-transparent border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"}`}
                onClick={() => setSandboxMode(v => !v)}
                title="Compliance Sandbox — drag pallets in the elevations, NO REVERT, physics recompute live"
                data-testid="sandbox-mode-toggle">
                <GripVertical className="w-3.5 h-3.5 mr-1" />Sandbox{sandboxMode && <span className="ml-1 text-[9px] font-bold px-1 py-0.5 rounded bg-white/20">ON</span>}
              </Button>
              <Button size="sm" className="h-7 bg-violet-600 hover:bg-violet-500 text-white" onClick={globalReoptimize} disabled={loading} data-testid="global-reoptimize-btn"><Scale3d className="w-3.5 h-3.5 mr-1" />Re-Optimize</Button>
              <Button size="sm" variant="outline" className="h-7 relative bg-transparent border-orange-700/60 text-orange-300 hover:bg-orange-950/40" onClick={() => setShowFiller(true)} data-testid="filler-manifest-btn"><PackagePlus className="w-3.5 h-3.5 mr-1" />Filler{fillerSummary.totalPallets > 0 && <span className="ml-1 px-1.5 rounded-full bg-orange-500 text-white text-[10px] font-bold tabular-nums" data-testid="filler-manifest-count">{fillerSummary.totalPallets}</span>}</Button>
              <Button size="sm" variant="outline" className="h-7 bg-transparent border-slate-700 text-slate-200 hover:bg-slate-800" onClick={resetSplit} data-testid="reset-erp-btn"><RefreshCw className="w-3.5 h-3.5 mr-1" />Reset</Button>
              <Button size="sm" variant="outline" className="h-7 bg-transparent border-slate-700 text-slate-200 hover:bg-slate-800" onClick={() => setShowConfig(true)} data-testid="config-drawer-btn"><SlidersHorizontal className="w-3.5 h-3.5 mr-1" />Options</Button>
              <Button size="sm" variant="outline" className="h-7 bg-transparent border-slate-700 text-slate-200 hover:bg-slate-800" onClick={openBoardFromMulti} data-testid="workspace-open-board-btn"><Boxes className="w-3.5 h-3.5 mr-1" />Load Board</Button>
              <Button size="sm" variant="outline" className="h-7 bg-transparent border-slate-700 text-slate-200 hover:bg-slate-800" onClick={() => exportFile("pdf")} data-testid="export-pdf-btn"><FileText className="w-3.5 h-3.5" /></Button>
              <Button size="sm" variant="outline" className="h-7 bg-transparent border-slate-700 text-slate-200 hover:bg-slate-800" onClick={() => exportFile("xlsx")} data-testid="export-xlsx-btn"><FileSpreadsheet className="w-3.5 h-3.5" /></Button>
            </div>
          </div>

          <div className="h-[40px] shrink-0 bg-slate-900 border-b border-slate-800 px-4 flex items-center justify-between gap-3">
            <div className="flex items-center gap-1.5 overflow-x-auto" data-testid="container-tabs">
              {multi.containers.map((c, i) => {
                const active = i === activeContainer;
                const tone = c.status === "over_loaded" ? "text-rose-300 border-rose-500/40" : c.status === "under_loaded" ? "text-amber-300 border-amber-500/40" : "text-emerald-300 border-emerald-500/40";
                const q = c.quality?.score;
                const busy = replanning.includes(i);
                const frozen = replanning.length > 0 && !busy;
                return (
                  <button key={i} onClick={() => { setActiveContainer(i); setPlan(c); }}
                    title={frozen ? "Locked — not affected by this edit" : busy ? "Recomputing this container…" : undefined}
                    className={`px-3 h-7 rounded-md text-xs font-medium border transition-colors inline-flex items-center gap-1.5 shrink-0 ${active ? "bg-blue-600 text-white border-blue-600" : `bg-slate-950 hover:bg-slate-800 ${tone}`} ${frozen ? "opacity-60" : ""}`}
                    data-testid={`container-tab-${i}`}>
                    {busy
                      ? <Loader2 className="w-3 h-3 animate-spin" data-testid={`container-tab-recomputing-${i}`} />
                      : frozen && <Lock className="w-3 h-3 opacity-80" data-testid={`container-tab-lock-${i}`} />}
                    Container {i + 1}
                    {q != null && <span className={`text-[9px] font-bold px-1 py-0.5 rounded-full ${active ? "bg-white/20" : "bg-slate-800"}`}>{q}</span>}
                    <span className="text-[9px] opacity-70">{c.total_pallets}plt</span>
                    {c.is_swing && <span className="text-[8px] font-bold px-1 rounded bg-amber-400 text-amber-950">SWING</span>}
                  </button>
                );
              })}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <select className="h-7 text-[11px] border border-slate-700 rounded px-1 bg-slate-800 text-slate-100" value={activeTypeId} onChange={e => setCType(activeContainer, e.target.value)} data-testid="active-container-type">
                {types.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
              {!sandboxMode && (
                <Button size="sm" variant="outline" className="h-7 bg-transparent border-violet-500/40 text-violet-300 hover:bg-violet-500/10" onClick={() => openArrange(activeContainer)} data-testid="arrange-active-btn"><Settings2 className="w-3.5 h-3.5 mr-1" />Arrange</Button>
              )}
              {multi.containers.length > 1 && <Button size="sm" variant="outline" className="h-7 bg-transparent border-slate-700 text-rose-300 hover:bg-rose-500/10" onClick={() => removeContainer(activeContainer)} data-testid="remove-active-container-btn"><Trash2 className="w-3.5 h-3.5" /></Button>}
            </div>
          </div>

          {sandboxMode && (
            <div className="shrink-0 bg-emerald-500/10 border-b border-emerald-500/30 px-4 py-1.5 flex flex-wrap items-center gap-2 text-[11px]" data-testid="sandbox-mode-banner">
              <GripVertical className="w-3.5 h-3.5 text-emerald-400" />
              <span className="text-emerald-200 font-semibold">Compliance Sandbox ON</span>
              <span className="text-emerald-300/80">— drag pallets in the side elevations · floor pallet moves whole stack · upper layer moves single box · cross-lane L↔R accepted · NO REVERT on physical limits, telemetry recomputes live</span>
              <button type="button" onClick={() => setSandboxMode(false)} className="ml-auto text-[10px] font-mono font-bold px-2 py-0.5 rounded bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="sandbox-mode-banner-off-btn">EXIT SANDBOX</button>
            </div>
          )}

          {multi.swing && !sandboxMode && (
            <div className="shrink-0 bg-amber-500/10 border-b border-amber-500/30 px-4 py-1.5 flex flex-wrap items-center gap-2 text-[11px]" data-testid="swing-container-callout">
              <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />
              <span className="text-amber-200">Last container is the swing load — {multi.swing.fill_pct}% full ({multi.swing.pallets} plt)</span>
              <Button size="sm" className="h-6 bg-amber-600 hover:bg-amber-500 text-white ml-auto" disabled={loading || !(multi.swing.topup?.length)} onClick={topUpSwing} data-testid="swing-topup-btn"><PackagePlus className="w-3 h-3 mr-1" />Top-up</Button>
              <Button size="sm" variant="outline" className="h-6 bg-transparent border-rose-500/40 text-rose-300 hover:bg-rose-500/10" disabled={loading} onClick={dropSwing} data-testid="swing-drop-btn"><TrendingDown className="w-3 h-3 mr-1" />Drop &amp; reship</Button>
            </div>
          )}

          <div className="flex-1 min-h-0 flex flex-row relative">
            <div className="flex-1 min-w-0 flex flex-col p-3 gap-3 overflow-hidden">
              <TopViewPanel plan={activePlan} cmap={cmap} onDropProduct={dropAdd} onDropPool={moveFromPool} onRotateStack={handleRotateStack} />
              <ElevationPanel plan={activePlan} cmap={cmap} lane="B" onRemove={removeOne} onAudit={isCPro ? undefined : auditBox} testid="elevation-view-lane-b" sandbox={sandboxMode} onSandboxMove={handleSandboxMove} cogx={activePlan?.us_decision_suite?.cog_x} />
              <ElevationPanel plan={activePlan} cmap={cmap} lane="A" onRemove={removeOne} onAudit={isCPro ? undefined : auditBox} testid="elevation-view-lane-a" sandbox={sandboxMode} onSandboxMove={handleSandboxMove} cogx={activePlan?.us_decision_suite?.cog_x} />
            </div>
            <TelemetryRail plan={activePlan} strictCtu={strictCtu} usHighway={usHighway} usDecisionSuite={usDecisionSuite} lastAudit={lastAudit} onOpenAuditor={() => lastAudit && setAudit(lastAudit)} hideAuditor={isCPro} truckSpecs={truckSpecs} />
            {loading && <div className="absolute inset-0 bg-slate-950/40 flex items-center justify-center pointer-events-none"><span className="text-xs font-mono text-slate-300 bg-slate-900/90 border border-slate-700 rounded-full px-3 py-1">Recomputing…</span></div>}
          </div>

          <UnassignedTray pool={pool} onReturn={moveFromPool} onDiscard={discardFromPool} onClearAll={clearPool} activeLabel={`Container ${activeContainer + 1}`} />
          <CatalogTray catalog={catalog} onAdd={dropAdd} activeLabel={`Container ${activeContainer + 1}`} />
        </>
      )}

      <PresetsDialog open={showPresets} onOpenChange={setShowPresets} types={types} reload={loadTypes} />
      <TruckSetupDialog open={showTruckSetup} onOpenChange={setShowTruckSetup} truckSpecs={truckSpecs} onSaved={(next) => { setTruckSpecs(next); }} />
      <ManualCompareDialog open={showCompare} onOpenChange={setShowCompare} typeId={typeId} poId={source === "po" ? poId : null} poNumber={poNumber || null} />
      <LoadBoard open={showBoard} onOpenChange={(v) => { setShowBoard(v); if (!v) setBoardSeed(null); }} typeId={typeId}
        poId={source === "po" ? poId : null} poNumber={poNumber || null}
        draftKey={source === "draft" && draft ? `draft:${draft.po_number || "adhoc"}` : null}
        seedContainers={boardSeed}
        allowRotation={allowRotation} topClearance={topClearance} maxStackWeight={maxStackWeight} centerGap={centerGap} crossClearance={crossClearance} palletGap={palletGap} />

      <Dialog open={showConfig} onOpenChange={setShowConfig}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-md">
          <DialogHeader><DialogTitle className="text-slate-100">Planning options</DialogTitle><DialogDescription className="text-slate-400">Changes re-run the active split instantly.</DialogDescription></DialogHeader>
          {configFields}
        </DialogContent>
      </Dialog>

      <Dialog open={showFiller} onOpenChange={setShowFiller}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-2xl" data-testid="filler-manifest-dialog">
          <DialogHeader>
            <DialogTitle className="text-slate-100 flex items-center gap-2"><PackagePlus className="w-4 h-4 text-orange-400" />Filler manifest — non-PO items</DialogTitle>
            <DialogDescription className="text-slate-400">Every pallet added on top of the PO (Top-Up or catalog), across the whole shipment. Updates live as you drag items in or remove them.</DialogDescription>
          </DialogHeader>
          {fillerSummary.rows.length === 0 ? (
            <div className="py-10 text-center text-sm text-slate-500" data-testid="filler-manifest-empty">No filler pallets added yet. Use Top-Up or drag a product from the catalog tray.</div>
          ) : (
            <div className="rounded-lg border border-slate-800 overflow-hidden">
              <table className="w-full text-sm" data-testid="filler-manifest-table">
                <thead>
                  <tr className="bg-orange-500/15 text-orange-200 text-[11px] uppercase tracking-wider">
                    <th className="text-left font-mono font-semibold px-3 py-2">Product</th>
                    <th className="text-left font-mono font-semibold px-3 py-2">Description</th>
                    <th className="text-right font-mono font-semibold px-3 py-2">Filler pallets</th>
                    <th className="text-right font-mono font-semibold px-3 py-2">Filler weight (kg)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {fillerSummary.rows.map(r => (
                    <tr key={r.code} className="hover:bg-slate-800/40" data-testid={`filler-manifest-row-${r.code}`}>
                      <td className="px-3 py-2 font-mono text-slate-100">{r.code}</td>
                      <td className="px-3 py-2 text-slate-300 truncate max-w-[260px]">{r.description || "—"}</td>
                      <td className="px-3 py-2 text-right font-mono text-orange-300 tabular-nums" data-testid={`filler-manifest-pallets-${r.code}`}>{r.pallets}</td>
                      <td className="px-3 py-2 text-right font-mono text-slate-200 tabular-nums">{Math.round(r.weight).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-orange-500/20 text-orange-100 font-bold border-t-2 border-orange-500/40">
                    <td className="px-3 py-2.5" colSpan={2}>GRAND TOTAL — added to shipment</td>
                    <td className="px-3 py-2.5 text-right font-mono tabular-nums" data-testid="filler-manifest-total-pallets">{fillerSummary.totalPallets} plt</td>
                    <td className="px-3 py-2.5 text-right font-mono tabular-nums" data-testid="filler-manifest-total-weight"><WeightLbsKg kg={fillerSummary.totalWeight} /></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
          <DialogFooter className="sm:justify-between gap-2">
            <div className="flex items-center gap-2">
              {fillerSummary.rows.length > 0 && (
                <Button size="sm" variant="outline" className="h-8 bg-transparent border-rose-700/60 text-rose-300 hover:bg-rose-950/40" onClick={clearAllFiller} data-testid="filler-clear-all-btn"><Trash2 className="w-3.5 h-3.5 mr-1" />Clear all filler</Button>
              )}
              <span className="text-[11px] text-slate-500 hidden sm:inline">Filler items also appear as a dedicated section in the PDF &amp; Excel exports.</span>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="h-8 bg-transparent border-slate-700 text-slate-200 hover:bg-slate-800" onClick={() => exportFile("xlsx")} data-testid="filler-export-xlsx-btn"><FileSpreadsheet className="w-3.5 h-3.5 mr-1" />Excel</Button>
              <Button size="sm" variant="outline" className="h-8 bg-transparent border-slate-700 text-slate-200 hover:bg-slate-800" onClick={() => exportFile("pdf")} data-testid="filler-export-pdf-btn"><FileText className="w-3.5 h-3.5 mr-1" />PDF</Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={editSplit && arrIdx != null} onOpenChange={(v) => { if (!v) setArrIdx(null); }}>
        <DialogContent className="bg-white text-slate-900 max-w-4xl max-h-[85vh] overflow-auto" data-testid="container-arrange-panel">
          <DialogHeader><DialogTitle>Arrange Container {(arrIdx ?? 0) + 1} — pallet sequence &amp; stacking</DialogTitle></DialogHeader>
          {arrIdx != null && (
            <ManualArrange arrangement={cEdit[arrIdx]?.arrangement || { A: [], B: [] }}
              addStacksToLane={arrMut.addStacksToLane} addLayerToStack={arrMut.addLayerToStack} onAuditStack={auditStack}
              removeLayer={arrMut.removeLayer} removeStack={arrMut.removeStack} clearArrangement={arrMut.clearArrangement}
              plan={multi.containers[arrIdx]} loading={loading} saved={[]} onSave={null} onLoad={null} onDelete={null}
              improve={null} improving={false} onAutoImprove={null} onApplyImprove={null}
              onDismissImprove={null} onCompare={null} hideActions cmap={cmap} />
          )}
        </DialogContent>
      </Dialog>

      <PlacementAuditor data={audit} onClose={() => setAudit(null)} onFixIt={applyFixIt} colorOf={colorForCode} />

      <Dialog open={!!reoptConfirm} onOpenChange={(v) => { if (!v) setReoptConfirm(null); }}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-md" data-testid="reoptimize-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="text-slate-100 flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-400" />Extra container required</DialogTitle>
            <DialogDescription className="text-slate-400">This optimization requires an extra container. Do you want to proceed?</DialogDescription>
          </DialogHeader>
          {reoptConfirm && (
            <p className="text-sm text-slate-300">Best fit needs <span className="font-semibold text-amber-300">{reoptConfirm.newCount}</span> container{reoptConfirm.newCount === 1 ? "" : "s"} instead of <span className="font-semibold text-slate-100">{reoptConfirm.target}</span>.</p>
          )}
          <DialogFooter className="gap-2 sm:justify-end">
            <Button variant="outline" className="bg-transparent border-slate-700 text-slate-200 hover:bg-slate-800" onClick={() => { setReoptConfirm(null); toast.info("Re-optimization cancelled — layout unchanged"); }} data-testid="reoptimize-confirm-no">No, keep current layout</Button>
            <Button className="bg-violet-600 hover:bg-violet-500 text-white" onClick={() => { const c = reoptConfirm; setReoptConfirm(null); applyReoptResult(c.result, c.okMsg); }} data-testid="reoptimize-confirm-yes">Yes, add container</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </>
  );
}

function KPI({ label, value, sub, tone = "slate" }) {
  const tones = { slate: "text-slate-800", green: "text-emerald-600", amber: "text-amber-600", rose: "text-rose-600", blue: "text-blue-700" };
  return (
    <div className="border border-slate-200 rounded-lg p-3">
      <div className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">{label}</div>
      <div className={`text-xl font-semibold mt-0.5 ${tones[tone]}`}>{value}</div>
      {sub && <div className="text-[11px] text-slate-400">{sub}</div>}
    </div>
  );
}


// Toolbar Save/Load-split control — save the current per-container plan against a PO,
// or load / delete a previously saved split. Replaces the old stepper-table save UI.
function SplitSaveLoad({ canSave, savedSplits, onSaveSplit, onLoadSplit, onDeleteSplit }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  return (
    <>
      <Button size="sm" variant="outline" className="h-7 bg-transparent border-slate-700 text-slate-200 hover:bg-slate-800" onClick={() => setOpen(true)} data-testid="save-split-btn">
        <Save className="w-3.5 h-3.5 mr-1" />Splits{(savedSplits || []).length ? ` (${savedSplits.length})` : ""}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-md" data-testid="splits-dialog">
          <DialogHeader>
            <DialogTitle className="text-slate-100">Saved splits</DialogTitle>
            <DialogDescription className="text-slate-400">Save the current per-container plan against this PO, or load a saved one.</DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2">
            <Input value={name} onChange={e => setName(e.target.value)} placeholder="New split name…" className="h-8 bg-slate-800 border-slate-700 text-slate-100" data-testid="split-save-input"
              onKeyDown={e => { if (e.key === "Enter" && canSave && name.trim()) { onSaveSplit(name.trim()); setName(""); } }} />
            <Button size="sm" className="h-8 bg-blue-600 hover:bg-blue-500" disabled={!canSave || !name.trim()} onClick={() => { onSaveSplit(name.trim()); setName(""); }} data-testid="split-save-confirm">Save</Button>
          </div>
          {!canSave && <p className="text-[11px] text-amber-300">Pick a PO to save a split against it.</p>}
          <div className="space-y-1 max-h-64 overflow-auto" data-testid="load-saved-split-list">
            {(savedSplits || []).length === 0 && <div className="text-xs text-slate-500">No saved splits for this PO yet.</div>}
            {(savedSplits || []).map(s => (
              <div key={s.id} className="flex items-center gap-2 text-sm border border-slate-800 bg-slate-950 rounded px-2 py-1" data-testid={`split-saved-item-${s.id}`}>
                <span className="font-medium text-slate-100">{s.name}</span>
                <span className="text-[11px] text-slate-500">{s.container_count} cont · {s.total_pallets || "?"} plt</span>
                <div className="ml-auto flex items-center gap-1">
                  <Button size="sm" variant="outline" className="h-6 bg-transparent border-slate-700 text-slate-200 hover:bg-slate-800" onClick={() => { onLoadSplit(s); setOpen(false); }} data-testid={`load-saved-split-${s.id}`}>Load</Button>
                  <button type="button" className="text-rose-400 hover:text-rose-300" onClick={() => onDeleteSplit(s.id)} data-testid={`split-saved-delete-${s.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

// SplitBar is imported from ./planQualityUI


const _esc = (s) => String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
function _pinBadge(p, x, y0, w, h) {
  let over = (p && p.top_overhang_in != null) ? p.top_overhang_in : 0;
  let pinned = !!(p && p.pinned);
  const fps = (p && p.layer_footprints_in) || [];
  if (fps.length > 1) {
    for (let i = 1; i < fps.length; i++) {
      const b = fps[i - 1] || [0, 0], c = fps[i] || [0, 0];
      const ov = Math.max(((c[0] || 0) - (b[0] || 0)) / 2, ((c[1] || 0) - (b[1] || 0)) / 2);
      if (ov > 0.1) { pinned = true; over = Math.max(over, Math.round(ov * 10) / 10); }
    }
  }
  over = Math.round(over * 10) / 10;
  if (!pinned || w <= 30 || h <= 12) return "";
  const label = over > 0.1 ? `\u21ca ${over}"` : "\u21ca pinned";
  const bw = Math.min(w - 4, 10 + label.length * 5.6), bh = 12;
  const bx = x + w - bw - 2, by = y0 + h - bh - 2;
  let g = `<g><title>${_esc(`Pinned — a longer/heavier pallet caps the shorter one below (up to ${over} in overhang), weighing it down. No bracing needed.`)}</title>`;
  g += `<rect x="${bx}" y="${by}" width="${bw}" height="${bh}" rx="3" fill="#4338ca" fill-opacity="0.92" stroke="#ffffff" stroke-width="0.6"/>`;
  g += `<text x="${bx + bw / 2}" y="${by + bh - 3}" text-anchor="middle" fill="#ffffff" font-size="8.5" font-weight="800">${label}</text>`;
  return g + `</g>`;
}


// Render a full product code inside an SVG box: shrink the font to fit; if it still
// overflows at the minimum size, wrap onto two lines at a hyphen. Never truncate.
const _codeLabelSvg = (code, cx, cy, boxW, fill = "#fff", maxFont = 12, minFont = 6) => {
  const c = _esc(code);
  const avail = Math.max(8, boxW - 4);
  let fs = Math.min(maxFont, avail / (code.length * 0.62));
  if (fs >= minFont) {
    return `<text x="${cx}" y="${cy}" text-anchor="middle" fill="${fill}" font-size="${fs.toFixed(1)}" font-weight="700">${c}</text>`;
  }
  // wrap: split near the middle at a hyphen (fallback: middle of the string)
  let sp = code.lastIndexOf("-", Math.ceil(code.length / 2));
  if (sp <= 0) sp = code.indexOf("-", Math.ceil(code.length / 2));
  if (sp <= 0) sp = Math.ceil(code.length / 2) - 1;
  const l1 = code.slice(0, sp + 1), l2 = code.slice(sp + 1);
  const longest = Math.max(l1.length, l2.length);
  const fs2 = Math.max(minFont, Math.min(maxFont, avail / (longest * 0.62)));
  return `<text x="${cx}" y="${(cy - fs2 * 0.55).toFixed(1)}" text-anchor="middle" fill="${fill}" font-size="${fs2.toFixed(1)}" font-weight="700">${_esc(l1)}</text>`
    + `<text x="${cx}" y="${(cy + fs2 * 0.6).toFixed(1)}" text-anchor="middle" fill="${fill}" font-size="${fs2.toFixed(1)}" font-weight="700">${_esc(l2)}</text>`;
};


// Horizontal dimension line with outward arrowheads at both ends + a label,
// used to annotate the empty floor length at the front/rear of the container.
// Dimension line + arrowheads across an empty-floor band [x0,x1] within the vertical
// band [yTop,yBot]. Wide gaps get an inline horizontal label sitting in a broken line;
// narrow gaps get a rotated label with a white halo so the line never covers the text.
function _dimBand(x0, x1, yTop, yBot, label, color = "#334155") {
  const w = x1 - x0;
  if (w < 3) return "";
  const yMid = (yTop + yBot) / 2;
  const a = Math.min(9, w / 2);
  const head = (px, dir) => `<path d="M${px},${yMid} L${px + dir * a},${yMid - 4.5} L${px + dir * a},${yMid + 4.5} Z" fill="${color}"/>`;
  const fs = 11;
  const tw = label ? label.length * fs * 0.62 : 0;
  let g = `<g>`;
  if (label && w > tw + 2 * a + 8) {
    const cx = (x0 + x1) / 2, gapL = cx - tw / 2 - 3, gapR = cx + tw / 2 + 3;
    g += `<line x1="${x0}" y1="${yMid}" x2="${gapL}" y2="${yMid}" stroke="${color}" stroke-width="1.2"/>`;
    g += `<line x1="${gapR}" y1="${yMid}" x2="${x1}" y2="${yMid}" stroke="${color}" stroke-width="1.2"/>`;
    g += head(x0, 1) + head(x1, -1);
    g += `<text x="${cx}" y="${yMid + fs * 0.35}" text-anchor="middle" fill="${color}" font-size="${fs}" font-weight="700">${_esc(label)}</text>`;
  } else {
    g += `<line x1="${x0}" y1="${yMid}" x2="${x1}" y2="${yMid}" stroke="${color}" stroke-width="1.2"/>`;
    g += head(x0, 1) + head(x1, -1);
    if (label) {
      const cx = (x0 + x1) / 2, fsN = 10, hh = fsN * 0.72, tw2 = label.length * fsN * 0.62;
      // rotated label + white halo, in the same rotated frame so the halo hides the line
      g += `<g transform="rotate(-90 ${cx} ${yMid})">`;
      g += `<rect x="${cx - tw2 / 2 - 2}" y="${yMid - hh}" width="${tw2 + 4}" height="${hh * 2}" fill="#ffffff" fill-opacity="0.9"/>`;
      g += `<text x="${cx}" y="${yMid + fsN * 0.35}" text-anchor="middle" fill="${color}" font-size="${fsN}" font-weight="700">${_esc(label)}</text>`;
      g += `</g>`;
    }
  }
  return g + `</g>`;
}

function topdownSvg(placement, cmap, cl, cw) {
  const LEN = 1000, sx = LEN / cl, sy = 140 / (cw || 1), TOL = 2;
  let s = `<rect x="0" y="5" width="${LEN}" height="140" fill="#f8fafc" stroke="#334155" stroke-width="1.5"/>`;
  s += `<line x1="0" y1="75" x2="${LEN}" y2="75" stroke="#cbd5e1" stroke-width="1"/>`;
  s += `<line x1="500" y1="5" x2="500" y2="145" stroke="#94a3b8" stroke-width="1" stroke-dasharray="6 4"/>`;
  s += `<line x1="600" y1="5" x2="600" y2="145" stroke="#f59e0b" stroke-width="1.5" stroke-dasharray="6 4"/>`;
  // empty length at the FRONT (nose) and REAR (doors) — visible after auto-balance
  // centres the block, leaving a gap at both ends of the container.
  if (placement.length) {
    const minStart = Math.min(...placement.map(p => p.start_in));
    const maxEnd = Math.max(...placement.map(p => p.end_in));
    const frontGap = Math.round(minStart * 10) / 10;
    const rearGap = Math.round((cl - maxEnd) * 10) / 10;
    const gapBand = (x0, wPx, gapIn, side) => {
      if (wPx < 3 || gapIn <= 0.5) return "";
      const x1 = x0 + wPx;
      let g = `<g><title>${_esc(`${gapIn} in of empty floor at the ${side} of the container`)}</title>`;
      g += `<rect x="${x0}" y="5" width="${wPx}" height="140" fill="#e2e8f0" fill-opacity="0.5" stroke="#94a3b8" stroke-width="0.8" stroke-dasharray="5 3"/>`;
      g += _dimBand(x0 + 2, x1 - 2, 8, 142, `${gapIn}" empty`);
      return g + `</g>`;
    };
    s += gapBand(0, frontGap * sx, frontGap, "front (nose)");
    s += gapBand(maxEnd * sx, rearGap * sx, rearGap, "rear (doors)");
  }
  placement.forEach((p) => {
    const x = p.start_in * sx, w = Math.max(3, (p.end_in - p.start_in) * sx);
    const col = cmap[p.product_code] || "#64748b";
    // viewed from the REAR: lane A = LEFT (drawn bottom), lane B = RIGHT (drawn top).
    // Draw the pallet at its TRUE cross-width, hugging the OUTER wall, so any free-play
    // sits along the centre channel (easy to brace with one row of dunnage).
    const laneCapPx = 140;
    const crossPx = Math.max(3, Math.min(laneCapPx, p.cross_in ? p.cross_in * sy : laneCapPx));
    // hug the OUTER wall so all free-play collects into the centre channel
    const y0 = p.lane === "A" ? 145 - crossPx : 5;
    const h = crossPx;
    const latIn = p.lateral_play_in != null ? p.lateral_play_in : 0;
    const ori = p.full_width ? "Full-width" : (p.rotated ? "Rotated 90\u00b0 (breadthwise)" : "Lengthwise");
    const lps = (p.layer_products && p.layer_products.length) ? p.layer_products : [p.product_code];
    const lfp = p.layer_footprints_in || [];
    const lh = p.layer_heights_in || [];
    const layerLines = lps.map((c, i) => {
      const fp = lfp[i] ? `${lfp[i][0]}\u00d7${lfp[i][1]}` : "";
      const hh = lh[i] != null ? `\u00d7${lh[i]}` : "";
      return `  L${i + 1} ${c}: ${fp}${hh} in`;
    }).join("\n");
    const head = p.headroom_in != null ? `\nHeadroom left on top: ${p.headroom_in} in` : "";
    const brace = p.needs_bracing ? `\n\u26a0 Needs bracing${latIn > TOL ? ` \u00b7 ${latIn} in side clearance` : ""}${p.overhang_in > TOL ? ` \u00b7 ${p.overhang_in} in on-stack overhang` : ""}` : "";
    const wkg = Math.round(p.weight_kg);
    const wlb = Math.round(p.weight_kg * 2.20462);
    const tip = `${p.product_code} \u00b7 ${ori} \u00b7 placed ${p.along_in}\u00d7${p.cross_in} in \u00b7 ${p.layers} layer(s) \u00b7 stack H ${p.total_height_in} in \u00b7 ${wlb.toLocaleString()} lbs / ${wkg.toLocaleString()} kg${p.added ? " \u00b7 ADDED" : ""}${head}${brace}\nLayers (floor\u2192top):\n${layerLines}`;
    s += `<g data-lane="${p.lane}" data-pos="${p.arr_index != null ? p.arr_index : ''}"><title>${_esc(tip + "\n\n\u21bb Corner badge = rotate whole stack 90\u00b0")}</title>`;
    if (p.layers > 1) s += `<rect x="${x + 6}" y="${y0 - 6}" width="${w}" height="${h}" fill="#cbd5e1"/>`;
    const stroke = p.added ? "#0f172a" : "#fff";
    const dash = p.added ? ` stroke-dasharray="5 3"` : "";
    s += `<rect x="${x}" y="${y0}" width="${w}" height="${h}" fill="${col}" stroke="${stroke}" stroke-width="${p.added ? 2 : 1.5}"${dash}/>`;
    if (w > 34 && h > 12) {
      if (p.layers > 1) {
        s += `<text x="${x + w / 2}" y="${y0 + h / 2 + 4}" text-anchor="middle" fill="#fff" font-size="12" font-weight="700">\u00d7${p.layers}</text>`;
      } else {
        s += _codeLabelSvg(p.product_code, x + w / 2, y0 + h / 2 + 4, w);
      }
    }
    // bracing flag (C): orange dashed outline around the pallet
    if (p.needs_bracing) s += `<rect x="${x + 1}" y="${y0 + 1}" width="${Math.max(1, w - 2)}" height="${Math.max(1, h - 2)}" fill="none" stroke="#ea580c" stroke-width="1.6" stroke-dasharray="3 2"/>`;
    s += _pinBadge(p, x, y0, w, h);
    // ── FILLER badge — TOP-RIGHT so it never covers the rotated marker ──
    if (p.added) {
      if (w > 46 && h > 16) {
        const bw = 36, bh = 14, bx = x + w - bw - 3, by = y0 + 3;
        s += `<rect x="${bx}" y="${by}" width="${bw}" height="${bh}" rx="3" fill="#0f172a" stroke="#facc15" stroke-width="1"/>`;
        s += `<text x="${bx + bw / 2}" y="${by + bh - 3}" text-anchor="middle" fill="#facc15" font-size="10" font-weight="800">NEW</text>`;
      } else {
        const cx = x + w - 8, cy = y0 + 8;
        s += `<circle cx="${cx}" cy="${cy}" r="7" fill="#0f172a" stroke="#facc15" stroke-width="1"/>`;
        s += `<text x="${cx}" y="${cy + 4}" text-anchor="middle" fill="#facc15" font-size="12" font-weight="800">+</text>`;
      }
    }
    // ── ROTATED sign — BOTTOM-RIGHT, dark pill w/ white ↻, high contrast on every colour ──
    if (p.arr_index != null && w > 16 && h > 12) {
      const rot = !!p.rotated;
      const bw = Math.min(28, w - 4), bh = Math.min(14, h - 4);
      const bx = x + w - bw - 3, by = y0 + h - bh - 3;
      s += `<g data-rotate-lane="${_esc(p.lane)}" data-rotate-pos="${p.arr_index}" style="cursor:pointer"><title>${_esc(rot ? "Rotated 90\u00b0 (breadthwise) \u2014 click to un-rotate the entire stack" : "Rotate this ENTIRE stack 90\u00b0 (breadthwise). Recomputes footprint + axle balance instantly.")}</title>`;
      s += `<rect x="${bx}" y="${by}" width="${bw}" height="${bh}" rx="${bh / 2}" fill="#0f172a" fill-opacity="0.86" stroke="${rot ? "#fde68a" : "#94a3b8"}" stroke-width="1.1"/>`;
      const lbl = rot ? "\u21bb ON" : "\u21bb";
      s += `<text x="${bx + bw / 2}" y="${by + bh - 3}" text-anchor="middle" fill="${rot ? "#fde68a" : "#ffffff"}" font-size="9.5" font-weight="800" style="pointer-events:none">${lbl}</text>`;
      s += `</g>`;
    }
    s += `</g>`;
  });
  // ---- combined lateral free play (centre channel) ----
  // Each lane hugs its outer wall, so the empty middle = cont_w − crossA − crossB at each
  // along position. Show it as ONE band labelled with the COMBINED inches (both lanes),
  // so an operator sees the true side-to-side slide room, not two half-figures.
  {
    const A = placement.filter((p) => p.lane === "A");
    const B = placement.filter((p) => p.lane === "B");
    const AB = placement.filter((p) => p.lane === "AB");
    const bounds = new Set([0, cl]);
    placement.forEach((p) => { bounds.add(Math.max(0, p.start_in)); bounds.add(Math.min(cl, p.end_in)); });
    const xs = [...bounds].filter((v) => v >= -0.01 && v <= cl + 0.01).sort((a, b) => a - b);
    const crossOf = (arr, mid) => { let c = 0; arr.forEach((p) => { if (p.start_in - 0.01 <= mid && p.end_in + 0.01 >= mid) c = Math.max(c, p.cross_in || 0); }); return c; };
    const segs = [];
    for (let i = 0; i < xs.length - 1; i++) {
      const x0 = xs[i], x1 = xs[i + 1];
      if (x1 - x0 < 0.5) { segs.push(null); continue; }
      const mid = (x0 + x1) / 2;
      if (AB.some((p) => p.start_in - 0.01 <= mid && p.end_in + 0.01 >= mid)) { segs.push(null); continue; }
      const ca = crossOf(A, mid), cb = crossOf(B, mid);
      if (ca === 0 && cb === 0) { segs.push(null); continue; }
      const gap = Math.round((cw - ca - cb) * 10) / 10;
      if (gap <= TOL) { segs.push(null); continue; }
      segs.push({ x0, x1, ca, cb, gap });
    }
    const merged = [];
    segs.forEach((sg) => {
      if (!sg) { merged.push(null); return; }
      const last = merged[merged.length - 1];
      if (last && Math.abs(last.ca - sg.ca) < 0.6 && Math.abs(last.cb - sg.cb) < 0.6) last.x1 = sg.x1;
      else merged.push({ ...sg });
    });
    merged.forEach((sg) => {
      if (!sg) return;
      const px0 = sg.x0 * sx, px1 = sg.x1 * sx;
      const yTopBand = 5 + sg.cb * sy, yBotBand = 145 - sg.ca * sy;
      const bh = yBotBand - yTopBand;
      if (bh < 4 || px1 - px0 < 3) return;
      s += `<g><title>${_esc(`${sg.gap} in combined side clearance (both lanes) — brace this centre channel`)}</title>`;
      s += `<rect x="${px0}" y="${yTopBand}" width="${px1 - px0}" height="${bh}" fill="#fde68a" fill-opacity="0.6" stroke="#f59e0b" stroke-width="0.9" stroke-dasharray="5 3"/>`;
      if ((px1 - px0) > 46 && bh > 10) s += `<text x="${(px0 + px1) / 2}" y="${(yTopBand + yBotBand) / 2 + 3.5}" text-anchor="middle" fill="#b45309" font-size="10" font-weight="800">${sg.gap}" gap</text>`;
      else if (bh > 24 && (px1 - px0) > 16) { const cx = (px0 + px1) / 2, cy = (yTopBand + yBotBand) / 2; s += `<text x="${cx}" y="${cy + 3}" text-anchor="middle" fill="#b45309" font-size="9" font-weight="800" transform="rotate(-90 ${cx} ${cy})">${sg.gap}"</text>`; }
      s += `</g>`;
    });
  }
  return `<svg viewBox="0 0 1000 150" preserveAspectRatio="none" class="w-full h-28 mb-3" style="display:block">${s}</svg>`;
}

function sideSvg(placement, cmap, laneId, cl, ch) {
  const LEN = 1000, sx = LEN / cl, yB = 105, yT = 5, sh = 100 / ch;
  let s = `<rect x="0" y="${yT}" width="${LEN}" height="${yB - yT}" fill="#f8fafc" stroke="#334155" stroke-width="1.5"/>`;
  // empty floor length at the front (nose) & rear (doors) — kept in sync with the
  // top-down view (uses the whole plan's extents, not just this lane).
  if (placement.length) {
    const minStart = Math.min(...placement.map(p => p.start_in));
    const maxEnd = Math.max(...placement.map(p => p.end_in));
    const frontGap = Math.round(minStart * 10) / 10;
    const rearGap = Math.round((cl - maxEnd) * 10) / 10;
    const gapBand = (x0, wPx, gapIn, side) => {
      if (wPx < 3 || gapIn <= 0.5) return "";
      const x1 = x0 + wPx;
      let g = `<g><title>${_esc(`${gapIn} in of empty floor at the ${side} of the container`)}</title>`;
      g += `<rect x="${x0}" y="${yT}" width="${wPx}" height="${yB - yT}" fill="#e2e8f0" fill-opacity="0.5" stroke="#94a3b8" stroke-width="0.7" stroke-dasharray="4 3"/>`;
      g += _dimBand(x0 + 2, x1 - 2, yT + 3, yB - 3, `${gapIn}"`);
      return g + `</g>`;
    };
    s += gapBand(0, frontGap * sx, frontGap, "front (nose)");
    s += gapBand(maxEnd * sx, rearGap * sx, rearGap, "rear (doors)");
  }
  placement.filter((p) => p.lane === laneId || p.lane === "AB").forEach((p) => {
    const x = p.start_in * sx, w = Math.max(3, (p.end_in - p.start_in) * sx);
    const col = cmap[p.product_code] || "#64748b";
    const heights = (p.layer_heights_in && p.layer_heights_in.length)
      ? p.layer_heights_in
      : Array(p.layers).fill(p.height_in || ch / Math.max(1, p.layers));
    const lps = (p.layer_products && p.layer_products.length) ? p.layer_products : [p.product_code];
    const lfp = p.layer_footprints_in || [];
    const lal = p.layer_along_in || [];
    const la = p.layer_added || [];
    const isAdded = (i) => (la.length ? !!la[i] : !!p.added);
    const totalH = heights.reduce((a, b) => a + b, 0);
    const over = totalH > ch + 0.1;
    let cum = 0;
    for (let layer = 0; layer < heights.length; layer++) {
      const boxh = heights[layer] * sh;
      const yy = yB - (cum + heights[layer]) * sh;
      cum += heights[layer];
      if (yy > yB) continue;
      const yTop = Math.max(yT, yy);
      const dh = Math.min(boxh, yB - yTop) - 1;
      if (dh <= 0) continue;
      const code = lps[layer] || p.product_code;
      const lcol = cmap[code] || col;
      const added = isAdded(layer);
      const ll = p.layer_loose || [];
      const loose = ll.length ? !!ll[layer] : false;
      // corner-align each layer to the FRONT edge of the base (B) so an upper pallet is
      // braced by the pallet ahead + wall; the open overhang is shown for bracing (C).
      const lw = (lal[layer] != null && lal[layer] > 0) ? Math.max(3, lal[layer] * sx) : w;
      const belowLw = (layer > 0 && lal[layer - 1] != null && lal[layer - 1] > 0) ? Math.max(3, lal[layer - 1] * sx) : w;
      const lx = x;
      const fp = lfp[layer] ? ` \u00b7 ${lfp[layer][0]}\u00d7${lfp[layer][1]}\u00d7${heights[layer]} in` : "";
      s += `<g><title>${_esc(`${code} \u00b7 layer ${layer + 1} of ${heights.length}${fp}${added ? " \u00b7 ADDED" : ""}${loose ? " \u00b7 LOOSE \u2014 brace" : ""}`)}</title>`;
      const lstroke = over ? "#dc2626" : (loose ? "#ea580c" : (added ? "#0f172a" : "#fff"));
      const ldash = (added || loose) ? ` stroke-dasharray="4 2"` : "";
      // overhang gap (free play) between this layer's rear edge and the pallet below it
      if (loose && (belowLw - lw) > 4) {
        s += `<rect x="${lx + lw}" y="${yTop}" width="${belowLw - lw}" height="${dh}" fill="#fde68a" fill-opacity="0.55" stroke="#f59e0b" stroke-width="0.7" stroke-dasharray="3 2"/>`;
        if ((belowLw - lw) > 24 && dh > 10) s += `<text x="${lx + lw + (belowLw - lw) / 2}" y="${yTop + dh / 2 + 3}" text-anchor="middle" fill="#b45309" font-size="8" font-weight="700">gap</text>`;
      }
      s += `<rect x="${lx}" y="${yTop}" width="${lw}" height="${dh}" fill="${lcol}" stroke="${lstroke}" stroke-width="${over ? 1.5 : (loose ? 1.6 : (added ? 1.5 : 0.8))}"${ldash}/>`;
      // per-layer NEW marker so every added pallet in a stack is flagged, not just the top one
      if (lw > 30 && dh > 11) {
        const codeY = added ? (yTop + dh - 3) : (yTop + dh / 2 + 3.5);
        s += _codeLabelSvg(code, lx + lw / 2, codeY, lw, "#fff", 9.5, 5.5);
      }
      // per-layer NEW marker (top-left corner) — drawn on top of the code label
      if (added && dh > 6) {
        if (lw > 24 && dh > 9) {
          const tw = Math.min(24, lw - 3), th = Math.min(9, dh - 2);
          s += `<rect x="${lx + 1.5}" y="${yTop + 1}" width="${tw}" height="${th}" rx="1.5" fill="#0f172a" stroke="#facc15" stroke-width="0.7"/>`;
          s += `<text x="${lx + 1.5 + tw / 2}" y="${yTop + 1 + th - 1.8}" text-anchor="middle" fill="#facc15" font-size="6.5" font-weight="800">NEW</text>`;
        } else {
          s += `<circle cx="${lx + Math.min(lw / 2, 6)}" cy="${yTop + Math.min(dh / 2, 5)}" r="${Math.min(4, dh / 2 - 0.5)}" fill="#0f172a" stroke="#facc15" stroke-width="0.7"/>`;
        }
      }
      s += `</g>`;
    }
    // headroom band: empty height left above this stack
    if (!over && p.headroom_in != null && p.headroom_in > 1) {
      const topY = yB - totalH * sh;
      const bandH = topY - yT;
      if (bandH > 3) {
        s += `<g><title>${_esc(`${p.headroom_in} in of empty height above this stack`)}</title>`;
        s += `<rect x="${x}" y="${yT}" width="${w}" height="${bandH}" fill="none" stroke="#94a3b8" stroke-width="0.7" stroke-dasharray="3 2"/>`;
        if (w > 34 && bandH > 10) s += `<text x="${x + w / 2}" y="${yT + bandH / 2 + 3}" text-anchor="middle" fill="#94a3b8" font-size="8.5" font-weight="600">${p.headroom_in}" left</text>`;
        s += `</g>`;
      }
    }
  });
  return `<svg viewBox="0 0 1000 115" preserveAspectRatio="none" class="w-full h-24" style="display:block">${s}</svg>`;
}

function LoadDiagram({ plan }) {
  const cmap = buildColorMap(plan.placement);
  const cont = plan.container;
  const cl = cont.internal_length_in || 1;
  const ch = cont.internal_height_in || 1;
  const cw = cont.internal_width_in || 1;
  const placement = plan.placement || [];
  if (!placement.length) {
    return <div className="text-sm text-slate-500 p-3 border border-slate-200 rounded-lg" data-testid="load-diagram">No floor-placed pallets to diagram (products are missing dimensions).</div>;
  }
  // NOTE: SVG is rendered via innerHTML strings (not JSX) so the dev-build
  // click-instrumentation can't inject <span> nodes inside the SVG (which zeroes
  // out <rect> geometry and hides the blocks in preview).
  return (
    <div className="border border-slate-200 rounded-lg p-3" data-testid="load-diagram">
      <div className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-1.5"><Boxes className="w-4 h-4" />Loading diagram</div>
      <div className="flex flex-wrap gap-x-3 gap-y-1 mb-2" data-testid="diagram-legend">
        {Object.entries(cmap).map(([code, col]) => (
          <span key={code} className="inline-flex items-center gap-1 text-[11px] text-slate-600"><span className="w-3 h-3 rounded-sm inline-block" style={{ background: col }} />{code}</span>
        ))}
      </div>
      <div className="text-[11px] text-slate-500 mb-1">Top-down floor plan <span className="text-slate-400">(×N = stack layers; ↻ = rotated 90°; black “NEW” tag = added; <span className="text-indigo-600 font-medium">indigo ⇊ badge = pinned (a longer/heavier pallet caps a shorter one below, weighing it down — no bracing)</span>; amber centre band = combined side clearance of BOTH lanes (slide risk — brace it); orange dashed = needs bracing; grey line = 50%, amber = 60% FHWA; shaded end bands = empty floor)</span></div>
      <div className="flex items-stretch gap-1.5 mb-1">
        <div className="flex items-center justify-center" data-testid="label-front">
          <span className="text-[10px] font-bold tracking-wider text-slate-600" style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}>◄ FRONT (nose)</span>
        </div>
        <div className="flex-1">
          <div className="text-[10px] font-bold tracking-wider text-slate-500 text-center mb-0.5" data-testid="label-right">RIGHT (FROM REAR)</div>
          <div data-testid="topdown" dangerouslySetInnerHTML={{ __html: topdownSvg(placement, cmap, cl, cw) }} />
          <div className="text-[10px] font-bold tracking-wider text-slate-500 text-center mt-0.5" data-testid="label-left">LEFT (FROM REAR)</div>
        </div>
        <div className="flex items-center justify-center" data-testid="label-rear">
          <span className="text-[10px] font-bold tracking-wider text-slate-600" style={{ writingMode: "vertical-rl" }}>REAR (doors) ►</span>
        </div>
      </div>
      <div className="text-[10px] text-slate-400 mb-3">Loading runs front → rear; left/right are as seen from the rear doors. Hover any block to see its product, orientation (lengthwise vs rotated), size &amp; weight.</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <div className="text-[11px] text-slate-500 mb-0.5">Side elevation — Right Lane (from Rear) <span className="text-slate-400">(front → rear, stacks ↑, dashed = free height on top)</span></div>
          <div data-testid="side-B" dangerouslySetInnerHTML={{ __html: sideSvg(placement, cmap, "B", cl, ch) }} />
        </div>
        <div>
          <div className="text-[11px] text-slate-500 mb-0.5">Side elevation — Left Lane (from Rear) <span className="text-slate-400">(front → rear, stacks ↑, dashed = free height on top)</span></div>
          <div data-testid="side-A" dangerouslySetInnerHTML={{ __html: sideSvg(placement, cmap, "A", cl, ch) }} />
        </div>
      </div>
    </div>
  );
}

function CompareTop({ placement, cmap, cl, cw, label, labelClass, testid }) {
  return (
    <div>
      <div className={`text-[11px] font-semibold mb-1 ${labelClass || "text-slate-600"}`} data-testid={`${testid}-label`}>{label}</div>
      <div className="flex items-stretch gap-1">
        <div className="flex items-center justify-center"><span className="text-[9px] font-bold tracking-wider text-slate-500" style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}>◄ FRONT</span></div>
        <div className="flex-1">
          <div className="text-[9px] font-bold tracking-wider text-slate-500 text-center">RIGHT (REAR)</div>
          <div data-testid={testid} dangerouslySetInnerHTML={{ __html: topdownSvg(placement, cmap, cl, cw) }} />
          <div className="text-[9px] font-bold tracking-wider text-slate-500 text-center">LEFT (REAR)</div>
        </div>
        <div className="flex items-center justify-center"><span className="text-[9px] font-bold tracking-wider text-slate-500" style={{ writingMode: "vertical-rl" }}>REAR ►</span></div>
      </div>
    </div>
  );
}

function CompareSide({ placement, cmap, cl, ch, label, labelClass, testidA, testidB }) {
  return (
    <div>
      <div className={`text-[11px] font-semibold mb-1 ${labelClass || "text-slate-600"}`}>{label}</div>
      <div className="text-[10px] text-slate-500">Right Lane (from Rear)</div>
      <div data-testid={testidB} dangerouslySetInnerHTML={{ __html: sideSvg(placement, cmap, "B", cl, ch) }} />
      <div className="text-[10px] text-slate-500 mt-1">Left Lane (from Rear)</div>
      <div data-testid={testidA} dangerouslySetInnerHTML={{ __html: sideSvg(placement, cmap, "A", cl, ch) }} />
    </div>
  );
}

function PlanResult({ plan: rawPlan, basePlan, onAutoBalance, loading, fillProps, removeProps, manualMode, multiMode, onOpenBoard }) {
  const [view, setView] = useState("ordered");
  useEffect(() => { setView("ordered"); }, [rawPlan]);
  const hasFitted = !!rawPlan.fitted_plan;
  const plan = (view === "fitted" && rawPlan.fitted_plan) ? rawPlan.fitted_plan : rawPlan;
  const meta = STATUS_META[plan.status] || STATUS_META.fits;
  const StatusIcon = meta.icon;
  const wd = plan.weight_distribution;
  const balanceIssue = !wd.fhwa_ok || !wd.lr_ok;
  const [showCompare, setShowCompare] = useState(false);
  const hasAdded = (plan.product_rows || []).some(r => r.added);
  const canCompare = hasAdded && basePlan && basePlan.placement && basePlan.placement.length;
  const bwd = basePlan && basePlan.weight_distribution;
  const delta = (a, b) => { const d = Math.round((a - b) * 10) / 10; return (d >= 0 ? "+" : "") + d; };
  // shared colour map across before & after so a product keeps the same colour in both
  const cmpCmap = buildColorMap([...((basePlan && basePlan.placement) || []), ...(plan.placement || [])]);
  const cCl = plan.container.internal_length_in || 1;
  const cCh = plan.container.internal_height_in || 1;
  const cCw = plan.container.internal_width_in || 1;
  return (
    <div className="border-t border-slate-200 p-4 space-y-4" data-testid="plan-result">
      <div className="flex flex-wrap items-center gap-3">
        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full border text-sm font-semibold ${meta.color}`} data-testid="plan-status-badge">
          <StatusIcon className="w-4 h-4" />{meta.label}
        </span>
        <span className="text-sm text-slate-500">{plan.title}</span>
        {rawPlan.optimized && <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 border border-indigo-200" data-testid="balanced-badge"><Scale3d className="w-3 h-3" />Auto-balanced</span>}
        {plan.securing?.needs_bracing && (
          <button type="button" data-testid="needs-bracing-chip"
            onClick={() => document.querySelector('[data-testid="plan-advisory"]')?.scrollIntoView({ behavior: "smooth", block: "center" })}
            title={`${plan.securing.overhang_pallet_count} upper pallet(s) overhang the pallet below; ${plan.securing.lateral_gap_count} stack(s) have side clearance (up to ${plan.securing.max_lateral_play_in}"). Click for dunnage guidance.`}
            className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-orange-100 text-orange-800 border border-orange-300 hover:bg-orange-200 transition-colors">
            <AlertTriangle className="w-3 h-3" />Needs bracing ({(plan.securing.loose_stacks || []).length})
          </button>
        )}

        <div className="ml-auto flex items-center gap-2">
          {/* Auto-balance toggle (auto mode only) */}
          {!manualMode && (rawPlan.optimized ? (
            <Button variant="outline" size="sm" onClick={() => onAutoBalance()} disabled={loading} data-testid="auto-balance-btn"><ArrowLeftRight className="w-4 h-4 mr-1" />Show as-ordered</Button>
          ) : (
            <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700" onClick={() => onAutoBalance()} disabled={loading} data-testid="auto-balance-btn"><Scale3d className="w-4 h-4 mr-1" />Auto-balance load</Button>
          ))}
          {!multiMode && onOpenBoard && (
            <Button variant="outline" size="sm" className="border-blue-300 text-blue-700 hover:bg-blue-50" onClick={onOpenBoard} data-testid="plan-open-load-board-btn" title="Open this plan in the Load Board — add, offload and rearrange pallets with full drag-and-drop tools"><Boxes className="w-4 h-4 mr-1" />Open in Load Board</Button>
          )}
          {manualMode && <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 border border-blue-200" data-testid="manual-mode-badge"><Settings2 className="w-3 h-3" />Manual arrangement</span>}
        </div>
      </div>

      {/* Fits-one-container toggle (secondary, over-capacity only) */}
      {hasFitted && (
        <div className="flex flex-wrap items-center gap-2" data-testid="fitted-toggle">
          <span className="text-xs text-slate-500">This order overflows one container. View:</span>
          <div className="flex gap-1 bg-slate-100 p-1 rounded-lg">
            <button onClick={() => setView("ordered")} className={`px-3 py-1 rounded-md text-xs font-medium ${view === "ordered" ? "bg-white shadow text-rose-700" : "text-slate-500"}`} data-testid="view-ordered-btn">As ordered (over capacity)</button>
            <button onClick={() => setView("fitted")} className={`px-3 py-1 rounded-md text-xs font-medium ${view === "fitted" ? "bg-white shadow text-emerald-700" : "text-slate-500"}`} data-testid="view-fitted-btn">Fits one container (−{(rawPlan.fitted_removed || []).reduce((a, r) => a + r.pallets_removed, 0)} pallets)</button>
          </div>
        </div>
      )}

      {/* Removed-to-fit banner */}
      {view === "fitted" && (rawPlan.fitted_removed || []).length > 0 && (
        <div className="border border-rose-200 rounded-lg overflow-hidden" data-testid="removed-to-fit">
          <div className="px-3 py-2 bg-rose-50 text-sm font-medium text-rose-800 flex items-center gap-1.5"><AlertTriangle className="w-4 h-4" />Removed to fit one container</div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider"><tr><th className="text-left p-2">Product</th><th className="text-right p-2">Pallets removed</th><th className="text-right p-2">Pieces removed</th></tr></thead>
            <tbody>
              {rawPlan.fitted_removed.map((r, i) => (
                <tr key={i} className="border-t border-slate-100" data-testid={`removed-fit-${r.product_code}`}>
                  <td className="p-2 font-medium">{r.product_code}</td>
                  <td className="p-2 text-right">{r.pallets_removed}</td>
                  <td className="p-2 text-right">{r.pieces_removed.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {rawPlan.optimized && balanceIssue && (
        <p className="text-[11px] text-slate-400" data-testid="balance-note">Auto-balance re-arranges the same pallets for the best possible distribution; some imbalance can remain with few or very uneven pallets.</p>
      )}


      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI label="Total weight"
             value={<WeightLbsKg kg={plan.total_weight_kg} size="lg" />}
             sub={<span className="inline-flex items-center gap-1">cap <WeightLbsKg kg={plan.container.max_payload_kg} /></span>}
             tone={plan.total_weight_kg > plan.container.max_payload_kg ? "rose" : "slate"} />
        <KPI label="Utilisation" value={`${plan.utilisation_weight_pct}%`} sub={`${plan.utilisation_length_pct}% floor length`} tone="blue" />
        <KPI label="Pallets · stacks" value={`${plan.total_pallets} · ${plan.total_stacks}`} sub={`${plan.used_length_in} in used`} />
        <KPI label="Remaining"
             value={<WeightLbsKg kg={plan.remaining_weight_kg} size="lg" />}
             sub={`${plan.remaining_length_in} in floor`}
             tone={plan.status === "under_loaded" ? "amber" : "green"} />
      </div>

      {/* Load quality assessment */}
      <QualityCard quality={plan.quality} />

      {/* Weight distribution */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="border border-slate-200 rounded-lg p-3">
          <div className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-1.5">Front / Rear split
            <span className={`text-[11px] px-1.5 py-0.5 rounded ${wd.fhwa_ok ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>{wd.fhwa_ok ? "60/40 OK" : "60/40 FAIL"}</span>
          </div>
          <SplitBar leftPct={wd.front_pct} rightPct={wd.rear_pct} ok={wd.fhwa_ok}
            leftLabel={<span className="inline-flex items-center gap-1"><span className="text-slate-400">Front</span><WeightLbsKg kg={wd.front_kg} /></span>}
            rightLabel={<span className="inline-flex items-center gap-1"><span className="text-slate-400">Rear</span><WeightLbsKg kg={wd.rear_kg} /></span>} guideline />
          <p className="text-[11px] text-slate-400 mt-1.5">Longitudinal CoG at {wd.cog_long_pct}% of length (ideal ≈ 50%). Amber line = 60% FHWA guideline.</p>
        </div>
        <div className="border border-slate-200 rounded-lg p-3">
          <div className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-1.5">Left / Right balance
            <span className={`text-[11px] px-1.5 py-0.5 rounded ${wd.lr_ok ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{wd.lr_ok ? "Balanced" : "Uneven"}</span>
          </div>
          <SplitBar leftPct={wd.left_pct} rightPct={wd.right_pct} ok={wd.lr_ok}
            leftLabel={<span className="inline-flex items-center gap-1"><span className="text-slate-400">Left</span><WeightLbsKg kg={wd.left_kg} /></span>}
            rightLabel={<span className="inline-flex items-center gap-1"><span className="text-slate-400">Right</span><WeightLbsKg kg={wd.right_kg} /></span>} />
          <p className="text-[11px] text-slate-400 mt-1.5">Even left/right keeps the trailer stable in corners.</p>
        </div>
      </div>

      {/* Loading diagram */}
      {canCompare && (
        <div className="flex items-center gap-2">
          <Button size="sm" variant={showCompare ? "default" : "outline"} className={showCompare ? "bg-indigo-600 hover:bg-indigo-700" : ""} onClick={() => setShowCompare(s => !s)} data-testid="compare-toggle-btn">
            <Boxes className="w-3.5 h-3.5 mr-1" />{showCompare ? "Hide before/after" : "Compare before / after"}
          </Button>
        </div>
      )}

      {canCompare && showCompare ? (
        <div className="space-y-4" data-testid="compare-view">
          <div className="grid grid-cols-2 gap-3 text-sm">
            {[["Utilisation (wt)", basePlan.utilisation_weight_pct, plan.utilisation_weight_pct, "%"],
              ["Floor used", basePlan.utilisation_length_pct, plan.utilisation_length_pct, "%"],
              ["Total weight (lbs)", Math.round((basePlan.total_weight_kg || 0) * KG_TO_LB), Math.round((plan.total_weight_kg || 0) * KG_TO_LB), " lbs"],
              ["Total weight (kg)", basePlan.total_weight_kg, plan.total_weight_kg, " kg"],
              ["Pallets", basePlan.total_pallets, plan.total_pallets, ""],
              ["Front / Rear", bwd && bwd.rear_pct, wd.rear_pct, "% rear"],
              ["Left / Right", bwd && bwd.right_pct, wd.right_pct, "% right"]].map(([lbl, a, b, u], i) => (
              <div key={i} className="flex items-center justify-between border border-slate-200 rounded-lg px-3 py-1.5" data-testid={`compare-delta-${i}`}>
                <span className="text-slate-500">{lbl}</span>
                <span className="text-slate-700 font-medium">{a}{u} → {b}{u} <span className={`ml-1 text-[11px] ${(b - a) >= 0 ? "text-emerald-600" : "text-rose-600"}`}>({delta(b, a)})</span></span>
              </div>
            ))}
          </div>
          <div className="border border-slate-200 rounded-lg p-3">
            <div className="flex flex-wrap gap-x-3 gap-y-1 mb-2" data-testid="compare-legend">
              {Object.entries(cmpCmap).map(([code, col]) => (
                <span key={code} className="inline-flex items-center gap-1 text-[11px] text-slate-600"><span className="w-3 h-3 rounded-sm inline-block" style={{ background: col }} />{code}</span>
              ))}
            </div>
            <div className="text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wider">Top-down — before vs after <span className="normal-case text-slate-400">(front → rear; L/R from rear doors; black “NEW” tag = added; shaded end bands = empty floor)</span></div>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <CompareTop placement={basePlan.placement} cmap={cmpCmap} cl={cCl} cw={cCw} testid="topdown-before"
                label={`Before — original ${basePlan.optimized ? "(auto-balanced)" : "(as ordered)"}`} labelClass="text-slate-600" />
              <CompareTop placement={plan.placement} cmap={cmpCmap} cl={cCl} cw={cCw} testid="topdown-after"
                label="After — with added pallets" labelClass="text-indigo-600" />
            </div>
            <div className="text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wider">Side elevation — before vs after</div>
            <div className="grid grid-cols-2 gap-4">
              <CompareSide placement={basePlan.placement} cmap={cmpCmap} cl={cCl} ch={cCh} testidA="side-before-A" testidB="side-before-B"
                label="Before" labelClass="text-slate-600" />
              <CompareSide placement={plan.placement} cmap={cmpCmap} cl={cCl} ch={cCh} testidA="side-after-A" testidB="side-after-B"
                label="After" labelClass="text-indigo-600" />
            </div>
          </div>
        </div>
      ) : (
        <LoadDiagram plan={plan} />
      )}

      {/* Advisory */}
      <div className="border border-slate-200 rounded-lg p-3 bg-slate-50">
        <div className="text-sm font-medium text-slate-700 mb-1.5">Customer advisory</div>
        <ul className="space-y-1" data-testid="plan-advisory">
          {(plan.advisory || []).map((line, i) => (
            <li key={i} className={`text-sm ${line.includes("⚠") || line.startsWith("OVER") ? "text-rose-600" : "text-slate-600"}`}>• {line}</li>
          ))}
        </ul>
      </div>

      {/* Product rows */}
      <div className="border border-slate-200 rounded-lg overflow-hidden">
        <div className="px-3 py-2 bg-slate-100 text-sm font-medium text-slate-700 flex items-center gap-1.5"><Boxes className="w-4 h-4" />Load breakdown</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="plan-product-table">
            <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
              <tr>
                <th className="text-left p-2">Product</th><th className="text-right p-2">Qty</th><th className="text-right p-2">Pcs/Plt</th>
                <th className="text-right p-2">Pallets</th><th className="text-right p-2">Stacks</th><th className="text-right p-2">Layers</th>
                <th className="text-right p-2">Footprint (in)</th><th className="text-right p-2">Ht (in)</th><th className="text-right p-2">Weight (kg)</th>
              </tr>
            </thead>
            <tbody>
              {plan.product_rows.map((r, i) => (
                <tr key={i} className="border-t border-slate-100" data-testid={`plan-row-${r.product_code}`}>
                  <td className="p-2">
                    <span className="font-medium text-slate-800">{r.product_code}</span>
                    {r.added && <Badge className="ml-1.5 text-[9px] bg-emerald-100 text-emerald-700 border-emerald-200" data-testid={`added-badge-${r.product_code}`}>added</Badge>}
                    {!r.matched && <Badge className="ml-1.5 text-[9px] bg-rose-100 text-rose-700 border-rose-200">not in catalog</Badge>}
                    {r.matched && r.incomplete && <Badge className="ml-1.5 text-[9px] bg-amber-100 text-amber-700 border-amber-200">no dims</Badge>}
                  </td>
                  <td className="p-2 text-right">{r.qty}</td>
                  <td className="p-2 text-right">{r.pieces_per_pallet || "—"}</td>
                  <td className="p-2 text-right">{r.pallet_count ?? "—"}</td>
                  <td className="p-2 text-right">{r.stack_count ?? "—"}</td>
                  <td className="p-2 text-right">{r.layers_per_stack ?? "—"}</td>
                  <td className="p-2 text-right">{r.per_pallet_footprint_in ? `${r.per_pallet_footprint_in[0]}×${r.per_pallet_footprint_in[1]}` : "—"}</td>
                  <td className="p-2 text-right">{r.pallet_height_in ?? "—"}</td>
                  <td className="p-2 text-right">{r.total_weight_kg != null ? r.total_weight_kg.toLocaleString() : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Suggestions (under-load) */}
      {plan.status === "under_loaded" && (
        <div className="border border-amber-200 rounded-lg overflow-hidden" data-testid="plan-suggestions">
          <div className="px-3 py-2 bg-amber-50 text-sm font-medium text-amber-800 flex items-center gap-1.5"><PackageCheck className="w-4 h-4" />Fill the container — pending items to the same destination with stock</div>
          {(plan.suggestions || []).length === 0 ? (
            <div className="p-3 text-sm text-slate-500">No matching pending-order items with available stock for this destination.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
                <tr><th className="text-left p-2">Product</th><th className="text-right p-2">In stock</th><th className="text-right p-2">Pcs/Plt</th><th className="text-right p-2">Add ~pallets</th><th className="text-left p-2">On pending PO</th></tr>
              </thead>
              <tbody>
                {plan.suggestions.map((s, i) => (
                  <tr key={i} className="border-t border-slate-100" data-testid={`suggestion-${s.product_code}`}>
                    <td className="p-2"><span className="font-medium">{s.product_code}</span> <span className="text-slate-400 text-xs">{s.description}</span></td>
                    <td className="p-2 text-right">{s.available_pieces?.toLocaleString()}</td>
                    <td className="p-2 text-right">{s.pieces_per_pallet || "—"}</td>
                    <td className="p-2 text-right font-medium text-emerald-700">{s.suggested_pallets ?? "—"}</td>
                    <td className="p-2 text-xs text-slate-500">{(s.on_pending_pos || []).join(", ")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Removal (over-load) — interactive trim controls (auto mode only) */}
      {!manualMode && !multiMode && plan.status === "over_loaded" && removeProps && (
        <OverCapacityAdjust plan={plan} {...removeProps} loading={loading} />
      )}

      {/* Fill remaining space (catalog recommender) — auto mode only */}
      {!manualMode && !multiMode && fillProps && view !== "fitted" && <FillPanel {...fillProps} />}
    </div>
  );
}

function CatalogPicker({ catalog, onPick, placeholder, testid, autoFocus }) {
  const [q, setQ] = useState("");
  const ql = q.trim().toLowerCase();
  const results = ql
    ? catalog.filter(p => (p.product_code || "").toLowerCase().includes(ql) || (p.description || "").toLowerCase().includes(ql)).slice(0, 25)
    : [];
  return (
    <div className="relative" data-testid={testid}>
      <Input autoFocus={autoFocus} className="h-8 text-sm" placeholder={placeholder} value={q}
        onChange={e => setQ(e.target.value)} data-testid={`${testid}-input`} />
      {results.length > 0 && (
        <div className="absolute z-30 mt-1 w-full max-h-52 overflow-auto border border-slate-200 rounded-lg bg-white shadow-lg">
          {results.map(p => (
            <button key={p.id || p.product_code} type="button"
              onClick={() => { if (!p.default_qty_per_pallet) { toast.error(`${p.product_code} has no pieces/pallet set — set it in Product Catalog first`); return; } onPick(p.product_code); setQ(""); }}
              className="w-full text-left px-3 py-1.5 hover:bg-blue-50 border-b border-slate-50 text-sm flex items-center gap-2"
              data-testid={`${testid}-option-${p.product_code}`}>
              <span className="font-medium text-slate-800 whitespace-nowrap">{p.product_code}</span>
              <span className="text-slate-400 text-xs truncate flex-1">{p.description}</span>
              <Plus className="w-3.5 h-3.5 text-blue-600 shrink-0" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ManualArrange({ arrangement, addStacksToLane, addLayerToStack, onAuditStack, removeLayer, removeStack, clearArrangement, plan, loading, saved, onSave, onLoad, onDelete, improve, improving, onAutoImprove, onApplyImprove, onDismissImprove, onCompare, hideActions, cmap: cmapProp }) {
  const [cat, setCat] = useState([]);
  const [openTop, setOpenTop] = useState(null);   // `${lane}-${idx}` of the stack whose "on top" picker is open
  const [asName, setAsName] = useState(null);      // null = closed; string = name being typed
  const [showSaved, setShowSaved] = useState(false);
  useEffect(() => {
    let ok = true;
    axios.get(`${API}/catalog/products`, authH()).then(r => { if (ok) setCat(r.data.products || []); }).catch(() => {});
    return () => { ok = false; };
  }, []);
  const cmap = useMemo(() => cmapProp || buildColorMap(plan?.placement || []), [plan, cmapProp]);
  const colorOf = (code) => cmap[code] || "#64748b";
  const total = arrangement.A.length + arrangement.B.length;
  const totalPallets = [...arrangement.A, ...arrangement.B].reduce((a, c) => a + c.layers.length, 0);

  const laneStats = (id) => {
    const cols = arrangement[id];
    const pallets = cols.reduce((a, c) => a + c.layers.length, 0);
    // Weight honestly tracks the CURRENT arrangement: an empty lane is 0 kg (never show stale
    // placement weight left over from a previous layout after a Clear all).
    const wt = cols.length
      ? (plan?.placement || []).filter(p => p.lane === id || (id === "A" && p.lane === "AB")).reduce((a, p) => a + (p.weight_kg || 0), 0)
      : 0;
    return { stacks: cols.length, pallets, wt: Math.round(wt) };
  };

  const renderLane = (id, title) => {
    const cols = arrangement[id];
    const st = laneStats(id);
    return (
      <div className="border border-slate-200 rounded-lg bg-slate-50" data-testid={`manual-lane-${id}`}>
        <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-200 text-sm font-semibold">
          {title}
          <span className="text-[11px] font-normal px-2 py-0.5 rounded-full bg-slate-200 text-slate-600" data-testid={`manual-lane-stats-${id}`}>{st.stacks} stacks · {st.pallets} pallets{st.wt ? ` · ${st.wt.toLocaleString()} kg` : ""}</span>
        </div>
        <div className="p-3 flex gap-3 overflow-x-auto">
          {cols.map((col, idx) => (
            <div key={idx} className="min-w-[128px] border border-dashed border-slate-300 rounded-lg bg-white p-2 space-y-1.5" data-testid={`manual-stack-${id}-${idx}`}>
              <div className="flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-wider text-slate-400">Pos {idx + 1}</span>
                <button type="button" onClick={() => removeStack(id, idx)} className="text-rose-400 hover:text-rose-600" data-testid={`manual-remove-stack-${id}-${idx}`}><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
              <div className="flex flex-col-reverse gap-1">
                {col.layers.map((code, li) => (
                  <div key={li} className="relative rounded-md px-2 py-1.5 text-white text-xs font-semibold flex items-center justify-between" style={{ background: colorOf(code) }} data-testid={`manual-layer-${id}-${idx}-${li}`}>
                    {li > 0 && <span className="absolute -top-1 left-1 text-[8px] bg-slate-900 text-yellow-300 rounded px-1 font-extrabold">L{li + 1}</span>}
                    <span className="truncate">{code}</span>
                    <button type="button" onClick={() => removeLayer(id, idx, li)} className="ml-1 opacity-80 hover:opacity-100" data-testid={`manual-remove-layer-${id}-${idx}-${li}`}>✕</button>
                  </div>
                ))}
              </div>
              {openTop === `${id}-${idx}` ? (
                <CatalogPicker catalog={cat} placeholder="Product on top…" autoFocus testid={`manual-stacktop-${id}-${idx}`}
                  onPick={(code) => { addLayerToStack(id, idx, code); if (onAuditStack) onAuditStack(id, col.layers, code); setOpenTop(null); }} />
              ) : (
                <button type="button" onClick={() => setOpenTop(`${id}-${idx}`)} className="w-full text-[11px] text-blue-600 border border-dashed border-blue-200 bg-blue-50 rounded-md py-1.5 hover:bg-blue-100" data-testid={`manual-add-top-${id}-${idx}`}>+ stack a product on top</button>
              )}
            </div>
          ))}
          <div className="min-w-[150px] border border-dashed border-blue-300 rounded-lg bg-blue-50 p-2 flex flex-col justify-center" data-testid={`manual-add-stack-${id}`}>
            <div className="text-[11px] text-blue-700 font-medium mb-1 text-center">+ add pallet (new stack)</div>
            <CatalogPicker catalog={cat} placeholder={`Search catalog → ${title}`} testid={`manual-addstack-${id}`} onPick={(code) => addStacksToLane(id, code, 1)} />
          </div>
        </div>
      </div>
    );
  };

  const gradeChip = (g) => g === "good" ? "bg-emerald-100 text-emerald-700 border-emerald-200"
    : g === "fair" ? "bg-amber-100 text-amber-700 border-amber-200"
      : g === "poor" ? "bg-rose-100 text-rose-700 border-rose-200" : "bg-slate-100 text-slate-500 border-slate-200";

  return (
    <div className="border border-blue-200 rounded-lg overflow-hidden" data-testid="manual-arrange">
      <div className="flex flex-wrap items-center gap-2 px-3 py-2 bg-blue-50 border-b border-blue-100">
        <Settings2 className="w-4 h-4 text-blue-700" />
        <span className="text-sm font-semibold text-blue-800">Manual arrange load</span>
        <span className="text-[11px] text-blue-600">{total} stacks · {totalPallets} pallets · edits re-plan live</span>
        {loading && <span className="text-[11px] text-blue-500 animate-pulse">updating…</span>}
        <div className="ml-auto flex items-center gap-2">
          {!hideActions && total > 0 && <Button size="sm" variant="outline" className="h-7 border-indigo-200 text-indigo-700" onClick={onAutoImprove} disabled={improving} data-testid="manual-autoimprove-btn"><Scale3d className="w-3.5 h-3.5 mr-1" />{improving ? "Improving…" : "Auto-improve"}</Button>}
          {!hideActions && total > 0 && <Button size="sm" variant="outline" className="h-7" onClick={onCompare} data-testid="manual-compare-btn"><ArrowLeftRight className="w-3.5 h-3.5 mr-1" />Compare</Button>}
          {!hideActions && total > 0 && <Button size="sm" variant="outline" className="h-7" onClick={() => onSave("")} data-testid="manual-save-btn"><Save className="w-3.5 h-3.5 mr-1" />Save</Button>}
          {!hideActions && total > 0 && (asName === null
            ? <Button size="sm" variant="outline" className="h-7" onClick={() => setAsName("")} data-testid="manual-saveas-btn">Save as…</Button>
            : <span className="inline-flex items-center gap-1">
                <Input autoFocus className="h-7 w-32 text-sm" placeholder="Layout name…" value={asName} onChange={e => setAsName(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter" && asName.trim()) { onSave(asName.trim()); setAsName(null); } }} data-testid="manual-saveas-input" />
                <Button size="sm" className="h-7 bg-blue-600 hover:bg-blue-700" onClick={() => { if (asName.trim()) { onSave(asName.trim()); setAsName(null); } }} data-testid="manual-saveas-confirm">Save</Button>
                <button type="button" className="text-slate-400 hover:text-slate-600 text-sm px-1" onClick={() => setAsName(null)}>✕</button>
              </span>)}
          {!hideActions && <Button size="sm" variant="outline" className="h-7" onClick={() => setShowSaved(s => !s)} data-testid="manual-saved-toggle">Saved ({(saved || []).length})</Button>}
          {total > 0 && <Button size="sm" variant="outline" className="h-7" onClick={clearArrangement} data-testid="manual-clear-btn"><Trash2 className="w-3.5 h-3.5 mr-1" />Clear all</Button>}
        </div>
      </div>
      {!hideActions && improve && (
        <div className="px-3 py-2 border-b border-indigo-100 bg-indigo-50" data-testid="autoimprove-result">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <Scale3d className="w-4 h-4 text-indigo-700" />
            <b className="text-indigo-800">Suggested: {improve.improved.score}<span className="text-[11px] font-normal">/100 {improve.improved.grade}</span></b>
            <span className="text-slate-500">vs your {improve.original.score} ({improve.original.grade})</span>
            {improve.improved_better
              ? <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200" data-testid="autoimprove-gain">+{improve.score_gain} better</Badge>
              : <Badge className="bg-slate-100 text-slate-600 border-slate-200" data-testid="autoimprove-gain">no gain</Badge>}
            <div className="ml-auto flex gap-2">
              {improve.improved_better && <Button size="sm" className="h-7 bg-indigo-600 hover:bg-indigo-700" onClick={onApplyImprove} data-testid="autoimprove-apply-btn">Apply</Button>}
              <Button size="sm" variant="outline" className="h-7" onClick={onDismissImprove} data-testid="autoimprove-dismiss-btn">Dismiss</Button>
            </div>
          </div>
          <ul className="mt-1 text-[12px] text-slate-600 list-disc pl-5">
            {improve.improved_better
              ? (improve.changes || []).map((c, i) => <li key={i} data-testid={`autoimprove-change-${i}`}>{c}</li>)
              : <li data-testid="autoimprove-change-0">Your layout is already close to optimal — no reshuffle scored higher.</li>}
          </ul>
        </div>
      )}
      {showSaved && (
        <div className="px-3 py-2 border-b border-blue-100 bg-white space-y-1" data-testid="saved-list">
          {(saved || []).length === 0 && <div className="text-[12px] text-slate-400">No saved layouts for this PO + container yet. Use <b>Save</b> or <b>Save as…</b>.</div>}
          {(saved || []).map(s => (
            <div key={s.id} className="flex items-center gap-2 text-sm border border-slate-200 rounded px-2 py-1" data-testid={`saved-item-${s.id}`}>
              <span className="font-medium text-slate-800 whitespace-nowrap">{s.name || "Current (quick save)"}</span>
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${gradeChip(s.quality?.grade)}`} title="Load quality score">{s.quality?.score ?? "–"}</span>
              <span className="text-[11px] text-slate-400 whitespace-nowrap">{s.total_pallets} plt · {Math.round(s.total_weight_kg || 0).toLocaleString()} kg</span>
              <span className="text-[10px] text-slate-300 truncate flex-1">{s.updated_by_email || ""}</span>
              <Button size="sm" variant="outline" className="h-6" onClick={() => onLoad(s)} data-testid={`saved-load-${s.id}`}>Load</Button>
              <button type="button" className="text-rose-400 hover:text-rose-600" onClick={() => onDelete(s.id)} data-testid={`saved-delete-${s.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </div>
      )}
      {total === 0 && <div className="px-3 py-3 text-[12px] text-slate-500" data-testid="manual-empty">Empty. Click <b>“Load from auto-plan”</b> (left panel) to start from the system's placement, or add pallets to a lane below and stack any product on top.</div>}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 p-3">
        {renderLane("A", "Left lane")}
        {renderLane("B", "Right lane")}
      </div>
      <div className="px-3 py-2 text-[11px] text-slate-500 bg-slate-50 border-t border-slate-100">“+ stack a product on top” places a DIFFERENT product as layer 2/3. Over-height, over-lane-width or footprint-mismatch stacks are flagged red with a bracing warning in the diagram + advisory below.</div>
    </div>
  );
}

function CompareMetric({ plan }) {
  const q = plan.quality || {};
  const wd = plan.weight_distribution || {};
  const tone = q.grade === "good" ? "text-emerald-600" : q.grade === "fair" ? "text-amber-600" : "text-rose-600";
  const rows = [
    ["Status", plan.status],
    ["Front / Rear", `${wd.front_pct}% / ${wd.rear_pct}%`],
    ["Left / Right", `${wd.left_pct}% / ${wd.right_pct}%`],
    ["CoG", `${wd.cog_long_pct}%`],
    ["Pallets", plan.total_pallets],
    ["Weight", `${Math.round(plan.total_weight_kg || 0).toLocaleString()} kg`],
  ];
  return (
    <div className="space-y-1 text-[12px]">
      <div className="flex items-baseline gap-1"><span className={`text-2xl font-bold ${tone}`} data-testid="compare-score">{q.score}</span><span className="text-slate-400 text-[11px]">/100 · {q.grade}</span></div>
      {rows.map(([k, v], i) => (
        <div key={i} className="flex justify-between border-b border-slate-50 py-0.5"><span className="text-slate-500">{k}</span><span className="font-medium text-slate-800">{v}</span></div>
      ))}
      {q.best_move && <div className="text-[11px] text-blue-700 mt-1"><b>Next move:</b> {q.best_move}</div>}
    </div>
  );
}

function CompareDialog({ open, onOpenChange, currentArr, saved, planArrangement, getSuggested, onUse }) {
  const [selA, setSelA] = useState("current");
  const [selB, setSelB] = useState("suggested");
  const [mA, setMA] = useState(null);
  const [mB, setMB] = useState(null);
  const [ldA, setLdA] = useState(false);
  const [ldB, setLdB] = useState(false);

  const resolveArr = async (sel) => {
    if (sel === "current") return currentArr;
    if (sel === "suggested") return await getSuggested();
    const s = (saved || []).find(x => x.id === sel);
    return s ? s.arrangement : null;
  };
  const compute = async (sel, setM, setL) => {
    setL(true); setM(null);
    try {
      const arr = await resolveArr(sel);
      if (!arr) { setM({ error: true }); return; }
      const plan = await planArrangement(arr);
      setM({ arr, plan });
    } catch { setM({ error: true }); }
    finally { setL(false); }
  };
  useEffect(() => { if (open) compute(selA, setMA, setLdA); /* eslint-disable-next-line */ }, [open, selA]);
  useEffect(() => { if (open) compute(selB, setMB, setLdB); /* eslint-disable-next-line */ }, [open, selB]);

  const options = [{ v: "current", l: "Current (editing)" },
    ...(saved || []).map(s => ({ v: s.id, l: s.name || "Current (quick save)" })),
    { v: "suggested", l: "Computer-suggested" }];
  const sA = mA?.plan?.quality?.score, sB = mB?.plan?.quality?.score;
  const winner = (sA != null && sB != null) ? (sA >= sB ? "A" : "B") : null;

  const col = (side, sel, setSel, m, ld) => (
    <div className={`border rounded-lg p-3 ${winner === side ? "border-emerald-400 ring-1 ring-emerald-200" : "border-slate-200"}`} data-testid={`compare-side-${side}`}>
      <select value={sel} onChange={e => setSel(e.target.value)} className="w-full h-8 text-sm border border-slate-300 rounded px-2 mb-2" data-testid={`compare-select-${side}`}>
        {options.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
      {winner === side && <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 mb-2" data-testid={`compare-winner-${side}`}>Better load</Badge>}
      {ld ? <div className="text-sm text-slate-400 py-8 text-center animate-pulse">Computing…</div>
        : m?.error ? <div className="text-sm text-rose-500 py-4">Layout unavailable.</div>
          : m ? (<>
            <CompareMetric plan={m.plan} />
            <Button size="sm" className="w-full mt-2 bg-blue-600 hover:bg-blue-700" onClick={() => onUse(m.arr)} data-testid={`compare-use-${side}`}>Use this layout</Button>
          </>) : null}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="compare-dialog">
        <DialogHeader><DialogTitle>Compare layouts side-by-side</DialogTitle>
          <DialogDescription className="sr-only">Compare two container load layouts and their quality scores.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          {col("A", selA, setSelA, mA, ldA)}
          {col("B", selB, setSelB, mB, ldB)}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// QualityCard is imported from ./planQualityUI

function Stepper({ value, min = 0, max = 999, onChange, testid }) {
  return (
    <div className="inline-flex items-center border border-slate-300 rounded overflow-hidden" data-testid={testid}>
      <button type="button" className="px-1.5 py-1 text-slate-600 hover:bg-slate-100 disabled:opacity-30" disabled={value <= min} onClick={() => onChange(Math.max(min, value - 1))} data-testid={`${testid}-dec`}><Minus className="w-3 h-3" /></button>
      <input type="number" className="w-10 text-center text-sm border-x border-slate-200 h-6 outline-none" value={value} min={min} max={max}
        onChange={e => onChange(Math.max(min, Math.min(max, parseInt(e.target.value || "0", 10) || 0)))} data-testid={`${testid}-input`} />
      <button type="button" className="px-1.5 py-1 text-slate-600 hover:bg-slate-100 disabled:opacity-30" disabled={value >= max} onClick={() => onChange(Math.min(max, value + 1))} data-testid={`${testid}-inc`}><Plus className="w-3 h-3" /></button>
    </div>
  );
}

function OverCapacityAdjust({ plan, removeQty, setRemove, clearRemove, loading }) {
  const cap = plan.container.max_payload_kg;
  const overW = Math.max(0, Math.round(plan.total_weight_kg - cap));
  const contL = plan.container.internal_length_in;
  const overL = Math.max(0, Math.round((plan.used_length_in - contL) * 10) / 10);
  const rows = (plan.product_rows || []).filter(r => r.matched && r.pallet_count && !r.added);
  const totalRemoved = Object.values(removeQty).reduce((a, b) => a + b, 0);

  const applySuggested = () => {
    (plan.removal_suggestions || []).forEach(s => {
      const row = rows.find(r => r.product_code === s.product_code);
      const orig = (row?.pallet_count || 0) + (removeQty[s.product_code] || 0);
      setRemove(s.product_code, Math.min(orig, s.pallet_count), row?.pieces_per_pallet);
    });
  };

  return (
    <div className="border border-rose-200 rounded-lg overflow-hidden" data-testid="plan-removals">
      <div className="px-3 py-2 bg-rose-50 text-sm font-medium text-rose-800 flex flex-wrap items-center gap-2">
        <AlertTriangle className="w-4 h-4" />Over capacity — remove pallets to make it fit
        <span className="text-[11px] font-normal text-rose-600">
          {overW > 0 ? `· ${overW.toLocaleString()} kg over cap` : ""}{overL > 0 ? ` · ${overL} in too long` : ""}
        </span>
        <div className="ml-auto flex items-center gap-2">
          {loading && <span className="text-[11px] text-rose-500 animate-pulse">updating…</span>}
          {(plan.removal_suggestions || []).length > 0 && (
            <Button size="sm" variant="outline" className="h-7" onClick={applySuggested} data-testid="apply-suggested-removals-btn">Apply suggested</Button>
          )}
          {totalRemoved > 0 && <Button size="sm" variant="outline" className="h-7" onClick={clearRemove} data-testid="clear-removals-btn"><Trash2 className="w-3.5 h-3.5 mr-1" />Reset</Button>}
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
            <tr>
              <th className="text-left p-2">Product</th>
              <th className="text-right p-2">Pallets left</th>
              <th className="text-center p-2">Remove pallets</th>
              <th className="text-right p-2">Weight (kg)</th>
              <th className="text-left p-2 pl-3">Suggested cut</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const removed = removeQty[r.product_code] || 0;
              const orig = (r.pallet_count || 0) + removed;
              const sug = (plan.removal_suggestions || []).find(s => s.product_code === r.product_code);
              return (
                <tr key={i} className="border-t border-slate-100" data-testid={`removal-${r.product_code}`}>
                  <td className="p-2"><span className="font-medium text-slate-800">{r.product_code}</span>{sug && <Badge className="ml-1.5 text-[9px] bg-rose-100 text-rose-700 border-rose-200">suggested</Badge>}</td>
                  <td className="p-2 text-right tabular-nums">{r.pallet_count} <span className="text-slate-400">/ {orig}</span></td>
                  <td className="p-2">
                    <div className="flex justify-center">
                      <Stepper value={removed} min={0} max={orig} onChange={(v) => setRemove(r.product_code, v, r.pieces_per_pallet)} testid={`remove-stepper-${r.product_code}`} />
                    </div>
                  </td>
                  <td className="p-2 text-right tabular-nums">{r.total_weight_kg != null ? r.total_weight_kg.toLocaleString() : "—"}</td>
                  <td className="p-2 pl-3 text-xs text-slate-500">{sug ? `−${sug.pallet_count} plt · ${sug.weight_kg?.toLocaleString()} kg` : "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="px-3 py-2 text-[11px] text-slate-500 bg-slate-50 border-t border-slate-100">
        Removing pallets updates the plan live. Once the status turns <b className="text-emerald-700">Fits</b>, use “Fill remaining space” below to add other items.
      </div>
    </div>
  );
}

function AddFromCatalog({ fillQty, setFill }) {
  const [cat, setCat] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  useEffect(() => {
    let ok = true;
    axios.get(`${API}/catalog/products`, authH())
      .then(r => { if (ok) setCat(r.data.products || []); })
      .catch(() => { /* catalog.view may be denied — picker just stays empty */ });
    return () => { ok = false; };
  }, []);
  const byCode = useMemo(() => { const m = {}; cat.forEach(p => { m[p.product_code] = p; }); return m; }, [cat]);
  const ql = q.trim().toLowerCase();
  const results = ql
    ? cat.filter(p => (p.product_code || "").toLowerCase().includes(ql) || (p.description || "").toLowerCase().includes(ql)).slice(0, 30)
    : [];
  const addedCodes = Object.keys(fillQty).filter(c => fillQty[c] > 0);

  const addOne = (p) => {
    const ppp = p.default_qty_per_pallet;
    if (!ppp) { toast.error(`${p.product_code} has no pieces/pallet set — set it in Product Catalog first`); return; }
    setFill(p.product_code, (fillQty[p.product_code] || 0) + 1, ppp);
    setQ(""); setOpen(false);
  };

  return (
    <div className="p-3 border-b border-blue-100 bg-white space-y-2" data-testid="add-from-catalog">
      <div className="text-[11px] uppercase tracking-wider text-slate-400 font-medium">Add any product from catalog</div>
      <div className="relative">
        <Input className="h-8 text-sm" placeholder="Search product code or description…" value={q}
          onChange={e => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)}
          data-testid="catalog-add-search" />
        {open && results.length > 0 && (
          <div className="absolute z-20 mt-1 w-full max-h-56 overflow-auto border border-slate-200 rounded-lg bg-white shadow-lg" data-testid="catalog-add-results">
            {results.map(p => (
              <button key={p.id || p.product_code} type="button" onClick={() => addOne(p)}
                className="w-full text-left px-3 py-1.5 hover:bg-blue-50 border-b border-slate-50 text-sm flex items-center gap-2"
                data-testid={`catalog-add-option-${p.product_code}`}>
                <span className="font-medium text-slate-800 whitespace-nowrap">{p.product_code}</span>
                <span className="text-slate-400 text-xs truncate flex-1">{p.description}</span>
                <span className="text-[10px] text-slate-400 whitespace-nowrap">{p.default_qty_per_pallet ? `${p.default_qty_per_pallet}/plt` : "no pcs/plt"}</span>
                <Plus className="w-3.5 h-3.5 text-blue-600 shrink-0" />
              </button>
            ))}
          </div>
        )}
      </div>
      {addedCodes.length > 0 && (
        <div className="space-y-1" data-testid="added-items-list">
          <div className="text-[11px] text-slate-500">Added to load:</div>
          {addedCodes.map(code => {
            const p = byCode[code] || {};
            const ppp = p.default_qty_per_pallet;
            const val = fillQty[code] || 0;
            return (
              <div key={code} className="flex items-center gap-2 text-sm border border-emerald-200 bg-emerald-50/50 rounded px-2 py-1" data-testid={`added-item-${code}`}>
                <span className="font-medium text-slate-800 whitespace-nowrap">{code}</span>
                <span className="text-slate-400 text-xs truncate flex-1">{p.description || ""}</span>
                <Stepper value={val} min={0} max={999} onChange={(v) => setFill(code, v, ppp)} testid={`added-stepper-${code}`} />
                <button type="button" className="text-rose-500 hover:text-rose-700" onClick={() => setFill(code, 0)} data-testid={`added-remove-${code}`}><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function FillPanel({ suggest, suggestLoading, rankBy, setRankBy, fillQty, setFill, applyCombo, clearFill, loadSuggestions, loading }) {
  const RANKS = [
    { v: "space", label: "Fill the most space (least empty)" },
    { v: "weight", label: "Best weight fill" },
    { v: "pallets", label: "Most pallets" },
    { v: "pieces", label: "Most pieces" },
    { v: "compact", label: "Most compact (least floor)" },
  ];
  const addedCount = Object.values(fillQty).reduce((a, b) => a + b, 0);
  return (
    <div className="border border-blue-200 rounded-lg overflow-hidden" data-testid="fill-panel">
      <div className="px-3 py-2 bg-blue-50 text-sm font-medium text-blue-800 flex flex-wrap items-center gap-2">
        <PackageCheck className="w-4 h-4" />Fill remaining space from catalog
        {suggest && <span className="text-[11px] font-normal text-blue-600">· {suggest.free_length_in} in floor · {suggest.remaining_weight_kg.toLocaleString()} kg spare</span>}
        <div className="ml-auto flex items-center gap-2">
          {addedCount > 0 && <Button variant="outline" size="sm" onClick={clearFill} data-testid="fill-clear-btn"><Trash2 className="w-3.5 h-3.5 mr-1" />Clear added</Button>}
          <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={() => loadSuggestions(rankBy)} disabled={suggestLoading} data-testid="fill-find-btn">
            {suggestLoading ? "Finding…" : (suggest ? "Refresh" : "Find items to fill")}
          </Button>
        </div>
      </div>

      <AddFromCatalog fillQty={fillQty} setFill={setFill} />

      {suggest && (
        <div className="p-3 space-y-3">
          {suggest.base_over_loaded ? (
            <div className="text-sm text-rose-600" data-testid="fill-overloaded-msg">This load is already <b>over capacity</b>, so there's no room to add more (a container can't ship over-full). Tick <b>“Ignore max stack layers”</b> above or click <b>Auto-balance load</b> to make it fit first — then Find items to fill the leftover floor <i>and</i> the empty space on top of stacks.</div>
          ) : suggest.items.length === 0 ? (
            <div className="text-sm text-slate-500">No catalog products (with dimensions &amp; weight) fit the remaining space.</div>
          ) : (
            <>
              <div className="flex items-center gap-2 text-sm">
                <span className="text-slate-500">Prioritise:</span>
                <select className="h-8 text-sm border rounded px-2 bg-white" value={rankBy}
                  onChange={e => { setRankBy(e.target.value); loadSuggestions(e.target.value); }} data-testid="fill-rank-select">
                  {RANKS.map(r => <option key={r.v} value={r.v}>{r.label}</option>)}
                </select>
                {loading && <span className="text-[11px] text-blue-500 animate-pulse">updating plan…</span>}
              </div>

              {/* Combos */}
              {suggest.combos.length > 0 && (
                <div className="space-y-1.5">
                  <div className="text-[11px] uppercase tracking-wider text-slate-400 font-medium">Suggested combinations (best → worst)</div>
                  <div className="flex flex-col gap-1.5">
                    {suggest.combos.map((c, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm border border-slate-200 rounded-lg px-2.5 py-1.5" data-testid={`fill-combo-${i}`}>
                        <span className="text-[11px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 font-semibold">{c.utilisation_pct != null ? `${c.utilisation_pct}% full` : `${c.weight_used_pct}% wt`}</span>
                        <span className="text-slate-600 truncate">{c.items.map(it => `${it.product_code}×${it.pallets}`).join(" + ")}</span>
                        <span className="text-slate-400 text-xs ml-auto whitespace-nowrap">{c.total_pallets} plt · {c.total_pieces.toLocaleString()} pcs</span>
                        <Button size="sm" variant="outline" className="h-7" onClick={() => applyCombo(c)} data-testid={`fill-combo-apply-${i}`}>Apply</Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Ranked items with steppers */}
              <div className="border border-slate-200 rounded-lg overflow-x-auto">
                <table className="w-full text-sm" data-testid="fill-items-table">
                  <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
                    <tr>
                      <th className="text-left p-2">Product</th><th className="text-right p-2">Footprint</th>
                      <th className="text-right p-2">Pcs/Plt</th><th className="text-right p-2">Plt wt</th>
                      <th className="text-right p-2">Fits (max)</th><th className="text-right p-2">Wt%</th>
                      <th className="text-center p-2">Add pallets</th>
                    </tr>
                  </thead>
                  <tbody>
                    {suggest.items.map((it) => {
                      const cur = fillQty[it.product_code] || 0;
                      return (
                        <tr key={it.product_code} className={`border-t border-slate-100 ${cur > 0 ? "bg-emerald-50/60" : ""}`} data-testid={`fill-item-${it.product_code}`}>
                          <td className="p-2"><span className="font-medium">{it.product_code}</span>{it.already_in_plan && <span className="ml-1 text-[9px] text-slate-400">(in load)</span>}<div className="text-[11px] text-slate-400 truncate max-w-[180px]">{it.description}</div></td>
                          <td className="p-2 text-right whitespace-nowrap">{it.footprint_in[0]}×{it.footprint_in[1]}</td>
                          <td className="p-2 text-right">{it.pieces_per_pallet}</td>
                          <td className="p-2 text-right">{it.per_pallet_weight_kg}</td>
                          <td className="p-2 text-right font-medium text-blue-700">{it.max_pallets}</td>
                          <td className="p-2 text-right">{it.weight_used_pct}%</td>
                          <td className="p-2">
                            <div className="flex items-center justify-center gap-1">
                              <button className="w-6 h-6 rounded border border-slate-300 text-slate-600 disabled:opacity-30" disabled={cur <= 0} onClick={() => setFill(it.product_code, Math.max(0, cur - 1), it.pieces_per_pallet)} data-testid={`fill-minus-${it.product_code}`}>−</button>
                              <input type="number" min="0" max={it.max_pallets} value={cur} onChange={e => setFill(it.product_code, Math.max(0, Math.min(it.max_pallets, parseInt(e.target.value || "0", 10))), it.pieces_per_pallet)} className="w-12 h-6 text-center text-sm border border-slate-300 rounded" data-testid={`fill-qty-${it.product_code}`} />
                              <button className="w-6 h-6 rounded border border-slate-300 text-slate-600 disabled:opacity-30" disabled={cur >= it.max_pallets} onClick={() => setFill(it.product_code, Math.min(it.max_pallets, cur + 1), it.pieces_per_pallet)} data-testid={`fill-plus-${it.product_code}`}>+</button>
                              <button className="ml-1 text-[11px] text-blue-600 hover:underline" onClick={() => setFill(it.product_code, it.max_pallets, it.pieces_per_pallet)} data-testid={`fill-max-${it.product_code}`}>max</button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <p className="text-[11px] text-slate-400">Ticked items are added to the load and re-planned live (shown with an “added” badge &amp; dashed outline in the diagram). Max quantities use the leftover <b>floor space, the empty space on top of existing stacks (layer 2+)</b>, and the remaining weight — a product only stacks on a pallet whose footprint is equal or larger.</p>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function PresetsDialog({ open, onOpenChange, types, reload }) {
  const blank = { name: "", internal_length_in: "", internal_width_in: "", internal_height_in: "", max_payload_kg: "" };
  const [form, setForm] = useState(blank);
  const [editId, setEditId] = useState(null);

  const save = async () => {
    if (!form.name) { toast.error("Name required"); return; }
    try {
      if (editId) await axios.patch(`${API}/container-types/${editId}`, form, authH());
      else await axios.post(`${API}/container-types`, form, authH());
      toast.success(editId ? "Updated" : "Preset added");
      setForm(blank); setEditId(null);
      reload();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this preset?")) return;
    try { await axios.delete(`${API}/container-types/${id}`, authH()); toast.success("Deleted"); reload(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const setDefault = async (id) => {
    try { await axios.post(`${API}/container-types/${id}/set-default`, {}, authH()); toast.success("Default container set"); reload(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const edit = (t) => { setEditId(t.id); setForm({ name: t.name, internal_length_in: t.internal_length_in, internal_width_in: t.internal_width_in, internal_height_in: t.internal_height_in, max_payload_kg: t.max_payload_kg }); };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) { setForm(blank); setEditId(null); } }}>
      <DialogContent className="max-w-2xl" data-testid="presets-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Settings2 className="w-5 h-5 text-blue-700" />Container presets</DialogTitle></DialogHeader>
        <div className="border border-slate-200 rounded max-h-56 overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase sticky top-0"><tr><th className="text-left p-2">Name</th><th className="text-right p-2">L×W×H (in)</th><th className="text-right p-2">Payload (kg)</th><th className="p-2"></th></tr></thead>
            <tbody>
              {types.map(t => (
                <tr key={t.id} className="border-t border-slate-100" data-testid={`preset-row-${t.id}`}>
                  <td className="p-2 font-medium">{t.name} {t.is_system && <Badge className="ml-1 text-[9px] bg-slate-100 text-slate-500 border-slate-200">built-in</Badge>}{t.is_default && <Badge className="ml-1 text-[9px] bg-emerald-100 text-emerald-700 border-emerald-200" data-testid={`preset-default-badge-${t.id}`}>default</Badge>}</td>
                  <td className="p-2 text-right">{t.internal_length_in}×{t.internal_width_in}×{t.internal_height_in}</td>
                  <td className="p-2 text-right">{t.max_payload_kg.toLocaleString()}</td>
                  <td className="p-2 text-right whitespace-nowrap">
                    {t.is_default
                      ? <span className="text-[11px] text-emerald-600 font-medium px-2 inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" />Default</span>
                      : <Button size="sm" variant="ghost" className="h-7 px-2 text-emerald-700" onClick={() => setDefault(t.id)} data-testid={`preset-setdefault-${t.id}`}>Set default</Button>}
                    <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => edit(t)} data-testid={`preset-edit-${t.id}`}>Edit</Button>
                    {!t.is_system && <Button size="sm" variant="ghost" className="h-7 px-2 text-rose-600" onClick={() => del(t.id)} data-testid={`preset-delete-${t.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          <div className="col-span-2 md:col-span-1"><label className="text-[10px] uppercase text-slate-400">Name</label><Input className="h-8 text-sm" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="preset-name-input" /></div>
          <div><label className="text-[10px] uppercase text-slate-400">Length (in)</label><Input className="h-8 text-sm" type="number" value={form.internal_length_in} onChange={e => setForm({ ...form, internal_length_in: e.target.value })} data-testid="preset-length-input" /></div>
          <div><label className="text-[10px] uppercase text-slate-400">Width (in)</label><Input className="h-8 text-sm" type="number" value={form.internal_width_in} onChange={e => setForm({ ...form, internal_width_in: e.target.value })} /></div>
          <div><label className="text-[10px] uppercase text-slate-400">Height (in)</label><Input className="h-8 text-sm" type="number" value={form.internal_height_in} onChange={e => setForm({ ...form, internal_height_in: e.target.value })} /></div>
          <div><label className="text-[10px] uppercase text-slate-400">Payload (kg)</label><Input className="h-8 text-sm" type="number" value={form.max_payload_kg} onChange={e => setForm({ ...form, max_payload_kg: e.target.value })} data-testid="preset-payload-input" /></div>
        </div>
        <DialogFooter>
          {editId && <Button variant="ghost" onClick={() => { setForm(blank); setEditId(null); }}>Cancel edit</Button>}
          <Button onClick={save} data-testid="preset-save-btn"><Plus className="w-4 h-4 mr-1" />{editId ? "Save changes" : "Add preset"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// Truck & Chassis physics editor — admin-only. Every field maps 1:1 to a value
// consumed by the Law-of-Levers axle solver both server-side and in the JS mirror
// so the Scale Ticket updates instantly on save (no plan re-run required).
function TruckSetupDialog({ open, onOpenChange, truckSpecs, onSaved }) {
  const [form, setForm] = useState(truckSpecs);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setForm(truckSpecs); }, [open, truckSpecs]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/container-planner/truck-specs`,
        { truck_specs: form }, authH());
      const next = r.data?.truck_specs || form;
      onSaved(next);
      toast.success("Truck specs saved — Scale Ticket updated live");
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  const reset = () => setForm(TRUCK_SPEC_DEFAULTS);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="truck-setup-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Truck className="w-5 h-5 text-orange-600" />Truck &amp; Chassis Setup
          </DialogTitle>
          <div className="text-[12px] text-slate-500 mt-1">
            Physical constants used by the US Highway Compliance (BETA) Law-of-Levers solver.
            Changes persist for all future plans and refresh the Scale Ticket instantly.
          </div>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 py-2">
          {TRUCK_SPEC_FIELDS.map((f) => (
            <div key={f.key} className="space-y-1">
              <label className="text-[11px] font-medium text-slate-600 flex items-center justify-between">
                <span>{f.label}</span>
                <span className="text-[10px] text-slate-400 font-mono">{f.unit}</span>
              </label>
              <Input
                className="h-8 text-sm font-mono"
                type="number"
                step="0.1"
                value={form[f.key] ?? ""}
                onChange={(e) => setForm({ ...form, [f.key]: e.target.value === "" ? "" : Number(e.target.value) })}
                data-testid={`truck-spec-${f.key}`}
              />
              {f.hint && <div className="text-[10px] text-slate-400">{f.hint}</div>}
            </div>
          ))}
        </div>
        <div className="rounded-md bg-orange-50 border border-orange-200 px-3 py-2 text-[11px] text-orange-800">
          <span className="font-semibold">Fifth Wheel Lead physics:</span> steer share =
          fifth_wheel_lead ÷ tractor_wheelbase = {Number(form.fifth_wheel_lead_in || 0).toFixed(1)} ÷ {Number(form.tractor_wheelbase_in || 0).toFixed(1)}
          = <span className="font-mono font-bold">{form.tractor_wheelbase_in > 0
              ? ((form.fifth_wheel_lead_in || 0) / form.tractor_wheelbase_in * 100).toFixed(2) + "%"
              : "—"}</span> of drive-cargo transferred forward to the steer axle.
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={reset} data-testid="truck-spec-reset-btn">Reset to defaults</Button>
          <Button onClick={save} disabled={saving} data-testid="truck-spec-save-btn">
            <Save className="w-4 h-4 mr-1" />{saving ? "Saving…" : "Save specs"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
